import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ethers } from 'ethers';
import EthereumProvider from '@walletconnect/ethereum-provider';
import './App.css';
import { deriveDisplayState } from './state';
import ReleaseDetails from './components/ReleaseDetails';
import CustomIntegrationModal from './components/ops/CustomIntegrationModal';
import StatusPill, { statusLabelForOverall } from './components/StatusPill';
import BottomDrawer from './components/ui/BottomDrawer';
import { useDrawer } from './components/ui/drawer/DrawerContext';
import InfoCard from './components/ui/InfoCard';
import KeyValueList from './components/ui/KeyValueList';
import StatusBadge from './components/ui/StatusBadge';
import ExpandableSection from './components/ui/ExpandableSection';
import { formatDateTime } from './utils/formatters';
import {
  describeApprovalRequirement,
  humanizeEventType,
  humanizeGovernanceActionType,
  humanizePolicyId,
  humanizeRoleId,
  resolveUserLabel,
} from './utils/narration';

// Contract ABIs
const AUTHORITY_TOKEN_ABI = [
  "function hasAuthority(address holder, bytes32 authorityType) view returns (bool)",
  "function getIdentity(address wallet) view returns (string)",
  "function PROD_APPROVER() view returns (bytes32)",
  "function SECURITY_APPROVER() view returns (bytes32)",
  "function INFRA_PROVISIONER() view returns (bytes32)",
  "function BREAK_GLASS() view returns (bytes32)"
];

const RELEASE_AUTH_ABI = [
  "function intents(bytes32) view returns (bytes32, string, string, bytes32, uint256, uint256, address, bool)",
  "function approve(bytes32 intentId, bytes memory signature) external",
  "function isAuthorized(bytes32 intentId) view returns (bool)",
  "function getApprovals(bytes32 intentId) view returns (tuple(address approver, bytes32 authorityType, uint256 timestamp, bytes signature)[])"
];

// Contract addresses (override via build env for staging/prod)
const AUTHORITY_TOKEN_ADDRESS = process.env.REACT_APP_AUTHORITY_TOKEN_ADDRESS || '';
const RELEASE_AUTH_ADDRESS = process.env.REACT_APP_RELEASE_AUTH_ADDRESS || '';

const LeftPanel = ({ children }) => (
  <aside className="session-sidebar">
    {children}
  </aside>
);

const WorkSurface = ({ children }) => (
  <div className="console-main">
    {children}
  </div>
);

const RightPanel = ({ children }) => (
  <aside className="control-plane">
    {children}
  </aside>
);

function App() {
  const { openDrawer, setDrawerData } = useDrawer();
  const debugEnabled = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('debug') === 'true' || params.get('debug') === '1';
    } catch {
      return false;
    }
  })();
  const [debugUiEnabled, setDebugUiEnabled] = useState(false);
  // Temporary stealth mode: hide hero copy until re-enabled.
  const showHeroContent = false;
  const defaultRpcUrl = process.env.REACT_APP_ATTEST_R_RPC_URL || 'https://blockchain.attest-r.mergematter.io';
  const mainnetRpcUrl = process.env.REACT_APP_WALLETCONNECT_MAINNET_RPC || 'https://cloudflare-eth.com';
  const apiBase = process.env.REACT_APP_ATTEST_R_API_BASE
    || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? window.location.origin : defaultRpcUrl);
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [identityToken, setIdentityToken] = useState(null);
  const [identityProfile, setIdentityProfile] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [identityStatus, setIdentityStatus] = useState('');
  const [toast, setToast] = useState(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(null);
  const [oidcStatusNote, setOidcStatusNote] = useState('');
  const [oidcProvider, setOidcProvider] = useState('google');
  const [authorities, setAuthorities] = useState([]);
  const [identity, setIdentity] = useState('');
  const [connectionType, setConnectionType] = useState('');
  const [connectingType, setConnectingType] = useState('');
  const [intents, setIntents] = useState([]);
  const [lastIntentFetchAt, setLastIntentFetchAt] = useState(null);
  const [activeIntent, setActiveIntent] = useState(null);
  const [releaseRecord, setReleaseRecord] = useState(null);
  const [releaseView, setReleaseView] = useState('engineer');
  const [loading, setLoading] = useState(false);
  const [approvingIntentId, setApprovingIntentId] = useState(null);
  const [awaitingSignature, setAwaitingSignature] = useState(false);
  const [signatureIntentId, setSignatureIntentId] = useState(null);
  const [approvalPrompt, setApprovalPrompt] = useState(null);
  const [showApprovalDetails, setShowApprovalDetails] = useState(false);
  const [authorityTypeMap, setAuthorityTypeMap] = useState({});
  const [connectStatus, setConnectStatus] = useState('');
  const [lastConnectStatus, setLastConnectStatus] = useState('');
  const [showIntegrationActions, setShowIntegrationActions] = useState(false);
  const [showGitlabIntegration, setShowGitlabIntegration] = useState(true);
  const [showTokenIntegration, setShowTokenIntegration] = useState(true);
  const [walletNetworkMismatch, setWalletNetworkMismatch] = useState(false);
  const [walletNetworkId, setWalletNetworkId] = useState(null);
  const [walletNetworkSwitching, setWalletNetworkSwitching] = useState(false);
  const [debugEvents, setDebugEvents] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [showConnectOptions, setShowConnectOptions] = useState(false);
  const [showControlDebug, setShowControlDebug] = useState(false);
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);
  const [approvalEnvFilter, setApprovalEnvFilter] = useState('all');
  const [approvalSearch, setApprovalSearch] = useState('');
  const [needsMyApprovalOnly, setNeedsMyApprovalOnly] = useState(false);
  const [governanceTab, setGovernanceTab] = useState('overview');
  const consoleMode = String(process.env.REACT_APP_CONSOLE_MODE || 'ops').toLowerCase();
  const governanceOnly = consoleMode === 'governance';
  const opsOnly = consoleMode === 'ops';
  const allowConsoleToggle = consoleMode === 'both';
  const [activeConsole, setActiveConsole] = useState(governanceOnly ? 'governance' : 'operations');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = params.get('debugUI');
      const allowDebug = process.env.NODE_ENV !== 'production' || flag === '1' || flag === 'true';
      if (!allowDebug) return;
      const stored = window.localStorage.getItem('debugUI');
      const enabled = flag === '1' || flag === 'true' || stored === '1' || stored === 'true';
      setDebugUiEnabled(enabled);
    } catch {
      setDebugUiEnabled(false);
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (debugUiEnabled) {
      document.body.classList.add('debug-ui');
    } else {
      document.body.classList.remove('debug-ui');
    }
    return () => document.body.classList.remove('debug-ui');
  }, [debugUiEnabled]);
  const [currentPath, setCurrentPath] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : ''));
  const [governanceActions, setGovernanceActions] = useState([]);
  const [governanceActionsAll, setGovernanceActionsAll] = useState([]);
  const [governanceMembers, setGovernanceMembers] = useState([]);
  const [governanceRoles, setGovernanceRoles] = useState([]);
  const [governanceRoleAssignments, setGovernanceRoleAssignments] = useState([]);
  const [governancePolicies, setGovernancePolicies] = useState([]);
  const [governancePolicyBindings, setGovernancePolicyBindings] = useState([]);
  const [legacyAuthorityAssignments, setLegacyAuthorityAssignments] = useState([]);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [legacyScanWallet, setLegacyScanWallet] = useState('');
  const [governanceAuditLogs, setGovernanceAuditLogs] = useState([]);
  const [governanceDetail, setGovernanceDetail] = useState(null);
  const [governanceDetailLoading, setGovernanceDetailLoading] = useState(false);
  const [integrationInstalls, setIntegrationInstalls] = useState([]);
  const [integrationInstall, setIntegrationInstall] = useState(null);
  const [integrationTokens, setIntegrationTokens] = useState([]);
  const [integrationTriggers, setIntegrationTriggers] = useState([]);
  const [integrationAuthMode, setIntegrationAuthMode] = useState('oidc');
  const [integrationStatus, setIntegrationStatus] = useState('ACTIVE');
  const [integrationExternalOrgRef, setIntegrationExternalOrgRef] = useState('');
  const [integrationIssuersText, setIntegrationIssuersText] = useState('');
  const [integrationAudiencesText, setIntegrationAudiencesText] = useState('');
  const [integrationScopesText, setIntegrationScopesText] = useState('');
  const [triggerScopeType, setTriggerScopeType] = useState('gitlab_project');
  const [triggerScopeId, setTriggerScopeId] = useState('');
  const [triggerToken, setTriggerToken] = useState('');
  const [triggerRef, setTriggerRef] = useState('');
  const [triggerVariablesText, setTriggerVariablesText] = useState('');
  const [tokenProvider, setTokenProvider] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [tokenScopesText, setTokenScopesText] = useState('');
  const [tokenExpiryText, setTokenExpiryText] = useState('');
  const [tokenCreatedValue, setTokenCreatedValue] = useState('');
  const [tokenCreatedActionId, setTokenCreatedActionId] = useState('');
  const [governanceActionSecrets, setGovernanceActionSecrets] = useState({});
  // action detail modal uses `governanceDetail` + `loadGovernanceActionDetails`
  const [tenantUsers, setTenantUsers] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);
  const [councilSubmitError, setCouncilSubmitError] = useState('');
  const [bootstrapFinalizeStatus, setBootstrapFinalizeStatus] = useState('');
  const [bootstrapFinalizeError, setBootstrapFinalizeError] = useState('');
  const [bootstrapSignStatus, setBootstrapSignStatus] = useState('');
  const [bootstrapSignError, setBootstrapSignError] = useState('');
  const [bootstrapSigning, setBootstrapSigning] = useState(false);
  const [signProposalError, setSignProposalError] = useState('');
  const [governanceSubmitting, setGovernanceSubmitting] = useState('');
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');
  const [governanceLoading, setGovernanceLoading] = useState(false);
  const [govMemberId, setGovMemberId] = useState('');
  const [govMemberQuery, setGovMemberQuery] = useState('');
  const [govRemoveMemberId, setGovRemoveMemberId] = useState('');
  const [govRemoveQuery, setGovRemoveQuery] = useState('');
  const [govQuorumM, setGovQuorumM] = useState('');
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [assignmentUserId, setAssignmentUserId] = useState('');
  const [assignmentUserQuery, setAssignmentUserQuery] = useState('');
  const [assignmentRoleId, setAssignmentRoleId] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [policyDescription, setPolicyDescription] = useState('');
  const [policyRoleIds, setPolicyRoleIds] = useState([]);
  const [policyScopeEnv, setPolicyScopeEnv] = useState('ALL');
  const [policyDraftId, setPolicyDraftId] = useState('');
  const [policyActiveId, setPolicyActiveId] = useState('');
  const [policyTargetRef, setPolicyTargetRef] = useState('');
  const [policyTargetEnv, setPolicyTargetEnv] = useState('ALL');
  const [policyBindingMode, setPolicyBindingMode] = useState('legacy');
  const [policyBindingProvider, setPolicyBindingProvider] = useState('');
  const [policyBindingResourceType, setPolicyBindingResourceType] = useState('');
  const [policyBindingResourceId, setPolicyBindingResourceId] = useState('');
  const [policyBindingScopesText, setPolicyBindingScopesText] = useState('');
  const [setupStep, setSetupStep] = useState(0);
  const [testHarnessStatus, setTestHarnessStatus] = useState(null);
  const [testHarnessError, setTestHarnessError] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [desiredCouncilMembers, setDesiredCouncilMembers] = useState([]);
  const [desiredCouncilQuorumM, setDesiredCouncilQuorumM] = useState('');
  const [desiredCouncilQuorumN, setDesiredCouncilQuorumN] = useState('');
  const [desiredCouncilTouched, setDesiredCouncilTouched] = useState(false);
  const [councilSubmitting, setCouncilSubmitting] = useState(false);
  const [lastCouncilProcessId, setLastCouncilProcessId] = useState('');
  const [processLookupInput, setProcessLookupInput] = useState('');
  const [approvingCouncilProcess, setApprovingCouncilProcess] = useState(false);
  const intentsRef = useRef([]);
  const activeIntentRef = useRef(null);
  const releaseRecordRef = useRef(null);
  const externalProviderRef = useRef(null);
  const walletConnectProviderRef = useRef(null);
  const refreshPromiseRef = useRef(null);

  const apiUrl = (path) => `${apiBase}${path}`;

  useEffect(() => {
    if (!govMemberId) return;
    const exists = tenantUsers.some((u) => u.id === govMemberId);
    const already = governanceMembers.some((m) => (m.id === govMemberId || m.userId === govMemberId || m.email === tenantUsers.find((u) => u.id === govMemberId)?.email));
    if (!exists || already) {
      setGovMemberId('');
      setGovMemberQuery('');
    }
  }, [tenantUsers, governanceMembers, govMemberId]);

  useEffect(() => {
    if (!integrationInstall) return;
    setIntegrationExternalOrgRef(integrationInstall.externalOrgRef || '');
    setIntegrationAuthMode(integrationInstall.authMode || 'oidc');
    setIntegrationStatus(integrationInstall.status || 'ACTIVE');
    if (Array.isArray(integrationInstall.issuers)) {
      setIntegrationIssuersText(integrationInstall.issuers.join('\n'));
    } else {
      setIntegrationIssuersText('');
    }
    if (Array.isArray(integrationInstall.audiences)) {
      setIntegrationAudiencesText(integrationInstall.audiences.join('\n'));
    } else {
      setIntegrationAudiencesText('');
    }
    if (integrationInstall.scopes) {
      setIntegrationScopesText(JSON.stringify(integrationInstall.scopes, null, 2));
    } else {
      setIntegrationScopesText('');
    }
  }, [integrationInstall?.id]);

  useEffect(() => {
    if (tokenProvider) return;
    const tokenInstall = integrationInstalls.find((install) => install.authMode === 'token');
    if (tokenInstall?.provider) {
      setTokenProvider(tokenInstall.provider);
    }
  }, [integrationInstalls, tokenProvider]);
  const showToast = (message, tone = 'info') => {
    // Never use window.alert for approval flows; use toast + state updates instead.
    if (!message) return;
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  const parseListInput = (value) => {
    if (!value) return [];
    return value
      .split(/[\n,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  };

  const parseJsonInput = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const normalizeTriggerVariables = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') {
      return Object.entries(value).map(([key, entryValue]) => ({
        key,
        value: entryValue
      }));
    }
    return null;
  };

  function safeGetLocalStorage(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function safeSetLocalStorage(key, value) {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(key, value);
    } catch {
      // Ignore storage failures.
    }
  }

  function safeRemoveLocalStorage(key) {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  }

  function safeGetSessionStorage(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function safeSetSessionStorage(key, value) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage.setItem(key, value);
    } catch {
      // Ignore storage failures.
    }
  }

  function safeRemoveSessionStorage(key) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  }

  const formatMaybeId = (value) => {
    if (!value) return '—';
    const text = String(value);
    if (text.includes('@')) return text;
    if (text.length <= 16) return text;
    return 'Unknown identifier';
  };

  useEffect(() => {
    const stored = safeGetSessionStorage('attestDebugEvents');
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
        setDebugEvents(parsed.map((entry) => ({
          time: entry.split(' · ')[0],
          message: entry.split(' · ').slice(1).join(' · ')
        })));
        return;
      }
      if (Array.isArray(parsed)) {
        setDebugEvents(parsed);
      }
    } catch {
      // Ignore stored debug state errors.
    }
  }, []);

  function normalizeWallet(value) {
    return String(value || '').trim().toLowerCase();
  }

  function resolveCouncilMode(payload) {
    if (!payload) return null;
    return payload?.governance?.mode || payload?.mode || null;
  }

  function isActiveCouncilMode(payload) {
    return resolveCouncilMode(payload) === 'ACTIVE';
  }

  function isWalletAddress(value) {
    return /^0x[a-fA-F0-9]{40}$/.test(String(value || '').trim());
  }

  async function copyToClipboard(value) {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return false;
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }

  function downloadTextFile(filename, content) {
    if (typeof document === 'undefined') return;
    const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function normalizeWalletList(list) {
    if (!Array.isArray(list)) return [];
    const normalized = list.map((entry) => normalizeWallet(entry)).filter(Boolean);
    return Array.from(new Set(normalized)).sort();
  }

  function sameWalletSet(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  function parsePayloadJson(value) {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  const resolveUserFromQuery = (value) => {
    if (!value) return null;
    return tenantUsers.find((user) => (user.email || user.displayName || user.id) === value) || null;
  };

  const cardToneFor = (overallStatus) => {
    switch (overallStatus) {
      case 'EXECUTED_SUCCESS':
      case 'EXECUTED_FAILED':
        return 'card-executed';
      case 'APPROVED_PENDING_EXECUTION':
        return 'card-approved';
      case 'EXPIRED':
        return 'card-expired';
      default:
        return 'card-pending';
    }
  };

  const policyDisplayFor = (policyId) => {
    const normalized = String(policyId || '').toUpperCase();
    if (normalized === 'PRODUCTION_POLICY') {
      return '2 SECURITY + 1 PROD approval required';
    }
    if (normalized === 'TWO_PERSON_POLICY') {
      return 'SECURITY + PROD approval required';
    }
    return normalized ? 'Approval policy enforced' : 'Approval required';
  };

  const policyRolesFor = (policyId) => {
    const normalized = String(policyId || '').toUpperCase();
    const match = governancePolicies.find((policy) => policy.id === policyId);
    const roles = match?.requiredRoles || match?.requiredRoleIds || match?.required_roles || match?.roles || [];
    if (Array.isArray(roles) && roles.length > 0) return roles;
    if (normalized === 'PRODUCTION_POLICY') {
      return ['SECURITY_APPROVER', 'SECURITY_APPROVER', 'PROD_APPROVER'];
    }
    if (normalized === 'TWO_PERSON_POLICY') {
      return ['SECURITY_APPROVER', 'PROD_APPROVER'];
    }
    return [];
  };

  const requiredRolesForIntent = (intent) => {
    if (!intent) return [];
    if (Array.isArray(intent.required_roles) && intent.required_roles.length > 0) return intent.required_roles;
    if (Array.isArray(intent?.policy?.requiredRoles) && intent.policy.requiredRoles.length > 0) {
      return intent.policy.requiredRoles;
    }
    if (Array.isArray(intent?.policy?.requiredRoleIds) && intent.policy.requiredRoleIds.length > 0) {
      return intent.policy.requiredRoleIds;
    }
    return policyRolesFor(intent.policy_id || intent.policyId);
  };

  const roleNameForId = (roleId) => {
    const match = governanceRoles.find((role) => role.id === roleId);
    if (match?.name) return match.name;
    return humanizeRoleId(roleId, governanceRoles);
  };

  const authorityLabelFor = (value) => {
    const match = authorityOptions.find((option) => option.value === value);
    return match ? match.label : 'Approver';
  };

  const mergeLegacyAuthorities = (entries) => {
    if (!Array.isArray(entries)) return;
    setLegacyAuthorityAssignments((prev) => {
      const merged = new Map();
      (prev || []).forEach((entry) => {
        const key = `${entry.userId || entry.walletAddress || 'unknown'}:${entry.authority}`;
        merged.set(key, entry);
      });
      entries.forEach((entry) => {
        const key = `${entry.userId || entry.walletAddress || 'unknown'}:${entry.authority}`;
        if (!merged.has(key)) {
          merged.set(key, entry);
        }
      });
      return Array.from(merged.values());
    });
  };

  const summarizeGovernanceAction = (action) => {
    const params = action?.params || {};
    switch (action?.type) {
      case 'ADD_GC_MEMBER':
        return params.userEmail ? `Member: ${params.userEmail}` : 'Member added';
      case 'REMOVE_GC_MEMBER':
        return params.userEmail ? `Remove: ${params.userEmail}` : 'Member removed';
      case 'SET_GC_QUORUM':
        return params.quorumM ? `Quorum: ${params.quorumM}` : null;
      case 'CREATE_ROLE':
        return params.name ? `Role: ${params.name}` : null;
      case 'ASSIGN_ROLE':
        if (params.userEmail && params.roleName) return `Assign: ${params.userEmail} → ${params.roleName}`;
        if (params.roleName) return `Assign role: ${params.roleName}`;
        return 'Assign role';
      case 'REVOKE_AUTHORITY':
        return params.walletAddress ? 'Revoke wallet authority' : 'Revoke authority';
      case 'CREATE_POLICY':
        return params.name ? `Policy: ${params.name}` : null;
      case 'ACTIVATE_POLICY':
        return params.policyId ? `Activate: ${humanizePolicyId(params.policyId, governancePolicies)}` : null;
      case 'BIND_POLICY':
        return params.targetRef ? `Target: ${params.targetRef}` : null;
      case 'CONFIGURE_INTEGRATION':
        return params.provider ? `Provider: ${params.provider}` : null;
      case 'CREATE_INTEGRATION_TOKEN':
        return params.provider ? `Token for: ${params.provider}` : 'Create integration token';
      case 'REVOKE_INTEGRATION_TOKEN':
        return params.provider ? `Revoke token: ${params.provider}` : 'Revoke integration token';
      case 'ENABLE_INTEGRATION':
      case 'DISABLE_INTEGRATION':
        return params.provider ? `Provider: ${params.provider}` : null;
      case 'CONFIGURE_INTEGRATION_TRIGGER':
      case 'DISABLE_INTEGRATION_TRIGGER':
        if (params.scopeType && params.scopeId) {
          return `Trigger: ${params.scopeType} ${params.scopeId}`;
        }
        return params.provider ? `Trigger provider: ${params.provider}` : null;
      default:
        return null;
    }
  };

  const renderWizardActions = (actions, options = {}) => {
    const { readOnly = false } = options;

    if (!actions || actions.length === 0) {
      return (
        <p className="panel-hint">No pending governance actions.</p>
      );
    }
    return (
      <ul className="gov-list compact">
        {actions.map((action) => {
          const signaturesCounted = Number.isFinite(action.signaturesCounted)
            ? Number(action.signaturesCounted)
            : Number(action.signatures || 0);
          const quorumReached = signaturesCounted >= action.quorumM && action.quorumM > 0;
          const adminOnly = action.requiresQuorum === false && isBootstrapMode && !isProvisionalAdmin;
          const proposerId = action.createdByUserId || action.created_by_user_id || null;
          const isProposer = !isBootstrapMode && proposerId && currentUserId && proposerId === currentUserId;
          const canSignActionBase = action.canSign !== undefined
            ? action.canSign
            : (canSignGovernance && !adminOnly);
          const signatureRecords = Array.isArray(action.signatureRecords) ? action.signatureRecords : [];
          const signedUserIds = new Set(signatureRecords.map((sig) => sig.userId).filter(Boolean));
          const alreadySigned = !!(currentUserId && signedUserIds.has(currentUserId));
          const canSignAction = canSignActionBase && !isProposer && !alreadySigned;
          const canApplyAction = action.canApply !== undefined
            ? action.canApply
            : (canApplyGovernance && quorumReached && !isBootstrapMode);
          const isDisabled = readOnly || !canSignAction;
          const params = action.params || {};
          const proposerUser = tenantUsers.find((u) => u.id === action.createdByUserId || u.email === action.createdByUserId || u.id === action.created_by_user_id);
          const proposedBy = action.actorEmail || proposerUser?.email || action.createdByUserId || action.created_by_user_id || '—';
          const createdAt = action.createdAt || action.created_at ? (new Date(action.createdAt || action.created_at)).toLocaleString() : '—';
          const statusLabel = action.state === 'APPLIED'
            ? 'Executed'
            : action.state === 'SIGNED' || action.state === 'QUORUM_PENDING'
              ? 'In progress'
              : action.state === 'PROPOSED'
                ? 'Pending'
                : action.state || '—';
          const approvalsRequired = action.quorumM || '—';
          const memberIds = governanceMembers.map((member) => member.userId || member.id).filter(Boolean);
          const waitingOnIds = memberIds.filter((id) => id !== proposerId && !signedUserIds.has(id));
          const waitingLabels = waitingOnIds.map((id) => {
            const user = tenantUsers.find((u) => u.id === id);
            return user?.email || user?.displayName || id;
          }).filter(Boolean);
          const signDisabledReason = isProposer
            ? 'Proposer cannot approve their own change.'
            : alreadySigned
              ? 'You already signed this change.'
              : adminOnly
                ? 'Only the bootstrap admin can sign this action.'
                : '';
          const statusTone = statusLabel === 'Executed'
            ? 'success'
            : statusLabel === 'Pending'
              ? 'warning'
              : statusLabel === 'In progress'
                ? 'default'
                : 'muted';
          const actionTitle = humanizeGovernanceActionType(action.type || 'Action');
          const summaryLine = summarizeGovernanceAction(action);
          return (
            <li key={action.id} className="gov-action-row">
              <div className="gov-action-summary">
                <div className="gov-action-header">
                  <strong>{actionTitle}</strong>
                  <StatusBadge status={statusLabel} tone={statusTone} />
                </div>
                {summaryLine && <div className="panel-hint">{summaryLine}</div>}
                <div className="panel-hint">
                  Proposed by: <span title={proposedBy}>{formatMaybeId(proposedBy)}</span>
                </div>
                <div className="panel-hint">Approvals: {signaturesCounted}/{approvalsRequired}</div>
                {waitingLabels.length > 0 && (
                  <div className="panel-hint">Waiting on: {waitingLabels.join(', ')}</div>
                )}
                <div className="panel-hint">Created: {createdAt}</div>
                <button
                  type="button"
                  className="link-toggle"
                  onClick={() => {
                    setDrawerData({
                      summary: `${actionTitle} proposed by ${proposedBy}. ${signaturesCounted}/${approvalsRequired} approvals recorded.`,
                      audit: [
                        { id: 'action-id', label: `actionId: ${action.id || '—'}` },
                        { id: 'type', label: `type: ${action.type || '—'}` },
                        { id: 'proposer', label: `proposer: ${proposedBy}` },
                        { id: 'status', label: `status: ${action.state || '—'}` },
                        { id: 'approvals', label: `approvals: ${signaturesCounted}/${approvalsRequired}` }
                      ],
                      rawJson: { action, signatureRecords },
                      copyItems: [
                        { label: 'Action ID', value: action.id || '' },
                        { label: 'Proposer', value: proposedBy || '' }
                      ]
                    });
                    openDrawer('events');
                  }}
                >
                  View audit events
                </button>
              </div>
              <div className="gov-action-buttons">
                <button
                  className="connect-button"
                  onClick={() => signGovernanceAction(action.id)}
                  disabled={isDisabled}
                  title={isProposer
                    ? 'Proposers cannot sign their own action'
                    : adminOnly
                      ? 'Only the bootstrap admin can sign this action'
                      : (isDisabled ? 'Action disabled in current mode' : '')}
                >
                  {alreadySigned ? 'Signed' : 'Sign'}
                </button>
                {/* Manual Apply removed: actions auto-apply when quorum is reached */}
                <button
                  className="connect-button secondary"
                  onClick={() => loadGovernanceActionDetails(action.id)}
                >
                  View
                </button>
                {signDisabledReason && (
                  <div className="panel-hint">{signDisabledReason}</div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const wizardActionsFor = (types) => {
    const typeSet = new Set(types);
    return governanceActions.filter((action) => typeSet.has(action.type) && action.state !== 'APPLIED');
  };

  const renderGovernanceWizard = () => {
    const canViewGovernanceWizard = isLoggedIn && (canGovern || isBootstrapMode); // Any logged in GC member can view

    if (!isLoggedIn) {
      return (
        <div className="panel-section">
          <p className="panel-hint">Sign in to start governance setup.</p>
          <button className="connect-button" onClick={startOidcLogin}>
            Sign in with {oidcProvider === 'google' ? 'Google' : 'OIDC'}
          </button>
        </div>
      );
    }

    if (!canViewGovernanceWizard) {
      return (
        <div className="panel-section">
          <h4>Setup in progress</h4>
          <p className="panel-hint">Governance setup is managed by the council.</p>
        </div>
      );
    }

    const canProposeOrSignBootstrap = isBootstrapMode && isProvisionalAdmin;

    // If in bootstrap mode and not the provisional admin, show a read-only message.
    if (isBootstrapMode && !isProvisionalAdmin) {
      return (
        <div className="wizard-panel">
          <h4>Governance Setup in Progress</h4>
          <p className="panel-hint">
            Tenant is in BOOTSTRAP mode. Setup is managed by the provisional administrator ({sessionInfo?.governance?.provisionalAdminUserId || 'Unknown'}).
          </p>
          <p className="panel-hint">
            You are a council member. Bind your wallet and wait for the provisional admin to complete initial setup.
          </p>
          <div className="panel-section">
            <h5>Pending bootstrap actions</h5>
            {renderWizardActions(wizardActionsFor(['ADD_GC_MEMBER', 'SET_GC_QUORUM']), { readOnly: false })}
          </div>
        </div>
      );
    }

    const canDriveWizard = isBootstrapMode ? isProvisionalAdmin : canGovern;
    const memberCount = sessionInfo?.governance?.memberCount || governanceMembers.length;
    const rolesReady = governanceRoles.length > 0;
    const assignmentsReady = governanceRoleAssignments.length > 0;
    const policiesReady = governancePolicies.length > 0;
    const bindingsReady = governancePolicyBindings.length > 0;
    const quorumActive = governanceMode === 'ACTIVE';

    const steps = [
      { title: 'Welcome', complete: true },
      { title: 'Add Governance Members', complete: memberCount >= 3 && wizardActionsFor(['ADD_GC_MEMBER']).length === 0 },
      { title: 'Set Quorum + Activate', complete: quorumActive },
      { title: 'Define Approval Roles', complete: rolesReady },
      { title: 'Assign Approvers', complete: assignmentsReady },
      { title: 'Create Approval Policy', complete: policiesReady },
      { title: 'Bind Policy to Target', complete: bindingsReady }
    ];
    const nextIncompleteStep = steps.findIndex((step, index) => index > 0 && !step.complete);
    const derivedStep = nextIncompleteStep === -1 ? steps.length - 1 : nextIncompleteStep;
    const currentStep = Math.max(
      0,
      Math.min(canDriveWizard ? setupStep : derivedStep, steps.length - 1)
    );

    return (
      <>
        <datalist id="tenant-users-list">
          {tenantUsers.map((user) => (
            <option key={user.id} value={user.email || user.displayName || user.id} />
          ))}
        </datalist>
        {/* action detail modal is handled by existing `governanceDetail` modal, open via loadGovernanceActionDetails */}
        <div className="wizard">
          <div className="wizard-header">
            <h3>Governance Setup</h3>
            <p className="panel-hint">
              Tenant: {sessionInfo?.tenant?.domain || '—'} · Mode: {governanceMode}
            </p>
            <div style={{ marginTop: 8 }}>
              {isProvisionalAdmin && (
                <button className="panel-button" onClick={async () => {
                  try {
                    setGovernanceSubmitting('CREATE_BOOTSTRAP_PROCESS');
                    const resp = await apiFetch('/api/v1/processes/tenant-bootstrap', { method: 'POST' });
                    const data = await resp.json();
                    if (!resp.ok) throw new Error(data.error || 'Failed to create process');
                    showToast('Bootstrap process created.', 'success');
                    await loadGovernanceData();
                    setGovernanceSubmitting('');
                  } catch (error) {
                    showToast(error.message || 'Failed to create bootstrap process', 'error');
                    setGovernanceSubmitting('');
                  }
                }}>Create tenant bootstrap process</button>
              )}
              {canGovern && (
                <button className="panel-button" style={{ marginLeft: 8 }} onClick={async () => {
                  try {
                    setGovernanceSubmitting('CREATE_COUNCIL_PROCESS');
                    const resp = await apiFetch('/api/v1/processes/council-lifecycle', { method: 'POST', body: JSON.stringify({}) });
                    const data = await resp.json();
                    if (!resp.ok) throw new Error(data.error || 'Failed to create process');
                    showToast('Council lifecycle process created.', 'success');
                    await loadGovernanceData();
                    setGovernanceSubmitting('');
                  } catch (error) {
                    showToast(error.message || 'Failed to create council process', 'error');
                    setGovernanceSubmitting('');
                  }
                }}>Propose council lifecycle process</button>
              )}
            </div>
          </div>
          <div className="wizard-steps">
            {steps.map((step, index) => (
              <div key={step.title} className={`wizard-step ${index === currentStep ? 'active' : ''} ${step.complete ? 'complete' : ''}`}>
                <span>{index + 1}</span>
                <span>{step.title}</span>
              </div>
            ))}
          </div>

            {currentStep === 0 && (
              <div className="wizard-panel">
                <p>You are the provisional governance administrator for this tenant.</p>
                <button className="connect-button" onClick={() => setSetupStep(1)} disabled={!canDriveWizard}>Continue</button>
                {!canDriveWizard && (
                  <p className="panel-hint">Read-only view. Setup is managed by the bootstrap admin.</p>
                )}
              </div>
            )}

            {currentStep === 1 && (
              <div className="wizard-panel">
                <h4>Add Governance Members</h4>
                <p className="panel-hint">Add at least 2 more members to continue.</p>
                <ul className="gov-list">
                  {governanceMembers.map((member) => (
                    <li key={member.id}>{member.email || member.displayName || member.userId}</li>
                  ))}
                </ul>
                <div className="gov-form">
                  <select
                    className="gov-input"
                    value={govMemberId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setGovMemberId(id);
                      const match = tenantUsers.find((u) => u.id === id);
                      setGovMemberQuery(match ? (match.email || match.displayName || match.id) : '');
                    }}
                  >
                    <option value="">Select member…</option>
                    {tenantUsers.map((user) => {
                      const label = user.email || user.displayName || user.id;
                      const already = governanceMembers.some((m) => (m.id === user.id || m.userId === user.id || m.email === user.email));
                      return (
                        <option key={user.id} value={user.id} disabled={already}>
                          {already ? `${label} (Already a GC member)` : label}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    className="connect-button"
                    onClick={() => proposeGovernanceAction('ADD_GC_MEMBER', { userId: govMemberId })}
                    disabled={!canDriveWizard || !govMemberId || governanceSubmitting === 'ADD_GC_MEMBER'}
                  >
                    {governanceSubmitting === 'ADD_GC_MEMBER' ? 'Submitting…' : 'Propose add'}
                  </button>
                </div>
                <div className="panel-section">
                  <h5>Pending member actions</h5>
                  {renderWizardActions(wizardActionsFor(['ADD_GC_MEMBER']))}
                </div>
                <button className="connect-button secondary" onClick={() => setSetupStep(2)} disabled={!canDriveWizard || !steps[1].complete}>
                  Continue
                </button>
              </div>
            )}

            {currentStep === 2 && (
              <div className="wizard-panel">
                <h4>Set Quorum + Activate</h4>
                <p className="panel-hint">Recommended: 2-of-{memberCount}.</p>
                <div className="gov-form">
                  <input
                    className="gov-input"
                    value={govQuorumM}
                    onChange={(event) => setGovQuorumM(event.target.value)}
                    placeholder="Quorum M"
                  />
                  <button
                    className="connect-button"
                    onClick={() => proposeGovernanceAction('SET_GC_QUORUM', { quorumM: Number(govQuorumM) })}
                    disabled={!canDriveWizard || governanceSubmitting === 'SET_GC_QUORUM'}
                  >
                    {governanceSubmitting === 'SET_GC_QUORUM' ? 'Submitting…' : 'Propose quorum'}
                  </button>
                </div>
                <div className="panel-section">
                  <h5>Pending quorum actions</h5>
                  {renderWizardActions(wizardActionsFor(['SET_GC_QUORUM']))}
                </div>
                <button className="connect-button secondary" onClick={() => setSetupStep(3)} disabled={!canDriveWizard || !steps[2].complete}>
                  Continue
                </button>
              </div>
            )}

            {currentStep === 3 && (
              <div className="wizard-panel">
                <h4>Define Approval Roles</h4>
                <p className="panel-hint">Create at least one role.</p>
                <ul className="gov-list">
                  {governanceRoles.map((role) => (
                    <li key={role.id}>{role.name}</li>
                  ))}
                </ul>
                <div className="gov-form stack">
                  <input
                    className="gov-input"
                    placeholder="Role name"
                    value={roleName}
                    onChange={(event) => setRoleName(event.target.value)}
                  />
                  <input
                    className="gov-input"
                    placeholder="Description (optional)"
                    value={roleDescription}
                    onChange={(event) => setRoleDescription(event.target.value)}
                  />
                  <button
                    className="connect-button"
                    onClick={() => proposeGovernanceAction('CREATE_ROLE', { name: roleName, description: roleDescription })}
                    disabled={!canDriveWizard || !roleName.trim() || governanceSubmitting === 'CREATE_ROLE'}
                  >
                    {governanceSubmitting === 'CREATE_ROLE' ? 'Submitting…' : 'Propose role'}
                  </button>
                </div>
                <div className="panel-section">
                  <h5>Pending role actions</h5>
                  {renderWizardActions(wizardActionsFor(['CREATE_ROLE']))}
                </div>
                <button className="connect-button secondary" onClick={() => setSetupStep(4)} disabled={!canDriveWizard || !steps[3].complete}>
                  Continue
                </button>
              </div>
            )}

            {currentStep === 4 && (
              <div className="wizard-panel">
                <h4>Assign Approvers</h4>
                <p className="panel-hint">Assign roles to users.</p>
                <ul className="gov-list">
                  {governanceRoleAssignments.map((assignment) => (
                    <li key={assignment.id}>
                      <span title={assignment.userId || ''}>
                        {assignment.userEmail || 'Council member'}
                      </span>
                      {' · '}
                      <span title={assignment.roleId}>{humanizeRoleId(assignment.roleId, governanceRoles)}</span>
                    </li>
                  ))}
                </ul>
                <div className="gov-form">
                  <input
                    className="gov-input"
                    list="tenant-users-list"
                    value={assignmentUserQuery}
                    onChange={(event) => {
                      const value = event.target.value;
                      setAssignmentUserQuery(value);
                      const match = resolveUserFromQuery(value);
                      setAssignmentUserId(match ? match.id : '');
                    }}
                    placeholder="Select user"
                  />
                  <select
                    className="gov-input"
                    value={assignmentRoleId}
                    onChange={(event) => setAssignmentRoleId(event.target.value)}
                  >
                    <option value="">Select role</option>
                    {roleOptionsList.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="connect-button"
                    onClick={() => proposeGovernanceAction('ASSIGN_ROLE', { userId: assignmentUserId, roleId: assignmentRoleId })}
                    disabled={!canDriveWizard || !assignmentUserId || !assignmentRoleId || governanceSubmitting === 'ASSIGN_ROLE'}
                  >
                    {governanceSubmitting === 'ASSIGN_ROLE' ? 'Submitting…' : 'Propose assign'}
                  </button>
                </div>
                <div className="panel-section">
                  <h5>Pending assignment actions</h5>
                  {renderWizardActions(wizardActionsFor(['ASSIGN_ROLE']))}
                </div>
                <button className="connect-button secondary" onClick={() => setSetupStep(5)} disabled={!canDriveWizard || !steps[4].complete}>
                  Continue
                </button>
              </div>
            )}

            {currentStep === 5 && (
              <div className="wizard-panel">
                <h4>Create Approval Policy</h4>
                <p className="panel-hint">Create at least one approval policy.</p>
                <div className="gov-form stack">
                  <input
                    className="gov-input"
                    placeholder="Policy name"
                    value={policyName}
                    onChange={(event) => setPolicyName(event.target.value)}
                  />
                  <input
                    className="gov-input"
                    placeholder="Description (optional)"
                    value={policyDescription}
                    onChange={(event) => setPolicyDescription(event.target.value)}
                  />
                  <div className="gov-checkboxes">
                    {roleOptionsList.map((role) => (
                      <label key={role.value} className="gov-checkbox">
                        <input
                          type="checkbox"
                          checked={policyRoleIds.includes(role.value)}
                          onChange={(event) => {
                            if (event.target.checked) {
                              setPolicyRoleIds((prev) => [...prev, role.value]);
                            } else {
                              setPolicyRoleIds((prev) => prev.filter((id) => id !== role.value));
                            }
                          }}
                        />
                        <span>{role.label}</span>
                      </label>
                    ))}
                  </div>
                  <select
                    className="gov-input"
                    value={policyScopeEnv}
                    onChange={(event) => setPolicyScopeEnv(event.target.value)}
                  >
                    <option value="ALL">Scope: All environments</option>
                    <option value="PROD">Scope: Production</option>
                    <option value="STAGING">Scope: Staging</option>
                  </select>
                  <button
                    className="connect-button"
                    onClick={() => proposeGovernanceAction('CREATE_POLICY', {
                      name: policyName,
                      description: policyDescription,
                      requiredRoleIds: policyRoleIds,
                      scopeEnv: policyScopeEnv
                    })}
                    disabled={!canDriveWizard || !policyName.trim() || policyRoleIds.length === 0 || governanceSubmitting === 'CREATE_POLICY'}
                  >
                    {governanceSubmitting === 'CREATE_POLICY' ? 'Submitting…' : 'Propose policy'}
                  </button>
                </div>
                <div className="panel-section">
                  <h5>Pending policy actions</h5>
                  {renderWizardActions(wizardActionsFor(['CREATE_POLICY']))}
                </div>
                <button className="connect-button secondary" onClick={() => setSetupStep(6)} disabled={!canDriveWizard || !steps[5].complete}>
                  Continue
                </button>
              </div>
            )}

            {currentStep === 6 && (
              <div className="wizard-panel">
                <h4>Activate + Bind Policy</h4>
                {steps[6].complete ? (
                  <>
                    <p>Setup complete. You can now access the full governance console.</p>
                    <button
                      className="connect-button"
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window.location.href = '/governance';
                        }
                      }}
                    >
                      Go to Governance Console
                    </button>
                  </>
                ) : (
                  <>
                    <div className="gov-form">
                      <select
                        className="gov-input"
                        value={policyDraftId}
                        onChange={(event) => setPolicyDraftId(event.target.value)}
                      >
                        <option value="">Select draft policy</option>
                        {governancePolicies.filter((policy) => policy.status === 'DRAFT').map((policy) => (
                          <option key={policy.id} value={policy.id}>
                            {policy.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="connect-button secondary"
                        onClick={() => proposeGovernanceAction('ACTIVATE_POLICY', { policyId: policyDraftId })}
                        disabled={!canDriveWizard || !policyDraftId || governanceSubmitting === 'ACTIVATE_POLICY'}
                      >
                        {governanceSubmitting === 'ACTIVATE_POLICY' ? 'Submitting…' : 'Propose activate'}
                      </button>
                    </div>
                    <div className="gov-form">
                      <select
                        className="gov-input"
                        value={policyActiveId}
                        onChange={(event) => setPolicyActiveId(event.target.value)}
                      >
                        <option value="">Select active policy</option>
                        {governancePolicies.filter((policy) => policy.status === 'ACTIVE').map((policy) => (
                          <option key={policy.id} value={policy.id}>
                            {policy.name}
                          </option>
                        ))}
                      </select>
                      <select
                        className="gov-input"
                        value={policyBindingMode}
                        onChange={(event) => setPolicyBindingMode(event.target.value)}
                      >
                        <option value="legacy">Binding: Legacy target_ref</option>
                        <option value="provider">Binding: Provider resource</option>
                      </select>
                      {policyBindingMode === 'provider' ? (
                        <>
                          <input
                            className="gov-input"
                            placeholder="Provider (e.g., gitlab)"
                            value={policyBindingProvider}
                            onChange={(event) => setPolicyBindingProvider(event.target.value)}
                          />
                          <input
                            className="gov-input"
                            placeholder="Resource type (e.g., gitlab_project)"
                            value={policyBindingResourceType}
                            onChange={(event) => setPolicyBindingResourceType(event.target.value)}
                          />
                          <input
                            className="gov-input"
                            placeholder="Resource id (e.g., GitLab project id)"
                            value={policyBindingResourceId}
                            onChange={(event) => setPolicyBindingResourceId(event.target.value)}
                          />
                          <textarea
                            className="gov-input"
                            placeholder="Scopes JSON (optional)"
                            rows={3}
                            value={policyBindingScopesText}
                            onChange={(event) => setPolicyBindingScopesText(event.target.value)}
                          />
                        </>
                      ) : (
                        <input
                          className="gov-input"
                          placeholder="Target reference (gitlab:group/... or gitlab:project/... )"
                          value={policyTargetRef}
                          onChange={(event) => setPolicyTargetRef(event.target.value)}
                        />
                      )}
                      <select
                        className="gov-input"
                        value={policyTargetEnv}
                        onChange={(event) => setPolicyTargetEnv(event.target.value)}
                      >
                        <option value="ALL">Environment: All</option>
                        <option value="PROD">Environment: Production</option>
                        <option value="STAGING">Environment: Staging</option>
                      </select>
                      <button
                        className="connect-button secondary"
                        onClick={() => {
                          if (policyBindingMode === 'provider') {
                            const scopes = parseJsonInput(policyBindingScopesText);
                            if (policyBindingScopesText.trim() && !scopes) {
                              showToast('Invalid scopes JSON.', 'error');
                              return;
                            }
                            proposeGovernanceAction('BIND_POLICY', {
                              policyId: policyActiveId,
                              envScope: policyTargetEnv,
                              provider: policyBindingProvider.trim(),
                              resourceType: policyBindingResourceType.trim(),
                              resourceId: policyBindingResourceId.trim(),
                              scopes
                            });
                            return;
                          }
                          proposeGovernanceAction('BIND_POLICY', {
                            policyId: policyActiveId,
                            targetRef: policyTargetRef,
                            envScope: policyTargetEnv
                          });
                        }}
                        disabled={!canDriveWizard || !policyActiveId || (policyBindingMode === 'provider'
                          ? !policyBindingProvider.trim() || !policyBindingResourceType.trim() || !policyBindingResourceId.trim()
                          : !policyTargetRef.trim()) || governanceSubmitting === 'BIND_POLICY'}
                      >
                        {governanceSubmitting === 'BIND_POLICY' ? 'Submitting…' : 'Propose bind'}
                      </button>
                    </div>
                    <div className="panel-section">
                      <h5>Pending activation/binding</h5>
                      {renderWizardActions(wizardActionsFor(['ACTIVATE_POLICY', 'BIND_POLICY']))}
                    </div>
                  </>
                )}
              </div>
            )}
        )}
      </div>
    </>
  );
};

  const displayIdentity = identityProfile?.email || identityProfile?.name || identity || 'Unknown User';
  const pathname = currentPath || (typeof window !== 'undefined' ? window.location.pathname : '');
  const showGovernanceTest = pathname.startsWith('/governance/test');
  const showGovernanceSetup = pathname.startsWith('/governance/setup');
  const governanceProcessMatch = pathname.match(/^\/governance\/processes\/([^/]+)/);
  const governanceProcessId = governanceProcessMatch ? governanceProcessMatch[1] : '';
  const showGovernanceProcess = !!governanceProcessId;
  const authLoading = identityLoading && !sessionInfo;
  const { connectedAddress, signMessage } = useWallet();
  const councilSetupStatus = useCouncilSetupStatus({ enabled: showGovernanceSetup && !authLoading });
  const councilSnapshot = useCouncilSnapshot({ enabled: showGovernanceSetup && !authLoading });
  const councilProcess = useProcess(governanceProcessId, { enabled: showGovernanceProcess && !authLoading, poll: true });
  const latestCouncilProcess = useProcess(lastCouncilProcessId, {
    enabled: showGovernanceSetup && !authLoading && !!lastCouncilProcessId,
    poll: false
  });
  const showOpsTest = pathname.startsWith('/console/test');
  const showTestHarness = showGovernanceTest || showOpsTest;
  const isLoggedIn = !!sessionInfo?.auth;
  const currentIdentity = sessionInfo?.identity || null;
  const currentUserId = useMemo(() => {
    if (sessionInfo?.identity?.userId) return sessionInfo.identity.userId;
    if (sessionInfo?.userId) return sessionInfo.userId;
    if (!currentIdentity) return null;
    const provider = currentIdentity.provider;
    const subject = currentIdentity.subject;
    const email = currentIdentity.email;
    const byIdentity = tenantUsers.find((user) => user.issuer === provider && user.subject === subject);
    if (byIdentity) return byIdentity.id;
    if (email) {
      const byEmail = tenantUsers.find((user) => user.email === email);
      if (byEmail) return byEmail.id;
    }
    return null;
  }, [tenantUsers, currentIdentity?.provider, currentIdentity?.subject, currentIdentity?.email]);
  const linkedWalletAddress = sessionInfo?.walletBinding?.walletAddress || null;
  const walletLinked = !!(isLoggedIn && account && linkedWalletAddress
    && linkedWalletAddress.toLowerCase() === account.toLowerCase());
  const walletState = !account ? 'NOT_CONNECTED' : (walletLinked ? 'LINKED' : 'CONNECTED');
  const approveEnabled = isLoggedIn && walletLinked;
  // Signing governance actions requires wallet; applying does not
  const canSignGovernance = approveEnabled;
  const canApplyGovernance = isLoggedIn && !!sessionInfo?.governance?.isMember;
  const authorityAssignments = sessionInfo?.authorityAssignments || [];
  const userRoleAssignments = Array.isArray(sessionInfo?.roleAssignments) ? sessionInfo.roleAssignments : [];
  const userRoleAssignmentsFromGovernance = useMemo(() => {
    if (!currentUserId) return [];
    return governanceRoleAssignments
      .filter((assignment) => assignment.userId === currentUserId)
      .map((assignment) => assignment.roleId)
      .filter(Boolean);
  }, [governanceRoleAssignments, currentUserId]);
  const authorityTypes = userRoleAssignments.length > 0
    ? userRoleAssignments
    : userRoleAssignmentsFromGovernance.length > 0
      ? userRoleAssignmentsFromGovernance
    : authorityAssignments.length > 0
      ? authorityAssignments.map((entry) => entry.authority)
      : authorities.map((entry) => entry.type);
  const isAdmin = authorities.some((auth) => auth.type === 'BREAK_GLASS');
  const canGovern = isLoggedIn && !!sessionInfo?.governance?.isMember;
  const canApproveReleases = authorityTypes.length > 0;
  const canAccessGovernance = isLoggedIn;
  const councilMode = resolveCouncilMode(setupStatus)
    || resolveCouncilMode(councilSetupStatus?.data)
    || sessionInfo?.governance?.mode
    || null;
  const councilIsActive = councilMode === 'ACTIVE';
  const isBootstrapMode = councilMode === 'BOOTSTRAP';
  const isProvisionalAdmin = !!sessionInfo?.governance?.isProvisionalAdmin;
  const governanceMode = councilMode || '—';
  const showGovernanceWizard = showGovernanceSetup;
  const displayedRoles = sessionInfo?.roles && sessionInfo.roles.length > 0
    ? sessionInfo.roles
    : authorityTypes.map((role) => roleNameForId(role));
  const roleOptionsList = governanceRoles.map((role) => ({
    value: role.id,
    label: role.name,
    description: role.description || ''
  }));
  const policyBindingPresets = [
    { id: 'gitlab', label: 'GitLab project', provider: 'gitlab', resourceType: 'gitlab_project', resourceId: '' },
    { id: 'databridge', label: 'DataBridge dataset', provider: 'databridge', resourceType: 'databridge_dataset', resourceId: 'customers_pii' }
  ];
  const toTitleCase = (value) => String(value || '').replace(/\b\w/g, (char) => char.toUpperCase());
  const intentPresentation = (intent = {}) => {
    const provider = intent.provider || '';
    const pipelineId = intent.pipeline_id || intent.pipelineId || null;
    const isGitLab = provider === 'gitlab' || !!pipelineId;
    const actionType = intent.action_type || intent.actionType || '';
    const actionLabel = actionType
      ? actionType.replace(/_/g, ' ')
      : isGitLab
        ? 'deployment'
        : 'approval request';
    const resourceType = intent.resource_type || intent.resourceType || '';
    const resourceId = intent.resource_id || intent.resourceId || '';
    const targetLabel = isGitLab
      ? pipelineId ? `Pipeline #${pipelineId}` : 'Pipeline'
      : resourceType || resourceId
        ? `${resourceType || 'resource'}:${resourceId || '—'}`
        : 'Request';
    const title = isGitLab
      ? `Deployment request for Pipeline #${pipelineId || '—'}`
      : `${toTitleCase(actionLabel)} request`;
    const requester = isGitLab
      ? `GitLab Pipeline #${pipelineId || '—'}`
      : `${provider || 'integration'} request`;
    const artifact = isGitLab
      ? `${intent.project_id || 'project'}:${intent.commit_sha ? intent.commit_sha.substring(0, 8) : 'commit'}`
      : targetLabel;
    return {
      provider,
      actionLabel,
      title,
      requester,
      artifact,
      targetLabel,
      pipelineId,
      resourceType,
      resourceId,
      isGitLab
    };
  };

  const governanceDrawerEvents = useMemo(() => {
    const labelMap = {
      PROPOSED: 'Action proposed',
      SIGNED: 'Signature collected',
      ACTION_SIGNED: 'Signature collected',
      APPLIED: 'Action applied',
      ACTION_APPLIED: 'Action applied',
      CHAIN_POLICY_REGISTERED: 'Policy registered on chain'
    };
    const usersById = new Map();
    const usersByWallet = new Map();
    tenantUsers.forEach((user) => {
      if (user?.id) usersById.set(user.id, user);
      const wallet = user?.walletBinding?.walletAddress;
      if (wallet) usersByWallet.set(normalizeWallet(wallet), user);
    });
    const resolveActor = (log) => {
      const actorEmail = log.actorEmail || log.email || log.actor_email;
      if (actorEmail) return actorEmail;
      const actorUserId = log.actorUserId || log.userId || log.actor_user_id;
      if (actorUserId && usersById.has(actorUserId)) {
        const user = usersById.get(actorUserId);
        return user?.email || user?.displayName || actorUserId;
      }
      const actorWallet = log.actorWallet || log.walletAddress || log.wallet_address;
      if (actorWallet && usersByWallet.has(normalizeWallet(actorWallet))) {
        const user = usersByWallet.get(normalizeWallet(actorWallet));
        return user?.email || user?.displayName || 'Council member';
      }
      return actorWallet ? 'Council member' : '—';
    };
    const parsed = (governanceAuditLogs || [])
      .map((log, index) => {
        const rawType = String(log.eventType || log.type || '').toUpperCase();
        const label = labelMap[rawType] || humanizeEventType(rawType);
        if (!label || String(label).toLowerCase() === 'intent') return null;
        const timestamp = log.createdAt || log.timestamp || null;
        const actor = resolveActor(log);
        return {
          id: `${log.actionId || 'event'}-${index}`,
          label,
          actor,
          timestamp,
          timestampMs: timestamp ? new Date(timestamp).getTime() : 0,
          status: rawType.includes('FAILED') ? 'fail' : 'ok',
          meta: { actionId: log.actionId, rawType }
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.timestampMs - a.timestampMs);
    const grouped = [];
    parsed.forEach((event) => {
      const last = grouped[grouped.length - 1];
      if (last && last.label === event.label && last.actor === event.actor) {
        last.count += 1;
      } else {
        grouped.push({ ...event, count: 1 });
      }
    });
    return grouped.map((event) => {
      const timeLabel = event.timestamp ? formatDateTime(event.timestamp) : '—';
      const secondary = event.actor && event.actor !== '—'
        ? `${event.actor} · ${timeLabel}`
        : timeLabel;
      return {
        ...event,
        secondary
      };
    });
  }, [governanceAuditLogs, tenantUsers]);

  useEffect(() => {
    if (!governanceOnly || activeConsole !== 'governance') return;
    const latestAction = governanceActions[0] || null;
    const latestActionLabel = latestAction
      ? humanizeGovernanceActionType(latestAction.type)
      : 'No recent actions';
    const proposerLabel = latestAction?.proposedByEmail || latestAction?.proposedBy || 'Unknown proposer';
    const summary = latestAction
      ? `${latestActionLabel} proposed by ${proposerLabel}. ${latestAction.signatures || 0}/${latestAction.quorumM || '—'} approvals recorded.`
      : `Council has ${governanceMembers.length} members, ${governanceRoles.length} roles, and ${governancePolicies.length} policies configured.`;
    const auditItems = [
      { id: 'action-id', label: `actionId: ${latestAction?.id || '—'}` },
      { id: 'proposer', label: `proposer: ${latestAction?.proposedByEmail || latestAction?.proposedBy || '—'}` },
      { id: 'status', label: `status: ${latestAction?.state || '—'}` },
      { id: 'approvals', label: `approvals: ${latestAction?.signatures || 0}/${latestAction?.quorumM || '—'}` },
      { id: 'audit-count', label: `events: ${governanceAuditLogs.length}` },
      { id: 'members', label: `members: ${governanceMembers.length}` },
      { id: 'roles', label: `roles: ${governanceRoles.length}` },
      { id: 'policies', label: `policies: ${governancePolicies.length}` }
    ];
    const copyItems = [
      { label: 'Tenant', value: sessionInfo?.tenant?.domain || '' },
      { label: 'Action ID', value: latestAction?.id || '' },
      { label: 'Proposer', value: latestAction?.proposedByEmail || latestAction?.proposedBy || '' }
    ];
    setDrawerData({
      summary,
      events: governanceDrawerEvents,
      audit: auditItems,
      rawJson: { audit: governanceAuditLogs, actions: governanceActions },
      copyItems
    });
  }, [
    governanceOnly,
    activeConsole,
    governanceAuditLogs,
    governanceDrawerEvents,
    governanceActions,
    governanceMembers.length,
    governanceRoles.length,
    governancePolicies.length,
    setDrawerData,
    sessionInfo?.tenant?.domain
  ]);
  const tenantKey = sessionInfo?.tenant?.domain || sessionInfo?.tenant?.id || 'unknown';
  const councilStorageKey = useMemo(() => `latestCouncilProcessId:${tenantKey}`, [tenantKey]);
  const saveLatestProcessId = (processId) => {
    if (!processId) return;
    setLastCouncilProcessId(processId);
    safeSetLocalStorage(councilStorageKey, processId);
  };

  useEffect(() => {
    if (!showGovernanceSetup) return;
    const snapshot = councilSnapshot?.data;
    if (!snapshot) return;
    const council = snapshot.council || snapshot;
    const members = Array.isArray(council?.members) ? council.members : [];
    const quorumM = council?.quorumM ?? council?.quorum_m ?? '';
    const quorumN = council?.quorumN ?? council?.quorum_n ?? members.length ?? '';
    if (!desiredCouncilTouched) {
      const users = Array.isArray(tenantUsers) ? tenantUsers : [];
      const userByWallet = new Map();
      users.forEach((user) => {
        const wallet = user.walletBinding?.walletAddress;
        if (!wallet) return;
        userByWallet.set(normalizeWallet(wallet), user.id);
      });
      const mapped = members
        .map((wallet) => userByWallet.get(normalizeWallet(wallet)))
        .filter(Boolean);
      setDesiredCouncilMembers(mapped);
      setDesiredCouncilQuorumM(quorumM === '' ? '' : String(quorumM));
      setDesiredCouncilQuorumN(quorumN === '' ? '' : String(quorumN));
    }
  }, [showGovernanceSetup, councilSnapshot?.data, desiredCouncilTouched, tenantUsers]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = safeGetLocalStorage(councilStorageKey);
    if (stored) {
      setLastCouncilProcessId(stored);
    }
  }, [councilStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!lastCouncilProcessId) return;
    safeSetLocalStorage(councilStorageKey, lastCouncilProcessId);
  }, [lastCouncilProcessId, councilStorageKey]);

  useEffect(() => {
    setDesiredCouncilTouched(false);
  }, [sessionInfo?.tenant?.domain]);
  const debugAllowed = debugEnabled || isAdmin;
  const walletProviderLabel = connectionType === 'walletconnect'
    ? 'WalletConnect'
    : connectionType === 'injected'
      ? 'MetaMask'
      : 'Not connected';
  const chainLabel = account
    ? (walletNetworkMismatch && walletNetworkId
        ? `Chain ${walletNetworkId}`
        : 'ATTEST-R (1337)')
    : '';
  const apiHealthy = lastIntentFetchAt && (Date.now() - lastIntentFetchAt.getTime() < 60000);
  const chainHealthy = !!provider && !walletNetworkMismatch;
  const cidStatus = releaseRecord?.derived?.verification?.cidRetrievable || null;
  const apiStatusLabel = !isLoggedIn
    ? 'Not connected'
    : !lastIntentFetchAt
      ? 'Not connected'
      : apiHealthy ? 'Connected' : 'Unreachable';
  const chainStatusLabel = !account
    ? 'Not connected'
    : chainHealthy ? 'Connected' : 'Unreachable';
  const ipfsStatusLabel = !isLoggedIn || !cidStatus
    ? 'Not connected'
    : cidStatus === 'PASS' ? 'Connected' : 'Unreachable';
  const statusTone = (label) => {
    if (label === 'Connected') return 'ok';
    if (label === 'Unreachable') return 'bad';
    return 'neutral';
  };

  function extractApproverWallet(entry) {
    if (!entry) return '';
    if (typeof entry === 'string') return normalizeWallet(entry);
    return normalizeWallet(entry.wallet || entry.wallet_address || entry.approver || entry.signer || entry.address);
  }

  function buildApprovalWallets(process, signatureList) {
    const source = Array.isArray(process?.approvalWallets)
      ? process.approvalWallets
      : Array.isArray(signatureList)
        ? signatureList
        : Array.isArray(process?.signatures)
          ? process.signatures
          : [];
    const normalized = source.map((entry) => extractApproverWallet(entry)).filter(Boolean);
    return Array.from(new Set(normalized));
  }

  function WalletChip({ value }) {
    if (!value) return null;
    return (
      <div className="wallet-chip">
        <span title={value}>Council member</span>
      </div>
    );
  }

  function StatePill({ state }) {
    const tone = state === 'GC_FINALIZED' ? 'ok' : state === 'GC_SIGNING_OPEN' ? 'warn' : 'neutral';
    return (
      <span className={`state-pill ${tone}`}>
        {state || 'Unknown'}
      </span>
    );
  }

  function ProgressBar({ value, max }) {
    const safeMax = Number(max) > 0 ? Number(max) : 0;
    const safeValue = Number(value) > 0 ? Number(value) : 0;
    const pct = safeMax ? Math.min(100, Math.round((safeValue / safeMax) * 100)) : 0;
    return (
      <div className="progress-bar">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-label">{safeValue} / {safeMax || '—'}</div>
      </div>
    );
  }

  function CouncilConfigCard({ title, subtitle, children }) {
    return (
      <div className="gov-card">
        <div className="gov-card-header">
          <h3>{title}</h3>
          {subtitle && <p className="panel-hint">{subtitle}</p>}
        </div>
        <div className="gov-card-body">
          {children}
        </div>
      </div>
    );
  }

  function DesiredCouncilEditor({
    users,
    selectedUserIds,
    onSelectionChange,
    quorumM,
    quorumN,
    onQuorumMChange,
    onQuorumNChange,
    missingWalletUsers,
    quorumMismatch
  }) {
    const availableUsers = Array.isArray(users) ? users : [];
    const selectedSet = new Set(selectedUserIds || []);
    return (
      <div className="desired-editor">
        <div className="desired-members">
          <div className="desired-members-header">
            <h4>Desired members</h4>
          </div>
          {availableUsers.length === 0 && (
            <p className="panel-hint">No tenant users available.</p>
          )}
          {(selectedUserIds || []).length === 0 && (
            <p className="panel-hint">Add at least one council wallet.</p>
          )}
          <div className="desired-member-list">
            {availableUsers.map((user) => {
              const label = user.displayName || user.email || user.id;
              const emailLabel = user.email ? ` (${user.email})` : '';
              const linked = !!user.walletBinding?.linked;
              const badgeLabel = linked ? 'Wallet linked' : 'Not linked';
              const badgeTone = linked ? 'success' : 'failed';
              return (
                <label key={user.id} className="member-select-row">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(user.id)}
                    onChange={() => {
                      const next = new Set(selectedSet);
                      if (next.has(user.id)) {
                        next.delete(user.id);
                      } else {
                        next.add(user.id);
                      }
                      onSelectionChange(Array.from(next));
                    }}
                  />
                  <span className="member-select-label">{label}{emailLabel}</span>
                  <span className={`status-badge ${badgeTone}`}>{badgeLabel}</span>
                </label>
              );
            })}
          </div>
          {missingWalletUsers.length > 0 && (
            <p className="panel-hint warn">All selected members must have linked wallets.</p>
          )}
        </div>
        <div className="desired-quorum">
          <div className="desired-quorum-row">
            <input
              className="gov-input"
              value={quorumM}
              onChange={(event) => onQuorumMChange(event.target.value)}
              placeholder="Quorum M"
            />
            <input
              className="gov-input"
              value={quorumN}
              onChange={(event) => onQuorumNChange(event.target.value)}
              placeholder="Quorum N"
            />
          </div>
          {quorumMismatch && (
            <p className="panel-hint warn">Quorum N must equal number of members.</p>
          )}
        </div>
      </div>
    );
  }

  function ProcessSummaryCard({ processId, onOpen, onClear, lookupValue, onLookupChange }) {
    return (
      <div className="gov-card">
        <div className="gov-card-header">
          <h3>Latest council lifecycle process</h3>
          <p className="panel-hint">Open the most recent proposal for this tenant.</p>
        </div>
        <div className="gov-card-body">
          {processId ? (
            <div className="process-summary-row">
              <div>
                <div className="panel-hint">Process ID</div>
                <div className="process-id-row">
                  <span title={processId}>Process on record</span>
                </div>
              </div>
              <div className="process-summary-actions">
                <button type="button" className="connect-button" onClick={() => onOpen(processId)}>
                  Open process
                </button>
                <button type="button" className="connect-button secondary" onClick={onClear}>
                  Clear
                </button>
              </div>
            </div>
          ) : (
            <p className="panel-hint">No council lifecycle process stored yet.</p>
          )}
          <div className="process-lookup">
            <input
              className="gov-input"
              placeholder="Paste process ID"
              value={lookupValue}
              onChange={(event) => onLookupChange(event.target.value)}
            />
            <button
              type="button"
              className="connect-button secondary"
              onClick={() => onOpen(lookupValue)}
              disabled={!lookupValue.trim()}
            >
              Open
            </button>
          </div>
        </div>
      </div>
    );
  }

  function LatestProcessCard({ processId, processData, signatureList, onOpen, onCopyLink, onClear, lookupValue, onLookupChange }) {
    const payload = parsePayloadJson(processData?.payload_json || processData?.payload || processData?.params);
    const proposer = normalizeWallet(processData?.created_by_wallet);
    const approvalWallets = buildApprovalWallets(processData, signatureList);
    const nonProposerCount = Number.isFinite(processData?.nonProposerCount)
      ? Number(processData?.nonProposerCount)
      : approvalWallets.filter((wallet) => wallet && wallet !== proposer).length;
    const requiredQuorum = Number(processData?.requiredQuorum ?? payload?.quorumM ?? 0);
    return (
      <div className="gov-card">
        <div className="gov-card-header">
          <h3>Latest council lifecycle process</h3>
          <p className="panel-hint">Open the most recent proposal for this tenant.</p>
        </div>
        <div className="gov-card-body">
          {processId ? (
            <>
              <div className="process-summary-row">
                <div>
                  <div className="panel-hint">Process ID</div>
                  <div className="process-id-row">
                    <span title={processId}>Process on record</span>
                  </div>
                </div>
                <StatePill state={processData?.state} />
              </div>
              <div className="panel-row">
                <span className="panel-label">Progress</span>
                <span className="panel-value">{nonProposerCount}/{requiredQuorum || '—'}</span>
              </div>
              <div className="panel-row">
                <span className="panel-label">Proposer</span>
                <span className="panel-value" title={proposer || ''}>
                  {proposer ? 'Proposer wallet on record' : '—'}
                </span>
              </div>
              <div className="wallet-chip-grid">
                {approvalWallets.length === 0 ? (
                  <p className="panel-hint">No approvals recorded yet.</p>
                ) : (
                  approvalWallets.map((wallet) => (
                    <WalletChip key={wallet} value={wallet} />
                  ))
                )}
              </div>
              {processData?.lastError && (
                <div className="banner warn">Last error: {processData.lastError}</div>
              )}
              <div className="process-summary-actions">
                <button type="button" className="connect-button" onClick={() => onOpen(processId)}>
                  Open signing
                </button>
                <button type="button" className="connect-button secondary" onClick={() => onCopyLink(processId)}>
                  Copy signing link
                </button>
                <button type="button" className="connect-button secondary" onClick={onClear}>
                  Clear
                </button>
              </div>
            </>
          ) : (
            <p className="panel-hint">No council lifecycle process stored yet.</p>
          )}
          <div className="process-lookup">
            <input
              className="gov-input"
              placeholder="Paste process ID"
              value={lookupValue}
              onChange={(event) => onLookupChange(event.target.value)}
            />
            <button
              type="button"
              className="connect-button secondary"
              onClick={() => onOpen(lookupValue)}
              disabled={!lookupValue.trim()}
            >
              Open
            </button>
          </div>
        </div>
      </div>
    );
  }

  function navigateTo(nextPath) {
    if (typeof window === 'undefined') return;
    if (!nextPath) return;
    window.history.pushState({}, '', nextPath);
    setCurrentPath(nextPath);
  }

  function renderGovernanceSetup() {
    const setupData = councilSetupStatus?.data;
    const setupMode = setupData?.governance?.mode || setupData?.mode || '—';
    const isBootstrapMode = setupMode === 'BOOTSTRAP';
    const councilData = councilSnapshot?.data;
    const council = councilData?.council || councilData || {};
    const topLevelMembers = Array.isArray(councilData?.members) ? councilData.members : [];
    const nestedMembers = Array.isArray(council?.members) ? council.members : [];
    const snapshotMembers = topLevelMembers.length > 0 ? topLevelMembers : nestedMembers;
    const snapshotQuorumM = council?.quorumM ?? councilData?.quorumM ?? council?.quorum_m ?? councilData?.quorum_m ?? null;
    const snapshotQuorumN = council?.quorumN ?? councilData?.quorumN ?? council?.quorum_n ?? councilData?.quorum_n ?? null;
    const snapshotAnchorFields = [
      councilData?.anchorTx,
      councilData?.executionHash,
      councilData?.intentHash,
      council?.anchorTx,
      council?.executionHash,
      council?.intentHash
    ].filter(Boolean);
    const latestProcessData = latestCouncilProcess?.data;
    const latestPayload = parsePayloadJson(
      latestProcessData?.payload_json || latestProcessData?.payload || latestProcessData?.params
    );
    const latestAction = latestPayload?.action || null;
    const latestNonProposerCount = Number.isFinite(latestProcessData?.nonProposerCount)
      ? Number(latestProcessData?.nonProposerCount)
      : 0;
    const latestRequiredQuorum = Number(latestProcessData?.requiredQuorum ?? latestPayload?.quorumM ?? 0);
    const showSigningBanner = !!(lastCouncilProcessId && latestProcessData?.state === 'GC_SIGNING_OPEN');
    const availableUsers = Array.isArray(tenantUsers) ? tenantUsers : [];
    const userById = new Map(availableUsers.map((user) => [user.id, user]));
    const currentNormalized = normalizeWalletList(snapshotMembers);
    const currentQuorumM = Number(snapshotQuorumM ?? 0);
    const currentQuorumN = Number(snapshotQuorumN ?? currentNormalized.length);
    const selectedUsers = (desiredCouncilMembers || [])
      .map((userId) => userById.get(userId))
      .filter(Boolean);
    const selectedWallets = selectedUsers
      .map((user) => user.walletBinding?.walletAddress || '')
      .filter(Boolean)
      .map((wallet) => normalizeWallet(wallet));
    const desiredNormalized = normalizeWalletList(selectedWallets);
    const parseQuorumValue = (value) => {
      const trimmed = String(value ?? '').trim();
      if (!trimmed) return null;
      if (!/^\d+$/.test(trimmed)) return Number.NaN;
      return Number(trimmed);
    };
    const desiredQuorumMNum = parseQuorumValue(desiredCouncilQuorumM);
    const desiredQuorumNNum = parseQuorumValue(desiredCouncilQuorumN);
    const quorumMissing = desiredQuorumMNum == null || desiredQuorumNNum == null;
    const quorumInvalidNumber = Number.isNaN(desiredQuorumMNum) || Number.isNaN(desiredQuorumNNum);
    const hasCouncil = currentNormalized.length > 0;
    const desiredMemberCount = selectedUsers.length;
    const quorumMismatch = !quorumMissing && !quorumInvalidNumber && desiredQuorumNNum !== desiredMemberCount;
    const missingWalletUsers = selectedUsers.filter((user) => !user.walletBinding?.linked);
    const quorumRangeInvalid = !quorumMissing && !quorumInvalidNumber
      && (desiredQuorumMNum < 1 || desiredQuorumMNum > desiredQuorumNNum);
    const needsUpdate = !hasCouncil
      || !sameWalletSet(currentNormalized, desiredNormalized)
      || (!quorumMissing && !quorumInvalidNumber
        && (desiredQuorumMNum !== currentQuorumM || desiredQuorumNNum !== currentQuorumN));
    const canPropose = desiredMemberCount >= 1
      && !quorumMismatch
      && !quorumRangeInvalid
      && !quorumMissing
      && !quorumInvalidNumber
      && missingWalletUsers.length === 0;
    const signingOpen = latestProcessData?.state === 'GC_SIGNING_OPEN';
    const proposalDisabled = councilSubmitting
      || !needsUpdate
      || !canPropose
      || (!isBootstrapMode && signingOpen)
      || (isBootstrapMode && !hasCouncil);
    const proposeDisabledReason = (() => {
      if (councilSubmitting) return 'Submitting proposal...';
      if (!isBootstrapMode && signingOpen) return 'Proposal pending signatures. Open signing to collect approvals.';
      if (isBootstrapMode && !hasCouncil) return 'Create the initial council proposal first.';
      if (!needsUpdate) return 'Council already matches desired configuration.';
      if (desiredMemberCount < 1) return 'Add at least one council member.';
      if (missingWalletUsers.length > 0) return 'All selected members must have linked wallets.';
      if (quorumMissing) return 'Set quorum values.';
      if (quorumInvalidNumber) return 'Enter numeric quorum values.';
      if (quorumMismatch) return 'Quorum N must equal number of members.';
      if (quorumRangeInvalid) return 'Quorum M must be between 1 and N.';
      return '';
    })();
    const proposalDebugDetail = [
      `disabled=${proposalDisabled}`,
      `canPropose=${canPropose}`,
      `needsUpdate=${needsUpdate}`,
      `signingOpen=${signingOpen}`,
      `members=${desiredMemberCount}`,
      `quorum=${Number.isFinite(desiredQuorumMNum) ? desiredQuorumMNum : '—'}/${Number.isFinite(desiredQuorumNNum) ? desiredQuorumNNum : '—'}`,
      `missingWallets=${missingWalletUsers.length}`
    ].join(' ');
    const bootstrapLifecycleProcess = isBootstrapMode && latestAction === 'GENESIS_CREATE';
    const bootstrapProcessReadyToSign = bootstrapLifecycleProcess && latestProcessData?.state === 'GC_SIGNING_OPEN';
    const bootstrapProcessFinalized = bootstrapLifecycleProcess && latestProcessData?.state === 'GC_FINALIZED';
    const councilSnapshotStatus = councilSnapshot?.status;
    const councilSnapshotErrorText = councilSnapshot?.errorText;
    const councilSnapshotOk = !!councilSnapshotStatus && councilSnapshotStatus >= 200 && councilSnapshotStatus < 300;
    const tenantId = sessionInfo?.tenant?.id || sessionInfo?.tenant?.domain || 'unknown';
    const currentUserWallet = sessionInfo?.walletBinding?.walletAddress || '—';

    if (isActiveCouncilMode(setupData) || isActiveCouncilMode(setupStatus)) {
      return (
        <div className="gov-card">
          <h3>Council is active</h3>
          <p className="panel-hint">Formation history is view-only. Redirecting to the council dashboard…</p>
          <button type="button" className="connect-button" onClick={() => navigateTo('/governance')}>
            Go to council dashboard
          </button>
        </div>
      );
    }

    const handleMembersChange = (next) => {
      setDesiredCouncilTouched(true);
      setDesiredCouncilMembers(next);
      if (!desiredCouncilTouched) {
        setDesiredCouncilQuorumN(String(next.length));
      }
    };

    const handlePropose = async () => {
      setCouncilSubmitError('');
      if (!connectedAddress) {
        showToast('Connect a wallet to propose a council update.', 'warn');
        return;
      }
      if (!isBootstrapMode && signingOpen) {
        showToast('Proposal pending signatures. Open signing to collect approvals.', 'warn');
        return;
      }
      if (!needsUpdate) {
        showToast('Council already matches desired configuration.', 'success');
        return;
      }
      if (!canPropose) {
        showToast('Fix desired council validation before proposing.', 'warn');
        return;
      }
      if (quorumMissing) {
        showToast('Set quorum values before proposing.', 'warn');
        return;
      }
      if (quorumInvalidNumber) {
        showToast('Enter numeric quorum values before proposing.', 'warn');
        return;
      }
      try {
        setCouncilSubmitting(true);
        const actionType = isBootstrapMode ? 'GENESIS_CREATE' : 'UPDATE';
        const payload = {
          params: {
            action: actionType,
            members: desiredNormalized,
            quorumM: Number(desiredQuorumMNum),
            quorumN: Number(desiredQuorumNNum)
          },
          created_by_wallet: normalizeWallet(connectedAddress)
        };
        const response = await apiFetch('/api/v1/processes/council-lifecycle', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorMessage = data?.error || data?.message || 'Failed to propose council update';
          setCouncilSubmitError(errorMessage);
          throw new Error(errorMessage);
        }
        if (data?.updatedExisting || data?.alreadyCompliant) {
          showToast(
            data?.alreadyCompliant
              ? 'Council already matches desired configuration.'
              : 'Bootstrap council updated.',
            'success'
          );
          await councilSetupStatus?.refresh?.();
          await councilSnapshot?.refresh?.();
          return;
        }
        if (data?.deduped || data?.idempotent) {
          showToast('Council already matches desired configuration.', 'success');
        }
        const processId = data?.processId || data?.intent_id || data?.id;
        if (processId) {
          saveLatestProcessId(processId);
          showToast('Proposal created. Collect signatures to reach quorum.', 'success');
          navigateTo(`/governance/processes/${processId}`);
          return;
        }
        showToast(isBootstrapMode ? 'Bootstrap council updated.' : 'Council update proposed.', 'success');
        await councilSetupStatus?.refresh?.();
        await councilSnapshot?.refresh?.();
      } catch (error) {
        showToast(error.message || 'Failed to propose council update', 'error');
      } finally {
        setCouncilSubmitting(false);
      }
    };

    const handleBootstrapFinalize = async () => {
      console.log('[bootstrap finalize] clicked');
      setBootstrapFinalizeStatus('');
      setBootstrapFinalizeError('');
      setBootstrapSignStatus('');
      setBootstrapSignError('');
      if (!connectedAddress) {
        showToast('Connect a wallet to finalize the initial council.', 'warn');
        return;
      }
      if (!canPropose) {
        showToast('Fix desired council validation before finalizing.', 'warn');
        return;
      }
      if (quorumMissing) {
        showToast('Set quorum values before finalizing.', 'warn');
        return;
      }
      if (quorumInvalidNumber) {
        showToast('Enter numeric quorum values before finalizing.', 'warn');
        return;
      }
      try {
        setCouncilSubmitting(true);
        const payload = {
          params: {
            action: 'GENESIS_CREATE',
            members: desiredNormalized,
            quorumM: Number(desiredQuorumMNum),
            quorumN: Number(desiredQuorumNNum)
          },
          created_by_wallet: normalizeWallet(connectedAddress)
        };
        console.log('[bootstrap finalize] request', {
          url: '/api/v1/processes/council-lifecycle',
          payload
        });
        setBootstrapFinalizeStatus('Creating council proposal…');
        const response = await apiFetch('/api/v1/processes/council-lifecycle', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = { raw: text };
        }
        console.log('[bootstrap finalize] response', { status: response.status, body: data });
        if (response.status === 409) {
          setBootstrapFinalizeStatus('Council already exists. Refreshing snapshot…');
          await councilSetupStatus?.refresh?.();
          await councilSnapshot?.refresh?.();
          if (!councilSnapshot?.data || (Array.isArray(councilSnapshot?.data?.members)
            ? councilSnapshot.data.members.length === 0
            : Array.isArray(councilSnapshot?.data?.council?.members)
              ? councilSnapshot.data.council.members.length === 0
              : true)) {
            setBootstrapFinalizeError('Council exists per API, but snapshot returned empty. See Truth Panel.');
          }
          return;
        }
        if (!response.ok) {
          const detail = data?.error || data?.message || text || 'Failed to finalize initial council';
          const errorMessage = `HTTP ${response.status}: ${detail}`;
          setBootstrapFinalizeError(errorMessage);
          setBootstrapFinalizeStatus('');
          throw new Error(errorMessage);
        }
        const processId = data?.processId || data?.intent_id || data?.id;
        const updatedExisting = data?.updatedExisting || data?.alreadyCompliant;
        if (updatedExisting && !processId) {
          setBootstrapFinalizeStatus(
            data?.alreadyCompliant
              ? 'Council already matches desired configuration.'
              : 'Bootstrap council updated.'
          );
          showToast(
            data?.alreadyCompliant
              ? 'Council already matches desired configuration.'
              : 'Bootstrap council updated.',
            'success'
          );
          await councilSetupStatus?.refresh?.();
          await councilSnapshot?.refresh?.();
          return;
        }
        if (processId) {
          saveLatestProcessId(processId);
          setBootstrapFinalizeStatus('Proposal created. Sign to activate council.');
          showToast('Council proposal created. Sign to activate.', 'success');
          await councilSetupStatus?.refresh?.();
          await councilSnapshot?.refresh?.();
          return;
        }
        setBootstrapFinalizeStatus('Bootstrap council updated.');
        showToast('Bootstrap council updated.', 'success');
        await councilSetupStatus?.refresh?.();
        await councilSnapshot?.refresh?.();
      } catch (error) {
        console.error('[bootstrap finalize] error', error);
        showToast(error.message || 'Failed to finalize initial council', 'error');
      } finally {
        setCouncilSubmitting(false);
      }
    };

    const handleBootstrapSign = async () => {
      setBootstrapSignStatus('');
      setBootstrapSignError('');
      if (!connectedAddress) {
        showToast('Connect a wallet to sign and activate the council.', 'warn');
        return;
      }
      if (!walletLinked) {
        showToast('Link your wallet before signing.', 'warn');
        return;
      }
      if (!lastCouncilProcessId) {
        showToast('Create the initial council proposal first.', 'warn');
        return;
      }
      if (!bootstrapProcessReadyToSign) {
        showToast('No bootstrap proposal is awaiting signature.', 'warn');
        return;
      }
      try {
        setBootstrapSigning(true);
        setBootstrapSignStatus('Requesting signature challenge…');
        const challengeResponse = await apiFetch(`/api/v1/processes/${lastCouncilProcessId}/approve/challenge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ approverAddress: connectedAddress })
        });
        const challengeData = await challengeResponse.json().catch(() => ({}));
        if (!challengeResponse.ok) {
          throw new Error(challengeData?.error || 'Failed to create signature challenge');
        }
        const messageToSign = challengeData?.messageToSign || challengeData?.message;
        if (!messageToSign || !challengeData?.nonce) {
          throw new Error('Challenge payload missing message or nonce.');
        }
        setBootstrapSignStatus('Awaiting wallet signature…');
        const signature = await signMessage(messageToSign);
        setBootstrapSignStatus('Submitting signature…');
        const approveResponse = await apiFetch(`/api/v1/processes/${lastCouncilProcessId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet_address: connectedAddress,
            signature,
            nonce: challengeData.nonce
          })
        });
        const approveData = await approveResponse.json().catch(() => ({}));
        if (!approveResponse.ok) {
          throw new Error(approveData?.error || 'Failed to submit signature');
        }
        setBootstrapSignStatus('Signature submitted. Refreshing…');
        showToast('Bootstrap signature submitted.', 'success');
        await latestCouncilProcess?.refresh?.();
        await councilSetupStatus?.refresh?.();
        await councilSnapshot?.refresh?.();
      } catch (error) {
        const message = error.message || 'Signature failed';
        setBootstrapSignError(message);
        showToast(message, 'error');
      } finally {
        setBootstrapSigning(false);
      }
    };

    return (
      <div className="governance-setup">
        <CouncilConfigCard title="Truth panel" subtitle="Temporary debug view of current backend truth.">
          <div className="panel-row">
            <span className="panel-label">Tenant</span>
            <span className="panel-value">{tenantId}</span>
          </div>
          <div className="panel-row">
            <span className="panel-label">Mode</span>
            <span className="panel-value">{setupMode}</span>
          </div>
          <div className="panel-row">
            <span className="panel-label">Current wallet</span>
            <span className="panel-value" title={currentUserWallet !== '—' ? currentUserWallet : ''}>
              {currentUserWallet !== '—' ? 'Wallet on record' : '—'}
            </span>
          </div>
          <div className="panel-row">
            <span className="panel-label">Snapshot status</span>
            <span className="panel-value">
              {councilSnapshotOk
                ? `ok (${snapshotMembers.length} members, ${snapshotQuorumM || '—'}/${snapshotQuorumN || '—'})`
                : `error (${councilSnapshotStatus || '—'})`}
            </span>
          </div>
          <div className="panel-row">
            <span className="panel-label">Proposal debug</span>
            <span className="panel-value">
              {proposalDisabled
                ? `disabled (${proposeDisabledReason || 'unknown'})`
                : 'enabled'}
            </span>
          </div>
          <div className="panel-hint">{proposalDebugDetail}</div>
          {!councilSnapshotOk && councilSnapshotErrorText && (
            <div className="banner warn">Snapshot error (HTTP {councilSnapshotStatus || '—'}): {councilSnapshotErrorText}</div>
          )}
          <div className="panel-row">
            <span className="panel-label">Audit anchor</span>
            <span className="panel-value">
              {snapshotAnchorFields.length > 0 ? 'Present' : 'Audit anchor not available in snapshot response.'}
            </span>
          </div>
          {snapshotAnchorFields.length > 0 && (
            <div className="panel-hint">{snapshotAnchorFields.slice(0, 3).join(' • ')}</div>
          )}
          {isBootstrapMode && hasCouncil && (
            <div className="banner warn">
              Council is configured, but mode still BOOTSTRAP. Backend setup-status may be stale or gated.
            </div>
          )}
        </CouncilConfigCard>
        <CouncilConfigCard
          title="Setup status"
          subtitle={setupMode === 'BOOTSTRAP'
            ? 'Initial governance is managed by the bootstrap admin.'
            : 'Council governed mode'}
        >
          {councilSetupStatus?.loading ? (
            <p>Loading setup status…</p>
          ) : (
            <div className="setup-status">
              <div className="panel-row">
                <span className="panel-label">Mode</span>
                <span className="panel-value">{setupMode}</span>
              </div>
              {setupMode === 'BOOTSTRAP' && (
                <div className="banner warn">Initial governance is managed by the bootstrap admin.</div>
              )}
            </div>
          )}
          {councilSetupStatus?.error && (
            <p className="panel-hint warn">{councilSetupStatus.error}</p>
          )}
        </CouncilConfigCard>

        <CouncilConfigCard title="Governance council setup" subtitle="Compare current council state to desired configuration.">
          {councilSnapshot?.loading ? (
            <p>Loading council snapshot…</p>
          ) : (
            <>
              {showSigningBanner && !isBootstrapMode && (
                <div className="banner warn">
                  Pending signatures: {latestNonProposerCount}/{latestRequiredQuorum || '—'}.
                  <button
                    type="button"
                    className="connect-button"
                    onClick={() => navigateTo(`/governance/processes/${lastCouncilProcessId}`)}
                  >
                    Open signing
                  </button>
                </div>
              )}
              {isBootstrapMode && (
                <div className="banner warn">
                  <strong>Initial Governance Council is being established by the bootstrap admin.</strong>
                  <div className="panel-hint">Bootstrap admin can apply council changes without signatures.</div>
                  <div className="panel-hint">Requested quorum: {Number.isFinite(desiredQuorumMNum) ? desiredQuorumMNum : '—'}-of-{Number.isFinite(desiredQuorumNNum) ? desiredQuorumNNum : (desiredMemberCount || '—')}</div>
                  <div className="process-summary-actions">
                    {!hasCouncil && (
                      <button
                        type="button"
                        className="connect-button"
                        onClick={handleBootstrapFinalize}
                        disabled={councilSubmitting || !canPropose}
                      >
                        {councilSubmitting ? 'Submitting…' : 'Create initial council proposal'}
                      </button>
                    )}
                    {bootstrapProcessReadyToSign && (
                      <button
                        type="button"
                        className="connect-button secondary"
                        onClick={handleBootstrapSign}
                        disabled={bootstrapSigning}
                      >
                        {bootstrapSigning ? 'Awaiting signature…' : 'Sign & activate council'}
                      </button>
                    )}
                  </div>
                  {bootstrapFinalizeStatus && (
                    <p className="panel-hint">{bootstrapFinalizeStatus}</p>
                  )}
                  {bootstrapFinalizeError && (
                    <div className="banner warn">{bootstrapFinalizeError}</div>
                  )}
                  {bootstrapSignStatus && (
                    <p className="panel-hint">{bootstrapSignStatus}</p>
                  )}
                  {bootstrapSignError && (
                    <div className="banner warn">{bootstrapSignError}</div>
                  )}
                  {bootstrapProcessFinalized && (
                    <div className="banner success">Bootstrap council finalized. Redirecting to dashboard…</div>
                  )}
                </div>
              )}
              {signingOpen && !isBootstrapMode && (
                <div className="banner warn">Proposal pending signatures. Open signing to collect approvals.</div>
              )}
              <div className="council-current">
                <div className="panel-hint">Current council</div>
                <div className="wallet-chip-grid">
                  {currentNormalized.length === 0 ? (
                    <p className="panel-hint">No members recorded.</p>
                  ) : (
                    currentNormalized.map((wallet) => (
                      <WalletChip key={wallet} value={wallet} />
                    ))
                  )}
                </div>
                <div className="panel-row">
                  <span className="panel-label">Quorum</span>
                  <span className="panel-value">{currentQuorumM || '—'}-of-{currentQuorumN || '—'}</span>
                </div>
                <div className="panel-row">
                  <span className="panel-label">Mode</span>
                  <span className="panel-value">{council?.mode || '—'}</span>
                </div>
              </div>

              <DesiredCouncilEditor
                users={availableUsers}
                selectedUserIds={desiredCouncilMembers}
                onSelectionChange={handleMembersChange}
                quorumM={desiredCouncilQuorumM}
                quorumN={desiredCouncilQuorumN}
                onQuorumMChange={(value) => {
                  setDesiredCouncilTouched(true);
                  setDesiredCouncilQuorumM(value);
                }}
                onQuorumNChange={(value) => {
                  setDesiredCouncilTouched(true);
                  setDesiredCouncilQuorumN(value);
                }}
                missingWalletUsers={missingWalletUsers}
                quorumMismatch={quorumMismatch}
              />
              {desiredMemberCount === 0 && (
                <p className="panel-hint warn">Add at least one council wallet.</p>
              )}
              {quorumInvalidNumber && (
                <p className="panel-hint warn">Enter numeric quorum values.</p>
              )}
              {quorumRangeInvalid && (
                <p className="panel-hint warn">Quorum M must be between 1 and N.</p>
              )}
              <div className="council-actions">
                {!hasCouncil && (
                  <span className="status-badge running">NO COUNCIL CONFIGURED</span>
                )}
                {hasCouncil && !needsUpdate && (
                  <span className="status-badge success">Already compliant</span>
                )}
                <button
                  type="button"
                  className="connect-button"
                  onClick={handlePropose}
                  disabled={proposalDisabled}
                >
                  {councilSubmitting ? 'Submitting…' : 'Propose council update'}
                </button>
                {proposalDisabled && proposeDisabledReason && (
                  <p className="panel-hint warn">{proposeDisabledReason}</p>
                )}
                {councilSubmitError && (
                  <div className="banner warn">{councilSubmitError}</div>
                )}
              </div>
            </>
          )}
          {councilSnapshot?.error && (
            <p className="panel-hint warn">{councilSnapshot.error}</p>
          )}
        </CouncilConfigCard>

        {!isBootstrapMode && (
          <LatestProcessCard
            processId={lastCouncilProcessId}
            processData={latestProcessData}
            signatureList={latestCouncilProcess?.signatures}
            lookupValue={processLookupInput}
            onLookupChange={setProcessLookupInput}
            onOpen={(processId) => {
              if (!processId) return;
              saveLatestProcessId(processId);
              navigateTo(`/governance/processes/${processId}`);
            }}
            onCopyLink={(processId) => {
              if (!processId || typeof window === 'undefined') return;
              copyToClipboard(`${window.location.origin}/governance/processes/${processId}`);
              showToast('Signing link copied.', 'success');
            }}
            onClear={() => {
              setLastCouncilProcessId('');
              safeRemoveLocalStorage(councilStorageKey);
            }}
          />
        )}

        <CouncilConfigCard title="Handoff" subtitle="After council decisions are finalized, approvers continue in the Approvers Dashboard.">
          <a className="connect-button secondary" href="https://console-staging.attest-r.mergematter.io">
            Open Approvers Dashboard
          </a>
        </CouncilConfigCard>
      </div>
    );
  }

  function renderGovernanceProcess() {
    const processData = councilProcess?.data;
    const payload = parsePayloadJson(processData?.payload_json || processData?.payload || processData?.params);
    const members = Array.isArray(payload?.members) ? payload.members : [];
    const approvalWallets = buildApprovalWallets(processData, councilProcess?.signatures);
    const proposer = normalizeWallet(processData?.created_by_wallet);
    const approvalWalletSet = new Set(approvalWallets);
    const connected = normalizeWallet(connectedAddress);
    const governanceMode = setupStatus?.governance?.mode || setupStatus?.mode || '—';
    const isBootstrapMode = governanceMode === 'BOOTSTRAP';
    const eligibleMembers = normalizeWalletList(members);
    const isEligible = connected && (eligibleMembers.includes(connected) || (isBootstrapMode && connected === proposer));
    const hasApproved = connected && approvalWalletSet.has(connected);
    const tenantUserByWallet = new Map(
      (Array.isArray(tenantUsers) ? tenantUsers : [])
        .map((user) => {
          const wallet = normalizeWallet(user?.walletBinding?.walletAddress);
          return wallet ? [wallet, user] : null;
        })
        .filter(Boolean)
    );
    const nonProposerCount = Number.isFinite(processData?.nonProposerCount)
      ? Number(processData?.nonProposerCount)
      : approvalWallets.filter((wallet) => wallet && wallet !== proposer).length;
    const requiredQuorum = Number(processData?.requiredQuorum ?? payload?.quorumM ?? 0);
    const proposerBlocked = !isBootstrapMode && connected && proposer && connected === proposer;
    const canApprove = processData?.state === 'GC_SIGNING_OPEN'
      && isEligible
      && connected
      && walletLinked
      && !proposerBlocked
      && !hasApproved;
    const signDisabledReason = processData?.state !== 'GC_SIGNING_OPEN'
      ? 'Signing is closed.'
      : !connected
        ? 'Connect a wallet to sign.'
        : !walletLinked
          ? 'Wallet not linked. Link your wallet to sign.'
          : !isEligible
            ? 'Connected wallet is not an eligible approver.'
            : proposerBlocked
              ? 'Proposer wallets cannot approve.'
              : hasApproved
                ? 'You already signed this proposal.'
                : 'Signing not available.';

    const handleApprove = async () => {
      setSignProposalError('');
      if (!connectedAddress) {
        showToast('Connect a wallet to approve.', 'warn');
        return;
      }
      try {
        setApprovingCouncilProcess(true);
        const challengeResponse = await apiFetch(`/api/v1/processes/${governanceProcessId}/approve/challenge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ approverAddress: connectedAddress })
        });
        const challengeData = await challengeResponse.json().catch(() => ({}));
        if (!challengeResponse.ok) {
          throw new Error(challengeData?.error || 'Failed to create approval challenge');
        }
        const messageToSign = challengeData?.messageToSign || challengeData?.message;
        if (!messageToSign || !challengeData?.nonce) {
          throw new Error('Challenge payload missing message or nonce.');
        }
        const signature = await signMessage(messageToSign);
        const approveResponse = await apiFetch(`/api/v1/processes/${governanceProcessId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet_address: connectedAddress,
            signature,
            nonce: challengeData.nonce
          })
        });
        const approveData = await approveResponse.json().catch(() => ({}));
        if (!approveResponse.ok) {
          throw new Error(approveData?.error || 'Failed to submit approval');
        }
        showToast('Approval submitted.', 'success');
        await councilProcess.refresh();
      } catch (error) {
        setSignProposalError(error.message || 'Approval failed');
        showToast(error.message || 'Approval failed', 'error');
      } finally {
        setApprovingCouncilProcess(false);
      }
    };

    if (councilProcess?.loading) {
      return <p>Loading process…</p>;
    }
    if (councilProcess?.error) {
      return <p className="panel-hint warn">{councilProcess.error}</p>;
    }
    if (!processData) {
      return <p className="panel-hint">Process not found.</p>;
    }

    return (
      <div className="governance-process">
        <CouncilConfigCard title="Sign proposal">
          <div className="process-header">
            <div>
              <div className="panel-hint">Process ID</div>
              <div className="process-id-row">
                <span title={governanceProcessId}>Process on record</span>
              </div>
            </div>
            <StatePill state={processData?.state} />
          </div>
          {processData?.lastError && (
            <div className="banner warn">Last error: {processData.lastError}</div>
          )}
          {signProposalError && (
            <div className="banner warn">{signProposalError}</div>
          )}
          <div className="process-metrics">
            <div>
              <div className="panel-hint">Approval progress</div>
              <ProgressBar value={nonProposerCount} max={requiredQuorum} />
              <div className="panel-hint">{nonProposerCount} / {requiredQuorum || '—'} approvals</div>
            </div>
            <div className="panel-row">
              <span className="panel-label">Action</span>
              <span className="panel-value">{payload?.action ? payload.action.toLowerCase().replace(/_/g, ' ') : '—'}</span>
            </div>
            <div className="panel-row">
              <span className="panel-label">Quorum</span>
              <span className="panel-value">{payload?.quorumM || requiredQuorum || '—'}-of-{payload?.quorumN || members.length || '—'}</span>
            </div>
            <div className="panel-row">
              <span className="panel-label">Proposer</span>
              <span className="panel-value" title={proposer || ''}>
                {proposer ? 'Proposer wallet on record' : '—'}
              </span>
            </div>
          </div>
          <div className="panel-section">
            <div className="panel-hint">Approvals</div>
          </div>
          <div className="wallet-chip-grid">
            {approvalWallets.length === 0 ? (
              <p className="panel-hint">No approvals recorded yet.</p>
            ) : (
              approvalWallets.map((wallet) => (
                <WalletChip key={wallet} value={wallet} />
              ))
            )}
          </div>
          {canApprove && (
            <button
              type="button"
              className="connect-button"
              onClick={handleApprove}
              disabled={approvingCouncilProcess}
            >
              {approvingCouncilProcess ? 'Awaiting signature…' : 'Sign proposal (wallet)'}
            </button>
          )}
          {!canApprove && (
            <p className="panel-hint">{signDisabledReason}</p>
          )}
        </CouncilConfigCard>

        <CouncilConfigCard title="Eligible members">
          <div className="member-list">
            {eligibleMembers.length === 0 ? (
              <p className="panel-hint">No eligible members listed.</p>
            ) : (
              eligibleMembers.map((wallet) => {
                const label = wallet === proposer ? 'Proposer' : approvalWalletSet.has(wallet) ? 'Signed' : 'Pending';
                const user = tenantUserByWallet.get(wallet);
                const displayName = user?.displayName || user?.name || user?.email || '';
                return (
                  <div key={wallet} className="member-row">
                    <WalletChip value={wallet} />
                    {displayName && <span className="panel-hint">{displayName}</span>}
                    <span className={`member-status ${label.toLowerCase()}`}>{label}</span>
                  </div>
                );
              })
            )}
          </div>
        </CouncilConfigCard>

        <CouncilConfigCard title="Timeline">
          <div className="timeline-row">
            <span className="panel-label">
              Proposal created by {tenantUserByWallet.get(proposer)?.email || 'Council member'}
            </span>
            <span className="panel-value">{processData?.createdAt ? new Date(processData.createdAt).toLocaleString() : '—'}</span>
          </div>
          <div className="timeline-row">
            <span className="panel-label">Finalize started by system</span>
            <span className="panel-value">{processData?.finalizeStartedAt ? new Date(processData.finalizeStartedAt).toLocaleString() : '—'}</span>
          </div>
          <div className="timeline-row">
            <span className="panel-label">Finalize completed by system</span>
            <span className="panel-value">{processData?.finalizeCompletedAt ? new Date(processData.finalizeCompletedAt).toLocaleString() : '—'}</span>
          </div>
        </CouncilConfigCard>
      </div>
    );
  }

  const deriveApprovalSummary = (requiredRoles = [], approvals = [], authorized) => {
    const approvalsRequired = requiredRoles.length || 1;
    if (authorized && approvals.length === 0) {
      return {
        approvalsRequired,
        approvalsCollected: approvalsRequired,
        approvalsSatisfied: true
      };
    }
    const queues = new Map();
    approvals.forEach((approval) => {
      if (!approval.role) return;
      if (!queues.has(approval.role)) {
        queues.set(approval.role, []);
      }
      queues.get(approval.role).push(approval);
    });
    let approvalsCollected = 0;
    requiredRoles.forEach((role) => {
      const queue = queues.get(role) || [];
      const approval = queue.shift() || null;
      queues.set(role, queue);
      if (approval) approvalsCollected += 1;
    });
    const approvalsSatisfied = approvalsCollected >= approvalsRequired && approvalsRequired > 0;
    return { approvalsRequired, approvalsCollected, approvalsSatisfied };
  };

  const deriveApprovalSummaryFromRoles = (requiredRoles = [], approvals = [], approvalsSatisfiedOverride) => {
    const approvalsRequired = requiredRoles.length || 1;
    const queues = new Map();
    approvals.forEach((approval) => {
      if (!approval.role) return;
      if (!queues.has(approval.role)) {
        queues.set(approval.role, []);
      }
      queues.get(approval.role).push(approval);
    });
    let approvalsCollected = 0;
    requiredRoles.forEach((role) => {
      const queue = queues.get(role) || [];
      const approval = queue.shift() || null;
      queues.set(role, queue);
      if (approval) approvalsCollected += 1;
    });
    const approvalsSatisfied = approvalsSatisfiedOverride ?? (approvalsCollected >= approvalsRequired && approvalsRequired > 0);
    return { approvalsRequired, approvalsCollected, approvalsSatisfied };
  };

  const deriveOverallStatusLocal = ({ executionState, approvalsSatisfied, expiry }) => {
    if (executionState === 'SUCCEEDED') return 'EXECUTED_SUCCESS';
    if (executionState === 'FAILED') return 'EXECUTED_FAILED';
    const now = Date.now() / 1000;
    if (expiry && Number(expiry) > 0 && now > Number(expiry)) return 'EXPIRED';
    if (approvalsSatisfied) return 'APPROVED_PENDING_EXECUTION';
    return 'PENDING_APPROVAL';
  };

  const getStoredIdentityToken = () => {
    const token = safeGetSessionStorage('attest_r_identity_token');
    const expiresAt = Number(safeGetSessionStorage('attest_r_identity_exp') || 0);
    // Treat literal string "null" as absent (some redirects may include it)
    if (!token || token === 'null' || !expiresAt || Date.now() / 1000 > expiresAt) {
      safeRemoveSessionStorage('attest_r_identity_token');
      safeRemoveSessionStorage('attest_r_identity_exp');
      return null;
    }
    return token;
  };

  const storeIdentityToken = (token, expiresAt) => {
    if (!token || token === 'null') {
      safeRemoveSessionStorage('attest_r_identity_token');
      safeRemoveSessionStorage('attest_r_identity_exp');
      setIdentityToken(null);
      return;
    }
    safeSetSessionStorage('attest_r_identity_token', token);
    safeSetSessionStorage('attest_r_identity_exp', String(expiresAt));
    setIdentityToken(token);
  };

  const clearIdentityToken = () => {
    safeRemoveSessionStorage('attest_r_identity_token');
    safeRemoveSessionStorage('attest_r_identity_exp');
    setIdentityToken(null);
    setIdentityProfile(null);
    setSessionInfo(null);
    setAuthorities([]);
    setIdentityStatus('');
    setActiveConsole('operations');
    setGovernanceActions([]);
    setGovernanceMembers([]);
    setGovernanceAuthorities([]);
    setGovernancePolicyPacks([]);
    setGovernanceBindings([]);
    setGovernanceAuditLogs([]);
    resetWalletConnection('sign out');
  };

  const refreshAccessToken = async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch(apiUrl('/api/v1/auth/refresh'), {
          method: 'POST',
          credentials: 'include'
        });
        if (!response.ok) {
          clearIdentityToken();
          return null;
        }
        const data = await response.json();
        if (data?.access_token) {
          storeIdentityToken(data.access_token, data.expires_at || (Date.now() / 1000 + 15 * 60));
          return data.access_token;
        }
        clearIdentityToken();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();
    return refreshPromiseRef.current;
  };

  const apiFetch = async (path, options = {}, allowRetry = true) => {
    const token = getIdentityAuthToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(apiUrl(path), {
      credentials: 'include',
      ...options,
      headers
    });
    if (response.status === 401 && allowRetry) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return apiFetch(path, options, false);
      }
    }
    return response;
  };

  function useCouncilSetupStatus({ enabled }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fetchStatus = async () => {
      if (!enabled) return;
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch('/api/v1/governance/setup-status');
        if (!response.ok) {
          setError('Failed to load setup status');
          setLoading(false);
          return;
        }
        const json = await response.json();
        setData(json);
      } catch (err) {
        setError(err?.message || 'Failed to load setup status');
      } finally {
        setLoading(false);
      }
    };
    useEffect(() => {
      fetchStatus();
    }, [enabled, identityToken]);
    return { data, loading, error, refresh: fetchStatus };
  }

  function useCouncilSnapshot({ enabled }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState(null);
    const [errorText, setErrorText] = useState('');
    const fetchSnapshot = async () => {
      if (!enabled) return;
      setLoading(true);
      setError('');
      setStatus(null);
      setErrorText('');
      try {
        const response = await apiFetch('/api/v1/governance/council');
        setStatus(response.status);
        const text = await response.text();
        let json = null;
        try {
          json = text ? JSON.parse(text) : null;
        } catch {
          json = null;
        }
        if (!response.ok) {
          setError('Failed to load council snapshot');
          setErrorText(text || 'No response body');
          setLoading(false);
          return;
        }
        setData(json);
      } catch (err) {
        setError(err?.message || 'Failed to load council snapshot');
      } finally {
        setLoading(false);
      }
    };
    useEffect(() => {
      fetchSnapshot();
    }, [enabled, identityToken]);
    return { data, loading, error, status, errorText, refresh: fetchSnapshot };
  }

  function useProcess(processId, { enabled, poll }) {
    const [data, setData] = useState(null);
    const [signatures, setSignatures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [approvers, setApprovers] = useState(null);
    const [approversSupported, setApproversSupported] = useState(true);
    const fetchProcess = async () => {
      if (!enabled || !processId) return;
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch(`/api/v1/processes/${processId}`);
        if (!response.ok) {
          const text = await response.text();
          setError(text || 'Failed to load process');
          setLoading(false);
          return;
        }
        const json = await response.json();
        setData(json?.process || null);
        if (Array.isArray(json?.signatures)) {
          setSignatures(json.signatures);
        } else if (Array.isArray(json?.process?.signatures)) {
          setSignatures(json.process.signatures);
        } else {
          setSignatures([]);
        }
      } catch (err) {
        setError(err?.message || 'Failed to load process');
      } finally {
        setLoading(false);
      }
    };
    useEffect(() => {
      fetchProcess();
    }, [enabled, processId, identityToken]);
    useEffect(() => {
      if (!enabled || !processId) return;
      if (!poll) return;
      if (!data || data.state !== 'GC_SIGNING_OPEN' || data.lastError) return;
      const interval = setInterval(() => {
        fetchProcess();
      }, 2500);
      return () => clearInterval(interval);
    }, [enabled, poll, data?.state, data?.lastError, processId]);
    useEffect(() => {
      if (!enabled || !processId) return;
      if (!approversSupported) return;
      const loadApprovers = async () => {
        try {
          const response = await apiFetch(`/api/v1/processes/${processId}/approvers`);
          if (response.status === 404) {
            setApproversSupported(false);
            return;
          }
          if (!response.ok) return;
          const json = await response.json();
          setApprovers(json);
        } catch {
          // Ignore optional endpoint errors.
        }
      };
      loadApprovers();
    }, [enabled, processId, approversSupported, identityToken]);
    return {
      data,
      signatures,
      loading,
      error,
      refresh: fetchProcess,
      approvers,
      approversSupported
    };
  }

  function useWallet() {
    return {
      connectedAddress: account,
      signMessage: async (message) => {
        if (!signer) throw new Error('Wallet signer not ready');
        return signer.signMessage(message);
      }
    };
  }

  const fetchIdentityProfile = async (token) => {
    if (!token) return null;
    const response = await apiFetch('/api/v1/me', {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status === 401) {
      clearIdentityToken();
      setIdentityStatus('Session expired. Please sign in again.');
      return null;
    }
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  };

  const startOidcLogin = () => {
    if (oidcEnabled === false) {
      setIdentityStatus('SSO is not configured for this environment.');
      return;
    }
    if (typeof window === 'undefined') return;
    const redirect = `${window.location.origin}${window.location.pathname}`;
    const url = apiUrl(`/api/v1/oidc/login?redirect=${encodeURIComponent(redirect)}`);
    window.location.href = url;
  };

  const getIdentityAuthToken = () => identityToken || getStoredIdentityToken();

  const linkWalletToIdentity = async (overrides = {}) => {
    const token = getIdentityAuthToken();
    const activeSigner = overrides.signer || signer;
    const activeAccount = overrides.account || account;
    if (!token || !activeSigner || !activeAccount) {
      setIdentityStatus('Connect a wallet and sign in before linking.');
      return;
    }
    try {
      setIdentityLoading(true);
      setIdentityStatus('Preparing wallet link challenge...');
      const challengeResponse = await apiFetch('/api/v1/wallet/link/challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ wallet_address: activeAccount })
      });
      const challengeData = await challengeResponse.json();
      if (challengeResponse.status === 401) {
        clearIdentityToken();
        setIdentityStatus('Session expired. Please sign in again.');
        return;
      }
      if (!challengeResponse.ok) {
        throw new Error(challengeData.error || 'Failed to create challenge');
      }
      const signature = await activeSigner.signMessage(challengeData.message);
      const verifyResponse = await apiFetch('/api/v1/wallet/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          wallet_address: activeAccount,
          signature,
          nonce: challengeData.nonce
        })
      });
      const verifyData = await verifyResponse.json();
      if (verifyResponse.status === 401) {
        clearIdentityToken();
        setIdentityStatus('Session expired. Please sign in again.');
        return;
      }
      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Failed to link wallet');
      }
      const refreshed = await fetchIdentityProfile(token);
      if (refreshed) {
        setSessionInfo(refreshed);
        setIdentityProfile(refreshed.identity || null);
      }
      setIdentityStatus('Wallet linked to identity.');
    } catch (error) {
      setIdentityStatus(error.message || 'Failed to link wallet.');
    } finally {
      setIdentityLoading(false);
    }
  };

  const signOut = async () => {
    const token = getIdentityAuthToken();
    if (token) {
      try {
        await apiFetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        // Ignore logout errors; client will clear session anyway.
      }
    }
    clearIdentityToken();
  };

  const walletConnectProjectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '';

  useEffect(() => {
    if (account || signer || provider) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    const tryInjectedReconnect = async () => {
      if (!window.ethereum) return false;
      try {
        const browserProvider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await browserProvider.send('eth_accounts', []);
        if (!accounts || accounts.length === 0) return false;
        if (cancelled) return true;
        externalProviderRef.current = window.ethereum;
        const signer = await browserProvider.getSigner();
        setProvider(browserProvider);
        setSigner(signer);
        setAccount(accounts[0]);
        setConnectionType('MetaMask');
        setStatus('Reconnected to wallet.');
        scheduleStatusClear();
        if (getIdentityAuthToken()) {
          await linkWalletToIdentity({ signer, account: accounts[0] });
        }
        await loadUserAuthorities(accounts[0], browserProvider);
        return true;
      } catch {
        return false;
      }
    };
    const tryWalletConnectReconnect = async () => {
      if (!walletConnectProjectId) return false;
      if (!safeGetLocalStorage('walletconnect')) return false;
      try {
        const provider = await EthereumProvider.init({
          projectId: walletConnectProjectId,
          chains: [1337],
          rpcMap: { 1337: defaultRpcUrl },
          showQrModal: false,
          methods: [
            'eth_requestAccounts',
            'eth_sendTransaction',
            'eth_sign',
            'personal_sign',
            'eth_signTypedData',
            'eth_signTypedData_v4',
            'wallet_switchEthereumChain',
            'wallet_addEthereumChain'
          ],
          events: ['chainChanged', 'accountsChanged']
        });
        if (cancelled) return true;
        walletConnectProviderRef.current = provider;
        externalProviderRef.current = provider;
        await finalizeWalletConnectSession(provider);
        return true;
      } catch {
        return false;
      }
    };
    const run = async () => {
      const injected = await tryInjectedReconnect();
      if (injected || cancelled) return;
      await tryWalletConnectReconnect();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [account, signer, provider, walletConnectProjectId]);

  const ensureCorrectNetwork = async (externalProvider) => {
    if (!externalProvider?.request) return;
    try {
      await externalProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x539' }]
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await externalProvider.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0x539',
            chainName: 'ATTEST-R Blockchain',
            nativeCurrency: {
              name: 'ETH',
              symbol: 'ETH',
              decimals: 18
            },
            rpcUrls: [defaultRpcUrl]
          }]
        });
      } else {
        throw switchError;
      }
    }
  };

  const parseChainIdValue = (chainId) => {
    if (typeof chainId === 'string') {
      if (chainId.startsWith('0x')) return parseInt(chainId, 16);
      if (chainId.includes(':')) {
        const parts = chainId.split(':');
        return Number(parts[1]);
      }
      return Number(chainId);
    }
    if (typeof chainId === 'bigint') return Number(chainId);
    return Number(chainId);
  };

  const finalizeWalletConnectSession = async (provider) => {
    const browserProvider = new ethers.BrowserProvider(provider);
    const network = await browserProvider.getNetwork();
    pushDebugEvent(`WalletConnect: network ${network.chainId}`);

    if (network.chainId !== 1337n) {
      const chainIdNumber = parseChainIdValue(network.chainId);
      setWalletNetworkMismatch(true);
      setWalletNetworkId(chainIdNumber);
      setProvider(browserProvider);
      setSigner(null);
      setConnectionType('WalletConnect');
      setStatus(`Connected to chain ${chainIdNumber}. Switch to ATTEST-R network (1337) to proceed.`);
      pushDebugEvent('WalletConnect: wrong network');
      return false;
    }

    const signer = await browserProvider.getSigner();
    pushDebugEvent('WalletConnect: signer ready');
    const address = await signer.getAddress();
    pushDebugEvent(`WalletConnect: signer address ${address}`);

    setProvider(browserProvider);
    setSigner(signer);
    setAccount(address);
    setConnectionType('WalletConnect');
    setWalletNetworkMismatch(false);
    setWalletNetworkId(null);
    setStatus('Connected via WalletConnect.');
    scheduleStatusClear();
    pushDebugEvent('WalletConnect: connected');

    if (getIdentityAuthToken()) {
      await linkWalletToIdentity({ signer, account: address });
    }

    await loadUserAuthorities(address, browserProvider);
    return true;
  };

  const setStatus = (message) => {
    setConnectStatus(message);
    if (message) {
      setLastConnectStatus(message);
    }
  };

  const scheduleStatusClear = () => {
    setTimeout(() => {
      setConnectStatus('');
    }, 6000);
  };

  const openMetaMaskDeepLink = () => {
    if (typeof window === 'undefined') return;
    const target = encodeURIComponent(window.location.href);
    window.location.href = `https://metamask.app.link/dapp/${target}`;
  };

  const pushDebugEvent = (message) => {
    if (!message) return;
    const entry = { time: new Date().toLocaleTimeString(), message };
    setDebugEvents((prev) => {
      const next = [entry, ...prev].slice(0, 8);
      try {
        safeSetSessionStorage('attestDebugEvents', JSON.stringify(next));
      } catch {
        // Ignore storage failures.
      }
      return next;
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const onError = (event) => {
      pushDebugEvent(`Error: ${event.message || 'Unknown error'}`);
    };
    const onRejection = (event) => {
      const reason = event.reason?.message || String(event.reason || 'Unknown rejection');
      pushDebugEvent(`Promise rejection: ${reason}`);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
      const response = await apiFetch('/api/v1/oidc/config');
        const data = await response.json();
        if (data.enabled) {
          setOidcEnabled(true);
          setOidcProvider(data.provider || 'google');
          setOidcStatusNote('');
        } else {
          setOidcEnabled(false);
          setOidcStatusNote('OIDC is not configured.');
        }
      } catch {
        setOidcEnabled(null);
        setOidcStatusNote('OIDC status unavailable. Try signing in anyway.');
      }
    };
    run();
  }, [apiBase]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    const params = new URLSearchParams(window.location.search);
    const oidcToken = params.get('oidc_token');
    const oidcExp = Number(params.get('oidc_expires_at') || 0);
    if (oidcToken) {
      storeIdentityToken(oidcToken, oidcExp || (Date.now() / 1000 + 15 * 60));
      params.delete('oidc_token');
      params.delete('oidc_expires_at');
      const next = params.toString();
      const nextUrl = `${window.location.pathname}${next ? `?${next}` : ''}`;
      window.history.replaceState({}, document.title, nextUrl);
      setCurrentPath(window.location.pathname);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
    const cached = getStoredIdentityToken();
    if (cached) {
      setIdentityToken(cached);
    } else {
      refreshAccessToken();
    }
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const cached = identityToken || getStoredIdentityToken();
    if (!cached) return;
    let cancelled = false;
    const run = async () => {
      setIdentityLoading(true);
      const profile = await fetchIdentityProfile(cached);
      if (cancelled) return;
      if (!profile) {
        clearIdentityToken();
        setIdentityStatus('Failed to load identity profile.');
        setIdentityLoading(false);
        return;
      }
      setSessionInfo(profile);
      setIdentityProfile(profile.identity || null);
      setIdentityStatus('');
      setIdentityLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [identityToken]);

  useEffect(() => {
    if (activeConsole !== 'governance' || !isLoggedIn) return;
    loadGovernanceData();
  }, [activeConsole, isLoggedIn, identityToken]);

  useEffect(() => {
    if (activeConsole !== 'operations' || !isLoggedIn) return;
    loadGovernanceData({ silent: true });
  }, [activeConsole, isLoggedIn, identityToken]);

  useEffect(() => {
    if (activeConsole !== 'operations' || !isLoggedIn) return;
    loadGovernanceData({ silent: true });
  }, [activeConsole, isLoggedIn, identityToken]);

  useEffect(() => {
    if (!showTestHarness) return;
    loadTestHarnessStatus();
  }, [showTestHarness, identityToken]);

  useEffect(() => {
    if (governanceOnly && activeConsole !== 'governance') {
      setActiveConsole('governance');
    }
    if (opsOnly && activeConsole !== 'operations') {
      setActiveConsole('operations');
    }
  }, [governanceOnly, opsOnly, activeConsole]);

  useEffect(() => {
    if (!governanceOnly) return;
    if (showGovernanceTest || showGovernanceSetup) return;
    if (typeof window === 'undefined') return;
    const token = getIdentityAuthToken();
    if (!token) return;
    const run = async () => {
      try {
        const response = await apiFetch('/api/v1/governance/setup-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        setSetupStatus(data);
        if (!isActiveCouncilMode(data) && typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/governance/setup');
          setCurrentPath('/governance/setup');
        }
      } catch {
        // Ignore routing fetch failures.
      }
    };
    run();
  }, [governanceOnly, showGovernanceTest, showGovernanceSetup, identityToken]);

  useEffect(() => {
    if (!governanceOnly) return;
    if (!showGovernanceSetup) return;
    if (typeof window === 'undefined') return;
    const token = getIdentityAuthToken();
    if (!token) return;
    const run = async () => {
      try {
        const response = await apiFetch('/api/v1/governance/setup-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        setSetupStatus(data);
        if (isActiveCouncilMode(data) && typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/governance');
          setCurrentPath('/governance');
        }
      } catch {
        // Ignore routing fetch failures.
      }
    };
    run();
  }, [governanceOnly, showGovernanceSetup, identityToken]);

  const connectInjected = async () => {
    if (typeof window === 'undefined' || !window.ethereum) {
      showToast('No injected wallet found. Please install MetaMask or use WalletConnect.', 'error');
      return;
    }
    try {
      setConnectingType('injected');
      setLoading(true);
      setStatus('Waiting for MetaMask approval...');
      pushDebugEvent('Injected: awaiting wallet approval');
      const externalProvider = window.ethereum;
      externalProviderRef.current = externalProvider;

      await ensureCorrectNetwork(externalProvider);

      const browserProvider = new ethers.BrowserProvider(externalProvider);

      if (externalProvider?.on) {
        externalProvider.on('accountsChanged', (accounts) => {
          if (!accounts || accounts.length === 0) {
            setAccount(null);
            setAuthorities([]);
            setIdentity('');
            return;
          }
          setAccount(accounts[0]);
          loadUserAuthorities(accounts[0], browserProvider);
        });
        externalProvider.on('chainChanged', () => {
          window.location.reload();
        });
        externalProvider.on('disconnect', () => {
          resetWalletConnection();
        });
      }
      const signer = await browserProvider.getSigner();
      const address = await signer.getAddress();
      const network = await browserProvider.getNetwork();

      if (network.chainId !== 1337n) {
        showToast('Please switch to the ATTEST-R network (chainId 1337).', 'warn');
        return;
      }

      setProvider(browserProvider);
      setSigner(signer);
      setAccount(address);
      setConnectionType('MetaMask');
      setStatus('Connected via MetaMask.');
      scheduleStatusClear();
      pushDebugEvent('Injected: connected');

      if (getIdentityAuthToken()) {
        await linkWalletToIdentity({ signer, account: address });
      }

      await loadUserAuthorities(address, browserProvider);
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      showToast(`Failed to connect wallet: ${error.message}`, 'error');
      setStatus(`Connection failed: ${error.message}`);
      scheduleStatusClear();
      pushDebugEvent(`Injected: failed (${error.message})`);
    } finally {
      setConnectingType('');
      setLoading(false);
    }
  };

  const connectWalletConnect = async () => {
    if (!walletConnectProjectId) {
    showToast('WalletConnect project ID missing. Set REACT_APP_WALLETCONNECT_PROJECT_ID and redeploy.', 'error');
      return;
    }
    try {
      // Always start a fresh session to avoid stale WalletConnect handshakes.
      if (walletConnectProviderRef.current || safeGetLocalStorage('walletconnect')) {
        await resetWalletConnection('walletconnect auto reset');
      }
      setConnectingType('walletconnect');
      setLoading(true);
      setStatus('Waiting for WalletConnect session approval...');
      pushDebugEvent('WalletConnect: awaiting session approval');
      const provider = await EthereumProvider.init({
        projectId: walletConnectProjectId,
        chains: [1337],
        rpcMap: {
          1337: defaultRpcUrl
        },
        showQrModal: true,
        methods: [
          'eth_requestAccounts',
          'eth_sendTransaction',
          'eth_sign',
          'personal_sign',
          'eth_signTypedData',
          'eth_signTypedData_v4',
          'wallet_switchEthereumChain',
          'wallet_addEthereumChain'
        ],
        events: ['chainChanged', 'accountsChanged']
      });
      walletConnectProviderRef.current = provider;
      externalProviderRef.current = provider;
      if (provider?.on) {
        provider.on('display_uri', () => {
          pushDebugEvent('WalletConnect: QR displayed');
        });
      }
      const accounts = await provider.enable();
      if (!accounts || accounts.length === 0) {
        setStatus('No accounts returned by WalletConnect.');
        pushDebugEvent('WalletConnect: no accounts returned');
        resetWalletConnection('walletconnect no accounts');
        return;
      }
      setStatus('Session approved. Loading account...');
      pushDebugEvent('WalletConnect: session approved');
      setStatus('Network ready. Loading signer...');
      pushDebugEvent('WalletConnect: network ready');

      if (provider?.on) {
        provider.on('accountsChanged', (accounts) => {
          if (!accounts || accounts.length === 0) {
            pushDebugEvent('WalletConnect: accountsChanged -> empty');
            setAccount(null);
            setAuthorities([]);
            setIdentity('');
            return;
          }
          pushDebugEvent(`WalletConnect: accountsChanged -> ${accounts[0]}`);
          setAccount(accounts[0]);
          void finalizeWalletConnectSession(provider);
        });
        provider.on('chainChanged', (chainId) => {
          const chainIdNumber = parseChainIdValue(chainId);
          pushDebugEvent(`WalletConnect: chainChanged -> ${chainIdNumber}`);
          setWalletNetworkId(chainIdNumber);
          if (chainIdNumber === 1337) {
            setStatus('Network changed. Finalizing connection...');
            void finalizeWalletConnectSession(provider);
          } else {
            setWalletNetworkMismatch(true);
            setStatus(`Connected to chain ${chainIdNumber}. Switch to ATTEST-R network (1337).`);
          }
        });
        provider.on('disconnect', (error) => {
          if (error?.message) {
            setStatus(`Disconnected: ${error.message}`);
          } else {
            setStatus('Disconnected from WalletConnect session.');
          }
          pushDebugEvent(`WalletConnect: disconnected (${error?.message || 'no reason'})`);
          scheduleStatusClear();
          resetWalletConnection('walletconnect disconnect');
        });
      }

      pushDebugEvent('WalletConnect: provider initialized');
      const address = accounts?.[0] || null;
      setAccount(address);
      setIdentity('Unknown User');
      setAuthorities([]);
      await finalizeWalletConnectSession(provider);
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      showToast(`Failed to connect wallet: ${error.message}`, 'error');
      setStatus(`Connection failed: ${error.message}`);
      scheduleStatusClear();
      pushDebugEvent(`WalletConnect: failed (${error.message})`);
      resetWalletConnection('connect failure');
    } finally {
      setConnectingType('');
      setLoading(false);
    }
  };

  const resetWalletConnection = async (reason) => {
    if (reason) {
      pushDebugEvent(`Reset: ${reason}`);
    }
    try {
      if (externalProviderRef.current?.disconnect) {
        await externalProviderRef.current.disconnect();
      }
      if (externalProviderRef.current?.close) {
        await externalProviderRef.current.close();
      }
    } catch (error) {
      console.warn('Wallet disconnect failed:', error);
      pushDebugEvent(`Reset error: ${error.message || 'unknown'}`);
    } finally {
      externalProviderRef.current = null;
      walletConnectProviderRef.current = null;
      setAccount(null);
      setProvider(null);
      setSigner(null);
      setAuthorities([]);
      setIdentity('');
      setConnectionType('');
      setConnectStatus('');
      setWalletNetworkMismatch(false);
      setWalletNetworkId(null);
      setWalletNetworkSwitching(false);
      safeRemoveLocalStorage('WEB3_CONNECT_CACHED_PROVIDER');
      safeRemoveLocalStorage('walletconnect');
      safeRemoveLocalStorage('WALLETCONNECT_DEEPLINK_CHOICE');
      if (typeof window !== 'undefined' && window.localStorage) {
        Object.keys(window.localStorage)
          .filter((key) => key.startsWith('wc@2:'))
          .forEach((key) => safeRemoveLocalStorage(key));
      }
    }
  };

  const retryWalletConnectNetwork = async () => {
    const provider = externalProviderRef.current;
    if (!provider?.request) return;
    setWalletNetworkSwitching(true);
    setStatus('Switching to ATTEST-R network...');
    pushDebugEvent('WalletConnect: retry network switch');
    try {
      await ensureCorrectNetwork(provider);
      await finalizeWalletConnectSession(provider);
    } catch (error) {
      const message = error?.message || 'Unknown error';
      setStatus(`Network switch failed: ${message}`);
      pushDebugEvent(`WalletConnect: switch failed (${message})`);
    } finally {
      setWalletNetworkSwitching(false);
    }
  };

  const startFreshWalletConnect = async () => {
    pushDebugEvent('WalletConnect: starting fresh session');
    await resetWalletConnection('walletconnect fresh session');
    await connectWalletConnect();
  };

  // Load user authorities from contract
  const loadUserAuthorities = async (address, provider) => {
    try {
      console.log("🔍 Loading authorities for:", address);

      console.log("📋 Contract address:", AUTHORITY_TOKEN_ADDRESS);
      if (!AUTHORITY_TOKEN_ADDRESS) {
        throw new Error('Authority token address not configured.');
      }
      
      const authorityContract = new ethers.Contract(AUTHORITY_TOKEN_ADDRESS, AUTHORITY_TOKEN_ABI, provider);
      
      // Get authority types
      const prodApprover = await authorityContract.PROD_APPROVER();
      const securityApprover = await authorityContract.SECURITY_APPROVER();
      const infraProvisioner = await authorityContract.INFRA_PROVISIONER();
      const breakGlass = await authorityContract.BREAK_GLASS();
      
      console.log("🔑 Authority hashes:", { prodApprover, securityApprover });
      
      // Check which authorities user has
      const userAuthorities = [];
      
      const hasProd = await authorityContract.hasAuthority(address, prodApprover);
      const hasSecurity = await authorityContract.hasAuthority(address, securityApprover);
      const hasInfra = await authorityContract.hasAuthority(address, infraProvisioner);
      const hasBreakGlass = await authorityContract.hasAuthority(address, breakGlass);
      
      console.log("✅ Authority checks:", { hasProd, hasSecurity, hasInfra, hasBreakGlass });
      
      if (hasProd) {
        userAuthorities.push({ type: 'PROD_APPROVER', hash: prodApprover });
      }
      if (hasSecurity) {
        userAuthorities.push({ type: 'SECURITY_APPROVER', hash: securityApprover });
      }
      if (hasInfra) {
        userAuthorities.push({ type: 'INFRA_PROVISIONER', hash: infraProvisioner });
      }
      if (hasBreakGlass) {
        userAuthorities.push({ type: 'BREAK_GLASS', hash: breakGlass });
      }

      setAuthorityTypeMap({
        [prodApprover.toLowerCase()]: 'PROD_APPROVER',
        [securityApprover.toLowerCase()]: 'SECURITY_APPROVER',
        [infraProvisioner.toLowerCase()]: 'INFRA_PROVISIONER',
        [breakGlass.toLowerCase()]: 'BREAK_GLASS'
      });
      
      console.log("👤 User authorities:", userAuthorities);
      setAuthorities(userAuthorities);
      
      // Get identity
      const userIdentity = await authorityContract.getIdentity(address);
      console.log("🆔 User identity:", userIdentity);
      setIdentity(userIdentity || 'Unknown User');

      // Load intents after authority lookup (ops console only)
      if (!governanceOnly) {
        await loadIntents();
      }
      
    } catch (error) {
      console.error('❌ Failed to load authorities:', error);
    }
  };

  // Load intents from API
  const loadIntents = async () => {
    try {
      const token = getIdentityAuthToken();
      const response = await apiFetch('/api/v1/intents', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.status === 401) {
        setIntents([]);
        intentsRef.current = [];
        return;
      }
      if (response.status === 401) {
        setReleaseRecord(null);
        return;
      }
      const data = await response.json();
      
      console.log("📋 Loaded intents from API:", data.intents);
      
      const previousById = new Map(intentsRef.current.map((intent) => [intent.id, intent]));
      // Transform API data to match dashboard format
      const transformedIntents = data.intents.map(intent => {
        const displayState = intent.display_state || deriveDisplayState(intent);
        const approvalsArray = Array.isArray(intent.approvals) ? intent.approvals : null;
        const previous = previousById.get(intent.id);
        let approvalsRequired = intent.approvals_required ?? previous?.required ?? 1;
        let approvalsCollected = intent.approvals_collected ?? previous?.approvals ?? 0;
        let approvalsSatisfied = intent.approvals_satisfied ?? previous?.approvalsSatisfied ?? false;
        if (approvalsArray !== null) {
          const requiredRoles = Array.isArray(intent.required_roles) && intent.required_roles.length > 0
            ? intent.required_roles
            : policyRolesFor(intent.policy_id);
          const summary = deriveApprovalSummary(
            requiredRoles,
            approvalsArray,
            intent.authorized || intent.on_chain_authorized
          );
          approvalsRequired = summary.approvalsRequired;
          approvalsCollected = summary.approvalsCollected;
          approvalsSatisfied = summary.approvalsSatisfied;
        }
        if (intent.authorized || intent.on_chain_authorized) {
          approvalsCollected = Math.max(approvalsCollected, approvalsRequired);
          approvalsSatisfied = true;
        }
        const overallStatus = intent.overall_status || deriveOverallStatusLocal({
          executionState: intent.execution_state,
          approvalsSatisfied,
          expiry: intent.expiry
        });
        const requiredRolesForIntent = Array.isArray(intent.required_roles) && intent.required_roles.length > 0
          ? intent.required_roles
          : policyRolesFor(intent.policy_id);
        const policyDisplay = requiredRolesForIntent.length > 0
          ? describeApprovalRequirement(requiredRolesForIntent, governanceRoles)
          : humanizePolicyId(intent.policy_id, governancePolicies);
        const presentation = intentPresentation(intent);
        return ({
          id: intent.id,
          title: presentation.title,
          artifact: presentation.artifact,
          commit: presentation.isGitLab ? intent.commit_sha : null,
          environment: intent.environment,
          requester: presentation.requester,
          created: new Date(intent.created_at),
          expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
          policy: policyDisplay,
          policy_id: intent.policy_id,
          process_type: intent.process_type || 'RELEASE_INTENT_V0',
          required_roles: requiredRolesForIntent,
          approvals: approvalsCollected,
          approvals_detail: approvalsArray || [],
          required: approvalsRequired,
          approvalsSatisfied,
          pipeline_url: intent.pipeline_url,
          project_id: intent.project_id,
          pipeline_id: intent.pipeline_id,
          authorized: !!(intent.on_chain_authorized || intent.authorized),
          status: intent.status || (intent.authorized ? 'approved' : 'pending'),
          authorization_state: intent.authorization_state || null,
          execution_state: intent.execution_state || null,
          overallStatus,
          display_state: displayState,
          execution_outcome: intent.execution_outcome || null,
          execution_executor: intent.execution_executor || null,
          execution_recorded_at: intent.execution_recorded_at || null,
          intent_cid: intent.intent_cid || null,
          intent_cid_hash: intent.intent_cid_hash || null,
          execution_cid: intent.execution_cid || null,
          execution_cid_hash: intent.execution_cid_hash || null,
          expiry: intent.expiry || null,
          pipeline_status: intent.pipeline_status_raw || null,
          execution_proof: intent.execution_proof ? {
            ...intent.execution_proof,
            executed_at: intent.execution_proof.timestamp
              ? new Date(intent.execution_proof.timestamp * 1000)
              : null
          } : null,
          approved_at: intent.approved_at || null
        });
      });
      
      intentsRef.current = transformedIntents;
      setIntents(transformedIntents);
      setLastIntentFetchAt(new Date());
      
    } catch (error) {
      console.error('❌ Failed to load intents:', error);
      // Fallback to empty array
      setIntents([]);
    }
  };

  const loadGovernanceData = async ({ silent = false } = {}) => {
    const token = getIdentityAuthToken();
    if (!token || !canAccessGovernance) return;
    if (!silent) setGovernanceLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [
        setupRes,
        actionsRes,
        actionsAllRes,
        membersRes,
        rolesRes,
        assignmentsRes,
        policiesRes,
        bindingsRes,
        auditRes,
        usersRes,
        legacyRes,
        optionsRes,
        integrationsRes,
        gitlabTriggersRes
      ] = await Promise.all([
        apiFetch('/api/v1/governance/setup-status', { headers }),
        apiFetch('/api/v1/governance/actions?openOnly=1', { headers }),
        apiFetch('/api/v1/governance/actions', { headers }),
        apiFetch('/api/v1/governance/members', { headers }),
        apiFetch('/api/v1/governance/roles', { headers }),
        apiFetch('/api/v1/governance/role-assignments', { headers }),
        apiFetch('/api/v1/governance/approval-policies', { headers }),
        apiFetch('/api/v1/governance/approval-policy-bindings', { headers }),
        apiFetch('/api/v1/governance/audit', { headers }),
        apiFetch('/api/v1/tenant/users', { headers }),
        apiFetch('/api/v1/governance/authorities', { headers }),
        apiFetch('/api/v1/governance/authority-options', { headers }),
        apiFetch('/api/v1/integrations', { headers }),
        apiFetch('/api/v1/integrations/gitlab/triggers', { headers })
      ]);
      const responses = [
        setupRes,
        actionsRes,
        actionsAllRes,
        membersRes,
        rolesRes,
        assignmentsRes,
        policiesRes,
        bindingsRes,
        auditRes,
        usersRes,
        legacyRes,
        optionsRes,
        integrationsRes,
        gitlabTriggersRes
      ];
      if (responses.some((res) => res.status === 401)) {
        clearIdentityToken();
        setIdentityStatus('Session expired. Please sign in again.');
        if (!silent) setGovernanceLoading(false);
        return;
      }
      if (setupRes.ok) {
        const data = await setupRes.json();
        setSetupStatus(data);
      }
      if (actionsRes.ok) {
        const data = await actionsRes.json();
        const byKey = new Map();
        (data.actions || []).forEach((action) => {
          const key = action.idempotencyKey || action.id;
          if (!byKey.has(key) || new Date(action.createdAt) > new Date(byKey.get(key).createdAt)) {
            byKey.set(key, action);
          }
        });
        const deduped = Array.from(byKey.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGovernanceActions(deduped);
      }
      if (actionsAllRes.ok) {
        const data = await actionsAllRes.json();
        const byKey = new Map();
        (data.actions || []).forEach((action) => {
          const key = action.idempotencyKey || action.id;
          if (!byKey.has(key) || new Date(action.createdAt) > new Date(byKey.get(key).createdAt)) {
            byKey.set(key, action);
          }
        });
        const deduped = Array.from(byKey.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGovernanceActionsAll(deduped);
      }
      if (membersRes.ok) {
        const data = await membersRes.json();
        setGovernanceMembers(data.members || []);
      }
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setGovernanceRoles(data.roles || []);
      }
      if (assignmentsRes.ok) {
        const data = await assignmentsRes.json();
        setGovernanceRoleAssignments(data.assignments || []);
      }
      if (policiesRes.ok) {
        const data = await policiesRes.json();
        setGovernancePolicies(data.policies || []);
      }
      if (bindingsRes.ok) {
        const data = await bindingsRes.json();
        setGovernancePolicyBindings(data.bindings || []);
      }
      if (auditRes.ok) {
        const data = await auditRes.json();
        setGovernanceAuditLogs(data.logs || []);
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setTenantUsers(data.users || []);
      }
      if (legacyRes.ok) {
        const data = await legacyRes.json();
        setLegacyAuthorityAssignments(data.assignments || []);
      }
      if (optionsRes.ok) {
        const data = await optionsRes.json();
        setAuthorityOptions(data.options || []);
      }
      if (integrationsRes.ok) {
        const data = await integrationsRes.json();
        const installs = data.installs || [];
        setIntegrationInstalls(installs);
        const gitlab = installs.find((entry) => entry.provider === 'gitlab') || null;
        setIntegrationInstall(gitlab);
      }
      if (gitlabTriggersRes.ok) {
        const data = await gitlabTriggersRes.json();
        setIntegrationTriggers(data.triggers || []);
      }
    } catch (error) {
      console.error('❌ Failed to load governance data:', error);
    } finally {
      if (!silent) setGovernanceLoading(false);
    }
  };

  const loadIntegrationTokens = async (provider, { silent = true } = {}) => {
    const token = getIdentityAuthToken();
    if (!token || !provider) return;
    try {
      const response = await apiFetch(`/api/v1/integrations/${provider}/tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setIntegrationTokens(data.tokens || []);
    } catch (error) {
      if (!silent) {
        showToast(error.message || 'Failed to load integration tokens.', 'error');
      }
    }
  };

  const proposeGovernanceAction = async (type, params) => {
    const token = getIdentityAuthToken();
    if (!token) {
      showToast('Sign in to propose governance actions.', 'warn');
      return;
    }
    if (!signer || !account || !walletLinked) {
      showToast('Connect a linked wallet to propose governance actions.', 'warn');
      return;
    }
    if (governanceSubmitting === type) return;
    try {
      setGovernanceSubmitting(type);
      const challengeResponse = await apiFetch('/api/v1/governance/propose/challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action_type: type,
          params,
          wallet_address: account
        })
      });
      const challengeData = await challengeResponse.json().catch(() => ({}));
      if (!challengeResponse.ok) {
        throw new Error(challengeData.error || 'Failed to start proposal signature');
      }
      const messageToSign = challengeData.message;
      if (!messageToSign || !challengeData.nonce) {
        throw new Error('Proposal challenge missing message or nonce');
      }
      const signature = await signer.signMessage(messageToSign);
      const response = await apiFetch('/api/v1/governance/actions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          type,
          params,
          wallet_address: account,
          signature,
          nonce: challengeData.nonce
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to propose action');
      }
      showToast(data?.deduped ? 'Existing governance action reused.' : 'Governance action proposed.', 'success');
      if (type === 'ADD_GC_MEMBER') {
        setGovMemberQuery('');
        setGovMemberId('');
      }
      await loadGovernanceData();
    } catch (error) {
      showToast(error.message || 'Failed to propose action.', 'error');
    } finally {
      setGovernanceSubmitting('');
    }
  };

  const signGovernanceAction = async (actionId) => {
    const token = getIdentityAuthToken();
    if (!token || !signer || !account) {
      showToast('Connect a linked wallet to sign.', 'warn');
      return;
    }
    try {
      setAwaitingSignature(true);
      const challengeResponse = await apiFetch(`/api/v1/governance/actions/${actionId}/sign/challenge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const challengeData = await challengeResponse.json();
      if (!challengeResponse.ok) {
        throw new Error(challengeData.error || 'Failed to start signature');
      }
      const signature = await signer.signMessage(challengeData.message);
      const signResponse = await apiFetch(`/api/v1/governance/actions/${actionId}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          wallet_address: account,
          signature,
          nonce: challengeData.nonce
        })
      });
      const signData = await signResponse.json();
      if (!signResponse.ok) {
        throw signData;
      }
      showToast('Governance signature recorded.', 'success');
      await loadGovernanceData();
      const refreshedProfile = await fetchIdentityProfile(token);
      if (refreshedProfile) {
        setSessionInfo(refreshedProfile);
      }
    } catch (error) {
      console.error('Sign error:', error);
      if (error.code === 'SESSION_EXPIRED') {
        showToast('Session expired. Please sign in again.', 'error');
        signOut();
      } else if (error.code === 'WALLET_NOT_LINKED') {
        showToast('Wallet not linked. Please link your wallet to sign.', 'error');
      } else if (error.code === 'NOT_GC_MEMBER') {
        showToast('You are not a member of the Governance Council.', 'error');
      } else if (error.code === 'PROPOSER_CANNOT_SIGN') {
        showToast('Proposer cannot approve their own change.', 'error');
      } else {
        showToast(error.message || 'Failed to sign action.', 'error');
      }
    } finally {
      setAwaitingSignature(false);
    }
  };

  const scanLegacyAuthority = async () => {
    const token = getIdentityAuthToken();
    if (!token) {
      showToast('Sign in to scan approvers.', 'warn');
      return;
    }
    if (!legacyScanWallet.trim()) {
      showToast('Enter a wallet address to scan.', 'warn');
      return;
    }
    try {
      const response = await apiFetch(`/api/v1/governance/authorities/scan?wallet=${encodeURIComponent(legacyScanWallet.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to scan wallet');
      }
      const entries = (data.roles || []).map((role) => ({
        id: `${data.userId || data.wallet}:${role}`,
        userId: data.userId || null,
        userEmail: data.userEmail || null,
        walletAddress: data.wallet,
        authority: role,
        status: 'ACTIVE',
        source: 'on_chain'
      }));
      mergeLegacyAuthorities(entries);
      showToast(entries.length ? 'Legacy approvers loaded.' : 'No approvers found for wallet.', entries.length ? 'success' : 'info');
    } catch (error) {
      showToast(error.message || 'Failed to scan wallet.', 'error');
    }
  };

  const applyGovernanceAction = async (actionId) => {
    const token = getIdentityAuthToken();
    // Apply only requires OIDC auth (GC membership checked server-side), NOT wallet
    if (!token) {
      showToast('Sign in to apply governance actions.', 'warn');
      return;
    }
    try {
      const response = await apiFetch(`/api/v1/governance/actions/${actionId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        // Show specific error codes
        const errorMessage = data.code === 'QUORUM_NOT_MET'
          ? `Quorum not met (${data.signatures || 0}/${data.quorumM || '?'} signatures)`
          : data.code === 'NOT_GC_MEMBER'
          ? 'You must be a governance council member to apply'
          : data.error || 'Failed to apply action';
        throw new Error(errorMessage);
      }
      showToast(data.alreadyApplied ? 'Action already applied.' : 'Governance action applied.', 'success');
      if (data?.result?.token) {
        setGovernanceActionSecrets((prev) => ({
          ...prev,
          [actionId]: {
            token: data.result.token,
            tokenId: data.result.tokenId || null
          }
        }));
        setTokenCreatedValue(data.result.token);
        setTokenCreatedActionId(actionId);
        await loadGovernanceActionDetails(actionId);
      }
      await loadGovernanceData();
      const refreshedProfile = await fetchIdentityProfile(token);
      if (refreshedProfile) {
        setSessionInfo(refreshedProfile);
      }
    } catch (error) {
      showToast(error.message || 'Failed to apply action.', 'error');
    }
  };

  const loadGovernanceActionDetails = async (actionId) => {
    const token = getIdentityAuthToken();
    if (!token) return;
    setGovernanceDetailLoading(true);
    try {
      const response = await apiFetch(`/api/v1/governance/actions/${actionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load action');
      }
      if (data?.result?.token && actionId) {
        setGovernanceActionSecrets((prev) => ({
          ...prev,
          [actionId]: {
            token: data.result.token,
            tokenId: data.result.tokenId || null
          }
        }));
      }
      setGovernanceDetail(data);
    } catch (error) {
      showToast(error.message || 'Failed to load action details.', 'error');
    } finally {
      setGovernanceDetailLoading(false);
    }
  };

  const closeGovernanceDetail = () => {
    const actionId = governanceDetail?.action?.id;
    if (actionId) {
      setGovernanceActionSecrets((prev) => {
        const next = { ...prev };
        delete next[actionId];
        return next;
      });
      if (actionId === tokenCreatedActionId) {
        setTokenCreatedValue('');
        setTokenCreatedActionId('');
      }
    }
    setGovernanceDetail(null);
  };

  const loadTestHarnessStatus = async () => {
    if (!showTestHarness) return;
    const token = getIdentityAuthToken();
    if (!token) {
      setTestHarnessError('Sign in to access test status.');
      setTestHarnessStatus(null);
      return;
    }
    try {
      const response = await apiFetch('/api/test/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Test harness not enabled');
      }
      const data = await response.json();
      setTestHarnessStatus(data);
      setTestHarnessError('');
    } catch (error) {
      setTestHarnessError(error.message || 'Unable to load test status');
      setTestHarnessStatus(null);
    }
  };

  const resetTenant = async () => {
    const token = getIdentityAuthToken();
    if (!token) {
      showToast('Sign in to reset tenant.', 'warn');
      return;
    }
    const tenantDomain = sessionInfo?.tenant?.domain || testHarnessStatus?.tenant?.domain || '';
    const expected = tenantDomain ? `RESET ${tenantDomain}` : '';
    if (!expected || resetConfirm !== expected) {
      showToast(`Type "${expected}" to confirm.`, 'warn');
      return;
    }
    try {
      const response = await apiFetch('/api/test/factory-reset-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ confirm: resetConfirm })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Reset failed');
      }
      showToast('Tenant reset.', 'success');
      setResetConfirm('');
      await loadTestHarnessStatus();
      await loadGovernanceData();
    } catch (error) {
      showToast(error.message || 'Reset failed', 'error');
    }
  };

  const loadReleaseRecord = async (intentId) => {
    try {
      const token = getIdentityAuthToken();
      const response = await apiFetch(`/api/v1/releases/${intentId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load release');
      }
      setReleaseRecord(data);
      setIntents((prev) => {
        const updated = prev.map((intent) => {
          if (intent.id !== intentId) return intent;
          const approvals = data.approvals || [];
          const { approvalsRequired, approvalsCollected, approvalsSatisfied } = deriveApprovalSummaryFromRoles(
            data.policy?.requiredRoles || [],
            approvals,
            data.derived?.approvalsSatisfied
          );
          const overallStatus = data.derived?.overallStatus || deriveOverallStatusLocal({
            executionState: data.execution?.status,
            approvalsSatisfied,
            expiry: intent.expiry
          });
          return {
            ...intent,
            approvals: approvalsCollected,
            required: approvalsRequired,
            approvalsSatisfied,
            overallStatus
          };
        });
        intentsRef.current = updated;
        return updated;
      });
    } catch (error) {
      console.error('❌ Failed to load release record:', error.message);
      setReleaseRecord(null);
    }
  };

  const startApproval = async (intentId) => {
    if (!signer) return;
    if (!approveEnabled) {
      showToast('Sign in and link your wallet to approve.', 'warn');
      return;
    }
    try {
      setApprovingIntentId(intentId);
      setSignatureIntentId(intentId);

      const token = getIdentityAuthToken();
      if (!token) {
        throw new Error('Sign in required to approve');
      }

      const requiredChainId = 1337;
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== requiredChainId) {
        throw new Error(`Wrong network. Switch to ATTEST-R (${requiredChainId}) to approve.`);
      }

      const challengeResponse = await apiFetch(`/api/v1/approvals/${intentId}/challenge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ wallet_address: account })
      });
      const challengeData = await challengeResponse.json();
      if (challengeResponse.status === 401) {
        clearIdentityToken();
        throw new Error('Session expired. Please sign in again.');
      }
      if (!challengeResponse.ok) {
        throw new Error(challengeData.error || 'Failed to start approval');
      }

      const intentMeta = intentsRef.current.find((item) => item.id === intentId) || activeIntentRef.current || null;
      setApprovalPrompt({
        intentId,
        intent: intentMeta,
        challenge: challengeData,
        chainId: Number(network.chainId)
      });
      setShowApprovalDetails(false);
    } catch (error) {
      console.error('Approval failed:', error);
      showToast(error.message || 'Approval failed.', 'error');
    } finally {
      setApprovingIntentId(null);
      setSignatureIntentId(null);
    }
  };

  const confirmApproval = async () => {
    if (!approvalPrompt || !signer) return;
    const { intentId, challenge, chainId } = approvalPrompt;
    try {
      setApprovingIntentId(intentId);
      setAwaitingSignature(true);
      setSignatureIntentId(intentId);

      const token = getIdentityAuthToken();
      if (!token) {
        throw new Error('Sign in required to approve');
      }

      const serverSignature = await signer.signMessage(challenge.message);

      const digest = ethers.solidityPackedKeccak256(
        ['string', 'uint256', 'address', 'bytes32'],
        ['ATTEST-R Approval:', Number(chainId), RELEASE_AUTH_ADDRESS, intentId]
      );
      const chainSignature = await signer.signMessage(ethers.getBytes(digest));
      const releaseContract = new ethers.Contract(RELEASE_AUTH_ADDRESS, RELEASE_AUTH_ABI, signer);
      const tx = await releaseContract.approve(intentId, ethers.getBytes(chainSignature));
      pushDebugEvent(`Approval tx submitted: ${tx.hash}`);
      setStatus('Approval submitted on-chain. Waiting for confirmation...');
      const receipt = await Promise.race([
        tx.wait(),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Transaction confirmation timeout')), 45000);
        })
      ]);
      if (receipt?.status !== 1) {
        throw new Error('On-chain approval failed');
      }

      let syncError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          };
          const response = await apiFetch(`/api/v1/approvals/${intentId}/approve`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              signature: serverSignature,
              wallet_address: account,
              nonce: challenge.nonce,
              decision: 'APPROVE'
            })
          });
          const result = await response.json();
          if (!response.ok || !result.success) {
            const message = result?.error || `API approval failed (${response.status || 'unknown'})`;
            throw new Error(message);
          }
          syncError = null;
          break;
        } catch (error) {
          syncError = error;
        }
      }

      const waitForStatus = async () => {
        const deadline = Date.now() + 30000;
        let lastError = null;
        while (Date.now() < deadline) {
          try {
            const statusResponse = await apiFetch(`/api/v1/intents/${intentId}/status`);
            const statusData = await statusResponse.json();
            if (statusResponse.ok && (statusData.authorized || statusData.on_chain_authorized)) {
              return { ok: true, data: statusData };
            }
            lastError = statusData?.error || null;
          } catch (error) {
            lastError = error.message;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        return { ok: false, error: lastError };
      };

      const syncStatus = await waitForStatus();
      const checkApprovalRecorded = async () => {
        try {
          const releaseResponse = await apiFetch(`/api/v1/releases/${intentId}`);
          const releaseData = await releaseResponse.json();
          if (!releaseResponse.ok) {
            return false;
          }
          const approvals = Array.isArray(releaseData.approvals) ? releaseData.approvals : [];
          return approvals.some((approval) => approval.wallet?.toLowerCase() === account.toLowerCase());
        } catch {
          return false;
        }
      };
      const approvalRecorded = syncStatus.ok ? true : await checkApprovalRecorded();
      if (syncError) {
        pushDebugEvent(`API sync failed (${syncError.message})`);
      }
      if (syncStatus.ok) {
        showToast('Approval recorded on-chain. All required approvals satisfied.', 'success');
      } else {
        showToast('Approval recorded on-chain. Awaiting remaining approvers.', 'success');
      }
      setApprovalPrompt(null);
      setTimeout(() => {
        loadIntents();
        loadReleaseRecord(intentId);
      }, 6000);
      await loadIntents();
      await loadReleaseRecord(intentId);
    } catch (error) {
      console.error('Approval failed:', error);
      showToast(error.message || 'Approval failed.', 'error');
    } finally {
      setApprovingIntentId(null);
      setAwaitingSignature(false);
      setSignatureIntentId(null);
    }
  };

  const openIntentModal = (intent) => {
    if (intent) {
      const presentation = intentPresentation(intent);
      setDrawerData({
        summary: `${toTitleCase(presentation.actionLabel)} request ${intent.id || '—'}. ${intent.approvals || 0}/${intent.required || '—'} approvals recorded.`,
        events: [],
        audit: [
          { id: 'policy', label: `policyId: ${intent.policy_id || '—'}` },
          { id: 'roles', label: `roleIds: ${(intent.required_roles || []).join(', ') || '—'}` },
          { id: 'intent', label: `requestId: ${intent.id || '—'}` },
          { id: 'resource', label: `resource: ${presentation.targetLabel || '—'}` },
          { id: 'action', label: `action: ${presentation.actionLabel || '—'}` }
        ],
        rawJson: intent,
        copyItems: [
          { label: 'Pipeline URL', value: intent.pipeline_url || '' },
          { label: 'Request ID', value: intent.id || '' }
        ]
      });
    }
    setActiveIntent(intent);
    activeIntentRef.current = intent;
    setReleaseView('engineer');
    setReleaseRecord(null);
    loadReleaseRecord(intent.id);
  };

  const modalApproval = (() => {
    if (!releaseRecord || !account) return null;
    const requiredRoles = releaseRecord.policy?.requiredRoles
      || releaseRecord.required_roles
      || activeIntentRef.current?.required_roles
      || [];
    const approvals = releaseRecord.approvals || [];
    const userAuthorities = authorityTypes;
    const approvedByUser = approvals.some((approval) => approval.wallet?.toLowerCase() === account.toLowerCase());
    const eligible = approveEnabled && requiredRoles.some((role) => userAuthorities.includes(role));
    const approvalsCount = approvals.length;
    const requiredCount = requiredRoles.length || 0;
    const overallStatus = releaseRecord.derived?.overallStatus || 'PENDING_APPROVAL';
    return {
      eligible,
      approvedByUser,
      approvalsCount,
      requiredCount,
      overallStatus
    };
  })();

  const modalProvider = releaseRecord?.intent?.provider
    || activeIntentRef.current?.provider
    || activeIntent?.provider
    || 'legacy';
  const useCustomIntegrationModal = modalProvider && modalProvider !== 'gitlab' && modalProvider !== 'legacy';

  const closeIntentModal = () => {
    setActiveIntent(null);
    setReleaseRecord(null);
    activeIntentRef.current = null;
  };

  useEffect(() => {
    releaseRecordRef.current = releaseRecord;
  }, [releaseRecord]);

  const approvalEnvOptions = useMemo(() => {
    const envs = new Set();
    intents.forEach((intent) => {
      if (intent.environment) envs.add(intent.environment);
    });
    return Array.from(envs).sort();
  }, [intents]);

  const filteredIntents = useMemo(() => {
    const query = approvalSearch.trim().toLowerCase();
    return intents.filter((intent) => {
      if (approvalEnvFilter !== 'all' && intent.environment !== approvalEnvFilter) return false;
      if (query) {
        const haystack = [
          intent.pipeline_id,
          intent.pipelineId,
          intent.commit,
          intent.commitHash,
          intent.title,
          intent.environment
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [intents, approvalEnvFilter, approvalSearch]);

  const hasUserApproval = (intent) => {
    if (!account) return false;
    const approvals = Array.isArray(intent.approvals_detail) ? intent.approvals_detail : [];
    return approvals.some((entry) => {
      const wallet = entry.wallet || entry.wallet_address || entry.approver || '';
      return wallet && wallet.toLowerCase() === account.toLowerCase();
    });
  };

  const requiresMyApproval = (intent) => {
    const requiredRoles = requiredRolesForIntent(intent);
    return intent.overallStatus === 'PENDING_APPROVAL'
      && approveEnabled
      && requiredRoles.some((role) => authorityTypes.includes(role))
      && !hasUserApproval(intent);
  };

  const visibleIntents = useMemo(() => {
    if (!needsMyApprovalOnly) return filteredIntents;
    return filteredIntents.filter((intent) => requiresMyApproval(intent));
  }, [filteredIntents, needsMyApprovalOnly, approveEnabled, authorityTypes, account]);

  const awaitingIntents = visibleIntents.filter((intent) => intent.overallStatus === 'PENDING_APPROVAL');
  const needsMyApprovalIntents = awaitingIntents.filter((intent) => requiresMyApproval(intent));
  const waitingOnOthersIntents = awaitingIntents.filter((intent) => !requiresMyApproval(intent));
  const approvedNotExecutedIntents = visibleIntents.filter((intent) => intent.overallStatus === 'APPROVED_PENDING_EXECUTION');
  const executedIntents = visibleIntents.filter((intent) => (
    intent.overallStatus === 'EXECUTED_SUCCESS' || intent.overallStatus === 'EXECUTED_FAILED'
  ));
  const expiredIntents = visibleIntents.filter((intent) => intent.overallStatus === 'EXPIRED');

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds)) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const nowMs = Date.now();
  const windowMs = 24 * 60 * 60 * 1000;
  const intents24h = intents.filter((intent) => intent.created && nowMs - intent.created.getTime() <= windowMs);
  const executed24h = intents.filter((intent) => {
    if (!intent.execution_recorded_at) return false;
    const executedAt = new Date(intent.execution_recorded_at);
    return nowMs - executedAt.getTime() <= windowMs;
  });
  const approvalDurations = intents24h
    .filter((intent) => intent.approved_at)
    .map((intent) => {
      const createdAt = intent.created?.getTime?.() || 0;
      const approvedAt = new Date(intent.approved_at).getTime();
      if (!createdAt || !approvedAt) return null;
      return Math.max(0, (approvedAt - createdAt) / 1000);
    })
    .filter((value) => Number.isFinite(value));
  const avgApprovalSeconds = approvalDurations.length
    ? approvalDurations.reduce((sum, value) => sum + value, 0) / approvalDurations.length
    : null;

  useEffect(() => {
    if (governanceOnly) return;
    loadIntents();
  }, [apiBase, governanceOnly]);

  useEffect(() => {
    const token = getIdentityAuthToken();
    const streamUrl = token
      ? apiUrl(`/api/events?token=${encodeURIComponent(token)}`)
      : apiUrl('/api/events');
    const source = new EventSource(streamUrl);
    source.onopen = () => setRealtimeStatus('connected');
    source.onmessage = (event) => {
      if (!event.data) return;
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      const intentEventTypes = new Set([
        'intent_updated',
        'intent_created',
        'approval_recorded',
        'execution_recorded',
        'pipeline_status_changed',
        'verification_recorded'
      ]);
      const governanceEventTypes = new Set([
        'governance_action_created',
        'governance_action_signed',
        'governance_action_applied',
        'governance_action_failed'
      ]);
      if (payload.intentId && intentEventTypes.has(payload.type) && !governanceOnly) {
        loadIntents();
        if (activeIntentRef.current?.id === payload.intentId) {
          loadReleaseRecord(payload.intentId);
        }
        return;
      }
      if (payload.actionId && governanceEventTypes.has(payload.type)) {
        loadGovernanceData();
        // Refresh identity/session so UI role/member counts update for admins
        (async () => {
          const token = getIdentityAuthToken();
          if (token) {
            const refreshed = await fetchIdentityProfile(token);
            if (refreshed) setSessionInfo(refreshed);
          }
        })();
      }
    };
    source.addEventListener('governance.action', (event) => {
      if (!event.data) return;
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (payload.actionId) {
        loadGovernanceData();
        (async () => {
          const token = getIdentityAuthToken();
          if (token) {
            const refreshed = await fetchIdentityProfile(token);
            if (refreshed) setSessionInfo(refreshed);
          }
        })();
      }
    });
    source.onerror = () => {
      setRealtimeStatus('disconnected');
      // Let EventSource handle retries.
    };
    return () => source.close();
  }, [apiBase, governanceOnly, identityToken]);

  useEffect(() => {
    if (!governanceOnly || !isLoggedIn) return;
    if (activeConsole !== 'governance') return;
    const interval = setInterval(() => {
      loadGovernanceData({ silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [governanceOnly, isLoggedIn, activeConsole, identityToken]);

  useEffect(() => {
    if (governanceTab !== 'integrations') return;
    if (!tokenProvider) return;
    loadIntegrationTokens(tokenProvider, { silent: true });
  }, [governanceTab, tokenProvider]);

  const gitlabInstall = integrationInstall;
  const gitlabStatusValue = gitlabInstall?.status || 'NOT_CONFIGURED';
  const gitlabStatusLabel = gitlabStatusValue === 'ACTIVE'
    ? 'Enabled'
    : gitlabStatusValue === 'DISABLED'
    ? 'Disabled'
    : 'Not configured';
  const gitlabStatusClass = gitlabStatusValue === 'ACTIVE'
    ? 'success'
    : gitlabStatusValue === 'DISABLED'
    ? 'amber'
    : 'gray';
  const tokenInstalls = integrationInstalls.filter((install) => install.authMode === 'token');
  const tokenInstall = integrationInstalls.find((install) => install.provider === tokenProvider) || null;
  const tokenStatusValue = tokenInstall?.status || 'NOT_CONFIGURED';
  const integrationProviders = integrationInstalls.map((install) => install.provider);
  const activeIntegrationCount = integrationInstalls.filter((install) => install.status === 'ACTIVE').length;
  const tokenProviderList = tokenInstalls.map((install) => install.provider);
  const gitlabProjectId = policyBindingResourceId || 'YOUR_PROJECT_ID';
  const gitlabAudience = (Array.isArray(gitlabInstall?.audiences) && gitlabInstall.audiences[0]) || 'attest-r-staging';
  const databridgeConfigJson = JSON.stringify({
    baseUrl: apiBase,
    token: '<paste databridge token>',
    opsUrl: 'https://console.example.com',
    dataset: 'customers_pii',
    env: 'prod'
  }, null, 2);
  const tokenIntegrationHandoff = [
    'Token-based integration guide (share with customers)',
    '',
    '1) Provision access',
    '- Create integration (auth: token) and enable it via governance proposal.',
    '- Create a token and copy it once from the applied action.',
    '- Bind an ACTIVE policy to provider/resource_type/resource_id.',
    '',
    '2) Store the token securely in the customer app',
    '- Use an env var or secret manager.',
    '- Do NOT hardcode tokens in source control.',
    '',
    '3) Required headers for all requests',
    '  Authorization: Bearer <integration-token>',
    '  Content-Type: application/json',
    '',
    '4) Create intent (approval request)',
    `  POST ${apiBase}/api/v1/integrations/${tokenProvider || ':provider'}/intents`,
    '  Body:',
    '  {',
    '    "action_type": "data_export_csv",',
    '    "resource_type": "databridge_dataset",',
    '    "resource_id": "customers_pii",',
    '    "environment": "prod",',
    '    "context": { "attempt_id": "<uuid>", "reason": "demo export" }',
    '  }',
    '',
    '5) Poll for approval status',
    `  GET ${apiBase}/api/v1/integrations/${tokenProvider || ':provider'}/intents/:id`,
    '- Wait for status: approved',
    '',
    '6) Perform the action, then record execution on-chain',
    `  POST ${apiBase}/api/v1/integrations/${tokenProvider || ':provider'}/intents/:id/execute`,
    '  Body:',
    '  {',
    '    "executed_hash": "<sha256 or unique hash>",',
    '    "success": true,',
    '    "pipeline_status_raw": "SUCCEEDED"',
    '  }',
    '',
    'Notes',
    '- executed_hash should uniquely represent the action (hash of artifact, CSV, etc).',
    '- This is the step that records execution proof on-chain.'
  ].join('\n');
  const gitlabInstallSnippet = [
    'stages:',
    '  # First pipeline run: request approval intent',
    '  - intent',
    '  # Second pipeline run (triggered by ATTEST-R): deploy + record execution',
    '  - deploy',
    '',
    'variables:',
    '  # Base URL of your ATTEST-R API (governance console shows this)',
    `  ATTESTR_BASE_URL: "${apiBase}"`,
    '  # Required when ATTEST_R_API_KEY is set on the server',
    '  ATTEST_R_API_KEY: ""',
    '',
    'create_intent:',
    '  # Runs on regular pipelines, submits the approval intent.',
    '  stage: intent',
    '  image: curlimages/curl:8.7.1',
    '  rules:',
    '    # Never create a new intent on ATTEST-R triggered pipelines (prevents loops)',
    '    - if: \'$TRIGGER_SOURCE == "ATTEST_R"\'',
    '      when: never',
    '    - when: on_success',
    '  script:',
    '    - |',
    '      # Submit an intent using the GitLab OIDC id_token (audience set below).',
    '      # Ensure CI_ENVIRONMENT_NAME is set in GitLab or replace below.',
    '      printf \'%s\\n\' \\',
    '        \'{\' \\',
    `        "  \\"project_id\\\": \\"${gitlabProjectId}\\"," \\`,
    '        "  \\"project_path\\\": \\"${CI_PROJECT_PATH}\\"," \\',
    '        "  \\"group_id\\\": \\"${CI_PROJECT_NAMESPACE_ID}\\"," \\',
    '        "  \\"group_path\\\": \\"${CI_PROJECT_NAMESPACE}\\"," \\',
    '        "  \\"environment\\\": \\"${CI_ENVIRONMENT_NAME}\\"," \\',
    '        "  \\"pipeline_id\\\": \\"${CI_PIPELINE_ID}\\"," \\',
    '        "  \\"pipeline_url\\\": \\"${CI_PIPELINE_URL}\\"," \\',
    '        "  \\"commit_sha\\\": \\"${CI_COMMIT_SHA}\\"," \\',
    '        "  \\"ref\\\": \\"${CI_COMMIT_REF_NAME}\\"" \\',
    '        \'}\' | curl -fsS -X POST "$ATTESTR_BASE_URL/api/v1/integrations/gitlab/intents" \\',
    '          -H "Authorization: Bearer $ATTESTR_JWT" \\',
    '          -H "Content-Type: application/json" \\',
    '          -d @-',
    '  id_tokens:',
    '    ATTESTR_JWT:',
    '      # Audience must match the GitLab integration install in ATTEST-R.',
    `      aud: "${gitlabAudience}"`,
    '',
    'deploy:',
    '  # Runs only when ATTEST-R triggers the pipeline after on-chain approval.',
    '  stage: deploy',
    '  rules:',
    '    - if: \'$TRIGGER_SOURCE == "ATTEST_R"\'',
    '      when: on_success',
    '    - when: never',
    '  script:',
    '    - |',
    '      set -euo pipefail',
    '      if [ -z "$ATTEST_R_API_KEY" ]; then',
    '        echo "ATTEST_R_API_KEY missing"',
    '        exit 1',
    '      fi',
    '    - echo "Deploying release $RELEASE_ID (authorized=$AUTHORIZED)"',
    '    # Replace this with your real deployment command(s)',
    '    - echo "TODO: replace with real deploy"',
    '  after_script:',
    '    - |',
    '      set -euo pipefail',
    '      # Record execution on-chain using the execution endpoint.',
    '      EXEC_STATUS="$CI_JOB_STATUS"',
    '      SUCCESS=false',
    '      if [ "$EXEC_STATUS" = "success" ]; then SUCCESS=true; fi',
    '      EXECUTED_HASH="$CI_PIPELINE_ID:$CI_JOB_ID:$CI_COMMIT_SHA:$EXEC_STATUS"',
    '',
    '      # If API key auth is enabled, include the header; otherwise omit it.',
    '      if [ -n "$ATTEST_R_API_KEY" ]; then',
    '        AUTH_HEADER="-H X-Attest-R-Api-Key:$ATTEST_R_API_KEY"',
    '      else',
    '        AUTH_HEADER=""',
    '      fi',
    '',
    '      printf \'%s\\n\' \\',
    '        \'{\' \\',
    '        "  \\"executed_hash\\\": \\"${EXECUTED_HASH}\\"," \\',
    '        "  \\"success\\\": ${SUCCESS}," \\',
    '        "  \\"pipeline_status_raw\\\": \\"${EXEC_STATUS}\\"" \\',
    '        \'}\' | curl -fsS -X POST "$ATTESTR_BASE_URL/api/v1/intents/$RELEASE_ID/execute" \\',
    '          -H "Content-Type: application/json" \\',
    '          $AUTH_HEADER \\',
    '          -d @-'
  ].join('\n');

  return (
      <div className="App">
      <header className="app-header">
        {showHeroContent && (
          <>
            <h1>{governanceOnly ? 'ATTEST-R Governance Console' : 'ATTEST-R Approval Dashboard'}</h1>
            <p>
              {governanceOnly
                ? 'Governance Council controls, policy administration, and audit evidence.'
                : 'Authoritative Tamper-proof Transactional Evidence for Software Transfers Release'}
            </p>
          </>
        )}
      </header>

      <main className="app-main">
        {toast && (
          <div className={`toast toast-${toast.tone}`}>
            {toast.message}
          </div>
        )}
        <div className="dashboard">
          <LeftPanel>
            <div className="panel-section">
              <h3>Session &amp; Identity</h3>
              <p className="panel-muted">User access and signer controls</p>
            </div>
            <div className="panel-section">
              <h4>Console</h4>
              {allowConsoleToggle ? (
                <div className="panel-actions">
                  <button
                    className={activeConsole === 'operations' ? 'connect-button' : 'connect-button secondary'}
                    onClick={() => setActiveConsole('operations')}
                  >
                    Operations
                  </button>
                  {canAccessGovernance && (
                    <button
                      className={activeConsole === 'governance' ? 'connect-button' : 'connect-button secondary'}
                      onClick={() => setActiveConsole('governance')}
                    >
                      Governance
                    </button>
                  )}
                </div>
              ) : (
                <p className="panel-hint">Mode: {governanceOnly ? 'Governance' : 'Operations'}</p>
              )}
            </div>
            <div className="panel-section">
              <h4>User Session</h4>
              <div className="panel-stack">
                <div className="panel-line">
                  <span className="panel-label">Status:</span>
                  <span className="panel-value">{isLoggedIn ? 'Signed in' : 'Signed out'}</span>
                </div>
                <div className="panel-line">
                  <span className="panel-label">User:</span>
                  <span className="panel-value">{isLoggedIn ? displayIdentity : '—'}</span>
                </div>
                <div className="panel-line">
                  <span className="panel-label">Provider:</span>
                  <span className="panel-value">{oidcProvider}</span>
                </div>
              </div>
              {oidcEnabled !== false && (
                <div className="panel-actions">
                  {isLoggedIn ? (
                    <button
                      className="connect-button secondary"
                      onClick={signOut}
                      disabled={identityLoading}
                    >
                      {identityLoading ? 'Signing out...' : 'Sign out'}
                    </button>
                  ) : (
                    <button
                      className="connect-button secondary"
                      onClick={startOidcLogin}
                      disabled={identityLoading}
                    >
                      {identityLoading ? 'Signing in...' : `Sign in with ${oidcProvider}`}
                    </button>
                  )}
                  {identityStatus && <p className="connect-status">{identityStatus}</p>}
                  {oidcStatusNote && <p className="panel-hint">{oidcStatusNote}</p>}
                </div>
              )}
            </div>

            <div className="panel-section">
              <h4>Wallet Signer</h4>
              <div className="panel-stack">
                <div className="panel-line">
                  <span className="panel-label">Signer status:</span>
                  <span className="panel-value">{walletState === 'LINKED' ? 'Linked' : walletState === 'CONNECTED' ? 'Not linked' : 'Not connected'}</span>
                </div>
                {account ? (
                  <>
                    <div className="panel-line">
                      <span className="panel-label">Wallet:</span>
                      <span className="panel-value panel-wallet" title={account}>
                        Connected wallet on record
                      </span>
                    </div>
                    {walletProviderLabel !== 'Not connected' && (
                      <div className="panel-line">
                        <span className="panel-label">Connection:</span>
                        <span className="panel-value">{walletProviderLabel}</span>
                      </div>
                    )}
                    {chainLabel && (
                      <div className="panel-line">
                        <span className="panel-label">Chain:</span>
                        <span className="panel-value">{chainLabel}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="panel-line">
                    <span className="panel-label">Wallet:</span>
                    <span className="panel-value">Not connected</span>
                  </div>
                )}
              </div>
              {walletNetworkMismatch && (
                <div className="wallet-network-warning">
                  <p>
                    <strong>Network:</strong> Connected to chain {walletNetworkId}. Switch to ATTEST-R network (1337).
                  </p>
                </div>
              )}
              {!isLoggedIn ? (
                <p className="panel-hint">Sign in to connect your wallet.</p>
              ) : !walletLinked && (
                <div className="panel-actions">
                  <button
                    className="connect-button"
                    onClick={() => setShowConnectOptions((prev) => !prev)}
                    disabled={loading}
                  >
                    {loading ? 'Connecting...' : 'Connect Wallet'}
                  </button>
                  {showConnectOptions && (
                    <div className="connect-options">
                      <button
                        className="connect-button"
                        onClick={connectInjected}
                        disabled={loading}
                      >
                        {loading && connectingType === 'injected' ? 'Connecting...' : 'MetaMask (recommended)'}
                      </button>
                      <button
                        className="connect-button secondary"
                        onClick={connectWalletConnect}
                        disabled={loading}
                      >
                        {loading && connectingType === 'walletconnect' ? 'Connecting...' : 'WalletConnect (mobile)'}
                      </button>
                    </div>
                  )}
                  {connectStatus && <p className="connect-status">{connectStatus}</p>}
                  {!connectStatus && lastConnectStatus && (
                    <p className="connect-status connect-status-muted">
                      Last status: {lastConnectStatus}
                    </p>
                  )}
                </div>
              )}
              {walletLinked && (
                <div className="panel-actions">
                  <button className="switch-wallet" onClick={resetWalletConnection}>
                    Switch / Reset Wallet
                  </button>
                </div>
              )}
              {isLoggedIn && account && !walletLinked && (
                <div className="panel-actions">
                  <button
                    className="connect-button secondary"
                    onClick={linkWalletToIdentity}
                    disabled={identityLoading}
                  >
                    {identityLoading ? 'Linking...' : 'Link wallet to identity'}
                  </button>
                </div>
              )}
              {debugAllowed && (
                <>
                  <button
                    type="button"
                    className="connect-debug-toggle"
                    onClick={() => setShowTroubleshoot((prev) => !prev)}
                  >
                    {showTroubleshoot ? 'Hide troubleshooting' : 'Troubleshoot connection'}
                  </button>
                  {showTroubleshoot && (
                    <div className="connect-troubleshoot">
                      <div className="connect-actions">
                        <button
                          type="button"
                          className="connect-button secondary"
                          onClick={retryWalletConnectNetwork}
                          disabled={walletNetworkSwitching}
                        >
                          {walletNetworkSwitching ? 'Switching...' : 'Retry Network Switch'}
                        </button>
                        <button
                          type="button"
                          className="connect-button secondary"
                          onClick={startFreshWalletConnect}
                          disabled={loading}
                        >
                          New WalletConnect Session
                        </button>
                        <button
                          type="button"
                          className="connect-button secondary"
                          onClick={openMetaMaskDeepLink}
                          disabled={loading}
                        >
                          Open MetaMask
                        </button>
                      </div>
                      <button
                        type="button"
                        className="connect-debug-toggle"
                        onClick={() => setShowDebug((prev) => !prev)}
                      >
                        {showDebug ? 'Hide debug' : 'Show debug'}
                      </button>
                      {showDebug && (
                        <div className="connect-debug-panel">
                          {debugEvents.length === 0 ? (
                            <p>No debug events yet.</p>
                          ) : (
                            <ul>
                              {debugEvents.map((event, index) => (
                                <li key={`${event.time}-${index}`}>
                                  <span className="debug-time">{event.time}</span>
                                  <span className="debug-message">{event.message}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="panel-section">
              <h4>Your Roles</h4>
              {!isLoggedIn ? (
                <p className="panel-hint">Sign in to view your approval access.</p>
              ) : displayedRoles.length > 0 ? (
                <ul className="authorities-list">
                  {displayedRoles.map((role) => (
                    <li key={role}>{role.replace('_', ' ')}</li>
                  ))}
                </ul>
              ) : (
                <p className="panel-hint">Not authorized to approve. Ask an admin to assign roles.</p>
              )}
            </div>
          </LeftPanel>
          <WorkSurface>
            {showTestHarness ? (
              <div className="test-harness">
                <section className="console-hero">
                  <h2>Staging Test Harness</h2>
                  <p>Visible only when TEST_HARNESS_ENABLED is true.</p>
                </section>
                {testHarnessError && (
                  <div className="status-banner warn">{testHarnessError}</div>
                )}
                {testHarnessStatus && (
                  <div className="governance-grid">
                    <div className="panel-section">
                      <h4>Session</h4>
                      <p>User: {testHarnessStatus.user?.email || '—'}</p>
                      <p>Provider: {testHarnessStatus.user?.provider || '—'}</p>
                      <p title={testHarnessStatus.user?.userId || ''}>
                        User: {testHarnessStatus.user?.userId ? 'On record' : '—'}
                      </p>
                    </div>
                    <div className="panel-section">
                      <h4>Tenant</h4>
                      <p>Domain: {testHarnessStatus.tenant?.primaryDomain || '—'}</p>
                      <p>Allowed: {(testHarnessStatus.tenant?.allowedDomains || []).join(', ') || '—'}</p>
                    </div>
                    <div className="panel-section">
                      <h4>Governance</h4>
                      <p>Mode: {testHarnessStatus.governance?.mode || '—'}</p>
                      <p>Quorum: {testHarnessStatus.governance?.quorumM || '—'}-of-{testHarnessStatus.governance?.quorumN || '—'}</p>
                      <p>GC Member: {testHarnessStatus.governance?.isMember ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="panel-section">
                      <h4>Wallet Binding</h4>
                      <p>Linked: {testHarnessStatus.walletBinding?.linked ? 'Yes' : 'No'}</p>
                      <p title={testHarnessStatus.walletBinding?.walletAddress || ''}>
                        Wallet: {testHarnessStatus.walletBinding?.walletAddress ? 'On record' : '—'}
                      </p>
                    </div>
                    <div className="panel-section">
                      <h4>Capabilities</h4>
                      <p>Can govern: {testHarnessStatus.capabilities?.canGovern ? 'Yes' : 'No'}</p>
                      <p>Can sign governance: {testHarnessStatus.capabilities?.canSignGovernance ? 'Yes' : 'No'}</p>
                      <p>Operational approvers: {(testHarnessStatus.capabilities?.operationalAuthorities || []).join(', ') || '—'}</p>
                      <p>Can approve releases: {testHarnessStatus.capabilities?.canApproveReleases ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="panel-section">
                      <h4>Evidence Services</h4>
                      <p>API: {testHarnessStatus.services?.api || '—'}</p>
                      <p>Chain: {testHarnessStatus.services?.chain || '—'}</p>
                      <p>IPFS: {testHarnessStatus.services?.ipfs || '—'}</p>
                      <p>Anchor mode: {testHarnessStatus.anchor?.mode || '—'}</p>
                      <p title={testHarnessStatus.anchor?.address || ''}>
                        Anchor address: {testHarnessStatus.anchor?.address ? 'Configured' : '—'}
                      </p>
                    </div>
                    <div className="panel-section">
                      <h4>Recent Governance Actions</h4>
                      <ul className="gov-list">
                        {(testHarnessStatus.governanceActions || []).map((action) => (
                          <li key={action.id}>
                            {humanizeGovernanceActionType(action.type)} · {action.state === 'APPLIED'
                              ? 'Applied'
                              : action.state === 'SIGNED'
                                ? 'Signed'
                                : action.state === 'PROPOSED'
                                  ? 'Proposed'
                                  : 'In progress'} · {action.signatures}/{action.quorumM}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="panel-section">
                      <h4>Recent Deployment Requests</h4>
                      <ul className="gov-list">
                        {(testHarnessStatus.intents || []).map((intent) => (
                          <li key={intent.id}>
                            <span title={intent.id || ''}>
                              Deployment request · {intent.displayState || '—'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {canAccessGovernance && (
                      <div className="panel-section">
                        <h4>Reset Tenant (staging only)</h4>
                        <p className="panel-hint">
                          Type RESET {testHarnessStatus?.tenant?.domain || 'tenant-domain'} to confirm.
                        </p>
                        <input
                          className="gov-input"
                          value={resetConfirm}
                          onChange={(event) => setResetConfirm(event.target.value)}
                          placeholder={`RESET ${testHarnessStatus?.tenant?.domain || 'tenant-domain'}`}
                        />
                        <button className="connect-button secondary" onClick={resetTenant}>
                          Reset tenant
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : activeConsole === 'operations' ? (
              <>
            {account && awaitingSignature && (
              <div className="signature-banner">
                <div>
                  <strong>Waiting for wallet signature…</strong>
                  <span>Request {signatureIntentId ? signatureIntentId.slice(0, 10) + '…' : ''}</span>
                </div>
                <button
                  type="button"
                  className="connect-button secondary"
                  onClick={openMetaMaskDeepLink}
                >
                  Open MetaMask
                </button>
              </div>
            )}
            <section className="console-hero">
              {showHeroContent && (
                <>
                  <h2>ATTEST-R Approval Dashboard</h2>
                  {isLoggedIn ? (
                    <p>You have {awaitingIntents.length} approvals pending.</p>
                  ) : (
                    <p>Sign in to view approvals, evidence, and execution status.</p>
                  )}
                </>
              )}
            </section>

            {isLoggedIn && (
              <div className="summary-tiles">
                <div className="summary-tile">
                  <span className="summary-label">Requests (24h)</span>
                  <span className="summary-value">{intents24h.length}</span>
                </div>
                <div className="summary-tile">
                  <span className="summary-label">Pending approvals</span>
                  <span className="summary-value">{awaitingIntents.length}</span>
                </div>
                <div className="summary-tile">
                  <span className="summary-label">Approved awaiting deployment</span>
                  <span className="summary-value">{approvedNotExecutedIntents.length}</span>
                </div>
                <div className="summary-tile">
                  <span className="summary-label">Executed (24h)</span>
                  <span className="summary-value">{executed24h.length}</span>
                </div>
                <div className="summary-tile">
                  <span className="summary-label">Avg approval time</span>
                  <span className="summary-value">{formatDuration(avgApprovalSeconds)}</span>
                </div>
              </div>
            )}
            <div className="pending-approvals">
              <h2>Approval Board</h2>
              <div className="approval-filters">
                <div className="filter-group">
                  <label htmlFor="env-filter">Env</label>
                  <select
                    id="env-filter"
                    value={approvalEnvFilter}
                    onChange={(event) => setApprovalEnvFilter(event.target.value)}
                    disabled={approvalEnvOptions.length === 0}
                  >
                    <option value="all">All</option>
                    {approvalEnvOptions.map((env) => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="needs-my-approval">
                    <input
                      id="needs-my-approval"
                      type="checkbox"
                      checked={needsMyApprovalOnly}
                      onChange={(event) => setNeedsMyApprovalOnly(event.target.checked)}
                    />
                    Needs my approval
                  </label>
                </div>
                <div className="filter-group filter-search">
                  <label htmlFor="approval-search">Search</label>
                  <input
                    id="approval-search"
                    type="text"
                    placeholder="Pipeline or commit"
                    value={approvalSearch}
                    onChange={(event) => setApprovalSearch(event.target.value)}
                  />
                </div>
              </div>

              {(() => {
                const renderIntentCard = (intent, { emphasize = false, showApprove = false } = {}) => {
                  const isApproved = intent.overallStatus !== 'PENDING_APPROVAL';
                  const isApproving = approvingIntentId === intent.id;
                  const approvalsProgress = intent.required > 0
                    ? Math.min((intent.approvals / intent.required) * 100, 100)
                    : 0;
                  return (
                    <div
                      key={intent.id}
                      className={`intent-card ${cardToneFor(intent.overallStatus)}${emphasize ? ' intent-card--priority' : ''}`}
                      onClick={() => openIntentModal(intent)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openIntentModal(intent)}
                    >
                      <div className="intent-header">
                        <h3>{intent.title}</h3>
                        <div className="approval-status">
                          {intent.approvals} / {intent.required} approvals
                        </div>
                      </div>
                      <div className="intent-progress">
                        <div className="intent-progress__bar">
                          <div className="intent-progress__fill" style={{ width: `${approvalsProgress}%` }} />
                        </div>
                        <span className="intent-progress__label">
                          {intent.approvals} of {intent.required} approvals
                        </span>
                      </div>
                      <div className="intent-details compact">
                        <p><strong>Status:</strong> {statusLabelForOverall(intent.overallStatus)}</p>
                        {intent.environment && intent.environment !== 'pending-approval' ? (
                          <p><strong>Target:</strong> {intent.environment}</p>
                        ) : (
                          <p><strong>Policy:</strong> {intent.policy}</p>
                        )}
                        <p><strong>Approvals:</strong> {intent.approvals} / {intent.required} required</p>
                      </div>
                      <div className="intent-footer">
                        <span className="muted">Created {intent.created.toLocaleDateString()}</span>
                      </div>
                      <div className="intent-actions">
                        {showApprove ? (
                          approveEnabled && authorityTypes.length > 0 ? (
                            <button
                              className="approve-button primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                startApproval(intent.id);
                              }}
                              disabled={isApproving || isApproved}
                            >
                              {isApproving ? 'Approving...' : isApproved ? 'Already Approved' : 'Approve & Sign'}
                            </button>
                          ) : (
                            <p className="no-authority-message">
                              {isLoggedIn
                                ? 'Link your signer to approve this deployment.'
                                : 'Sign in to view and approve deployments.'}
                            </p>
                          )
                        ) : (
                          <button
                            type="button"
                            className="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openIntentModal(intent);
                            }}
                          >
                            Details
                          </button>
                        )}
                      </div>
                    </div>
                  );
                };

                const renderSectionList = (items, { emptyLabel, emphasize, showApprove } = {}) => (
                  items.length === 0 ? (
                    <p className="no-pending">{emptyLabel}</p>
                  ) : (
                    <div className="intent-list">
                      {items.map((intent) => renderIntentCard(intent, { emphasize, showApprove }))}
                    </div>
                  )
                );

                return (
                  <div className="approval-board">
                    <ExpandableSection title={`Needs your approval (${needsMyApprovalIntents.length})`} defaultOpen>
                      {renderSectionList(needsMyApprovalIntents, {
                        emptyLabel: 'No approvals need your action.',
                        emphasize: true,
                        showApprove: true
                      })}
                    </ExpandableSection>

                    <ExpandableSection title={`Waiting on others (${waitingOnOthersIntents.length})`}>
                      {renderSectionList(waitingOnOthersIntents, {
                        emptyLabel: 'No pending approvals waiting on others.'
                      })}
                    </ExpandableSection>

                    <ExpandableSection title={`Approved, pending deployment (${approvedNotExecutedIntents.length})`}>
                      {renderSectionList(approvedNotExecutedIntents, {
                        emptyLabel: 'No approved deployments pending execution.'
                      })}
                    </ExpandableSection>

                    <ExpandableSection title={`Executed (${executedIntents.length})`}>
                      {renderSectionList(executedIntents, {
                        emptyLabel: 'No executed deployments yet.'
                      })}
                    </ExpandableSection>

                    <ExpandableSection title={`Expired (${expiredIntents.length})`}>
                      {renderSectionList(expiredIntents, {
                        emptyLabel: 'No expired requests.'
                      })}
                    </ExpandableSection>
                  </div>
                );
              })()}
            </div>
              </>
            ) : showGovernanceSetup || showGovernanceProcess ? (
              authLoading ? (
                <div className="gov-card">
                  <h3>Loading session…</h3>
                  <p className="panel-hint">Finishing sign-in and preparing governance data.</p>
                </div>
              ) : showGovernanceSetup ? (
                renderGovernanceSetup()
              ) : (
                renderGovernanceProcess()
              )
            ) : (
              <div className="governance-console">
                <section className="console-hero">
                  <h2>Governance Console</h2>
                  {canAccessGovernance && (
                    <p>Tenant: {sessionInfo?.tenant?.domain || '—'}</p>
                  )}
                </section>
                {!showGovernanceWizard && councilIsActive && (
                  <div className="banner success">
                    <strong>Council active ✅</strong>
                    <span> Formation history is view-only. </span>
                    <a href="/governance/setup" className="banner-link">View formation history</a>
                  </div>
                )}
                {showGovernanceWizard ? (
                  renderGovernanceWizard()
                ) : !isLoggedIn ? (
                  <div className="panel-section">
                    <p className="panel-hint">Sign in to access governance controls.</p>
                    <button className="connect-button" onClick={startOidcLogin}>
                      Sign in with {oidcProvider === 'google' ? 'Google' : 'OIDC'}
                    </button>
                  </div>
                ) : !councilIsActive ? (
                  <div className="panel-section">
                    <p className="panel-hint">Council setup is still in progress. Redirecting to setup…</p>
                    <button className="connect-button" onClick={() => navigateTo('/governance/setup')}>
                      Open setup
                    </button>
                  </div>
                ) : !canAccessGovernance ? (
                  <div className="panel-section">
                    <p className="panel-hint">Access denied. Governance membership required.</p>
                  </div>
                ) : governanceLoading ? (
                  <p>Loading governance data…</p>
                ) : (
                  <div className="governance-shell">
                    <datalist id="tenant-users-list">
                      {tenantUsers.map((user) => (
                        <option key={user.id} value={user.email || user.displayName || user.id} />
                      ))}
                    </datalist>
                    <nav className="governance-nav">
                      {[
                        { id: 'overview', label: 'Overview' },
                        { id: 'members', label: 'Members' },
                        { id: 'roles', label: 'Roles' },
                        { id: 'policies', label: 'Policies' },
                        { id: 'integrations', label: 'Integrations' },
                        { id: 'activity', label: 'Activity Log' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`governance-nav__item${governanceTab === item.id ? ' is-active' : ''}`}
                          onClick={() => setGovernanceTab(item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </nav>
                    <div className="governance-content">
                      {governanceTab === 'overview' && (
                        <>
                          <div className="summary-tiles">
                            <div className="summary-tile">
                              <span className="summary-label">Members</span>
                              <span className="summary-value">{governanceMembers.length}</span>
                            </div>
                            <div className="summary-tile">
                              <span className="summary-label">Roles</span>
                              <span className="summary-value">{governanceRoles.length}</span>
                            </div>
                            <div className="summary-tile">
                              <span className="summary-label">Policies</span>
                              <span className="summary-value">{governancePolicies.length}</span>
                            </div>
                            <div className="summary-tile">
                              <span className="summary-label">Pending actions</span>
                              <span className="summary-value">{governanceActions.length}</span>
                            </div>
                            <div className="summary-tile">
                              <span className="summary-label">Integrations</span>
                              <span className="summary-value">{activeIntegrationCount}/{integrationInstalls.length}</span>
                            </div>
                            <div className="summary-tile">
                              <span className="summary-label">GitLab triggers</span>
                              <span className="summary-value">{integrationTriggers.length}</span>
                            </div>
                          </div>
                          <div className="panel-section">
                            <h4>Integrations truth</h4>
                            <ul className="gov-list">
                              <li>Providers: {integrationProviders.length ? integrationProviders.join(', ') : '—'}</li>
                              <li>Token providers: {tokenProviderList.length ? tokenProviderList.join(', ') : '—'}</li>
                              <li>Tokens (selected provider): {tokenProvider ? integrationTokens.length : '—'}</li>
                            </ul>
                          </div>
                          <div className="panel-section">
                            <h4>Recent Activity</h4>
                            <ul className="gov-list">
                              {(governanceAuditLogs || []).slice(0, 6).map((log, index) => (
                                <li key={`${log.actionId}-${index}`}>
                                  <span title={log.actionId || ''}>
                                    {humanizeEventType(log.eventType || log.type)} by {resolveUserLabel({
                                      userId: log.actorUserId || log.userId,
                                      email: log.actorEmail || log.email,
                                      walletAddress: log.actorWallet || log.walletAddress
                                    }, tenantUsers)} · {new Date(log.createdAt).toLocaleString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      )}
                      {governanceTab === 'members' && (
                        <div className="governance-grid">
                          <div className="panel-section">
                            <h4>Governance Council</h4>
                            <p className="panel-hint">
                              Mode: {sessionInfo?.governance?.mode || '—'} · Members {sessionInfo?.governance?.quorumN || '—'} · Quorum {sessionInfo?.governance?.quorumM || '—'}-of-{sessionInfo?.governance?.quorumN || '—'}
                            </p>
                            <ul className="gov-list">
                              {governanceMembers.length === 0 ? (
                                <li>No members found.</li>
                              ) : (
                                governanceMembers.map((member) => (
                                  <li key={member.id}>
                                    <span title={member.userId || member.id || ''}>
                                      {member.email || member.displayName || 'Council member'}
                                    </span>
                                  </li>
                                ))
                              )}
                            </ul>
                            <div className="gov-form">
                              <input
                                className="gov-input"
                                list="tenant-users-list"
                                value={govMemberQuery}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setGovMemberQuery(value);
                                  const match = resolveUserFromQuery(value);
                                  setGovMemberId(match ? match.id : '');
                                }}
                                placeholder="Select member"
                              />
                              <button
                                className="connect-button"
                                onClick={() => proposeGovernanceAction('ADD_GC_MEMBER', { userId: govMemberId })}
                                disabled={!govMemberId}
                              >
                                Propose add
                              </button>
                            </div>
                            <div className="gov-form">
                              <input
                                className="gov-input"
                                list="tenant-users-list"
                                value={govRemoveQuery}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setGovRemoveQuery(value);
                                  const match = resolveUserFromQuery(value);
                                  setGovRemoveMemberId(match ? match.id : '');
                                }}
                                placeholder="Select member"
                              />
                              <button
                                className="connect-button secondary"
                                onClick={() => proposeGovernanceAction('REMOVE_GC_MEMBER', { userId: govRemoveMemberId })}
                                disabled={!govRemoveMemberId}
                              >
                                Propose remove
                              </button>
                            </div>
                            <div className="gov-form">
                              <input
                                className="gov-input"
                                value={govQuorumM}
                                onChange={(event) => setGovQuorumM(event.target.value)}
                                placeholder="Quorum M"
                              />
                              <button
                                className="connect-button secondary"
                                onClick={() => proposeGovernanceAction('SET_GC_QUORUM', { quorumM: Number(govQuorumM) })}
                              >
                                Propose quorum
                              </button>
                            </div>
                          </div>
                          <div className="panel-section">
                            <h4>Pending member actions</h4>
                            {renderWizardActions(wizardActionsFor(['ADD_GC_MEMBER', 'REMOVE_GC_MEMBER', 'SET_GC_QUORUM']))}
                          </div>
                        </div>
                      )}
                      {governanceTab === 'roles' && (
                        <div className="governance-grid">
                          <div className="panel-section">
                            <h4>Roles</h4>
                            <ul className="gov-list">
                              {governanceRoles.length === 0 ? (
                                <li>No roles defined.</li>
                              ) : (
                                governanceRoles.map((role) => (
                                  <li key={role.id}>
                                    <span title={role.id}>
                                      {humanizeRoleId(role.id, governanceRoles)} · {role.status}
                                    </span>
                                  </li>
                                ))
                              )}
                            </ul>
                            <div className="gov-form stack">
                              <input
                                className="gov-input"
                                placeholder="Role name"
                                value={roleName}
                                onChange={(event) => setRoleName(event.target.value)}
                              />
                              <input
                                className="gov-input"
                                placeholder="Description (optional)"
                                value={roleDescription}
                                onChange={(event) => setRoleDescription(event.target.value)}
                              />
                              <button
                                className="connect-button"
                                onClick={() => proposeGovernanceAction('CREATE_ROLE', { name: roleName, description: roleDescription })}
                                disabled={!roleName.trim()}
                              >
                                Propose role
                              </button>
                            </div>
                          </div>
                          <div className="panel-section">
                            <h4>Role Assignments</h4>
                            <ul className="gov-list">
                              {governanceRoleAssignments.length === 0 ? (
                                <li>No role assignments.</li>
                              ) : (
                                governanceRoleAssignments.map((assignment) => {
                                  const user = tenantUsers.find((entry) => entry.id === assignment.userId);
                                  const label = user?.email || user?.displayName || 'Council member';
                                  return (
                                    <li key={assignment.id}>
                                      <span title={assignment.userId || ''}>{label}</span> · <span title={assignment.roleId}>{humanizeRoleId(assignment.roleId, governanceRoles)}</span>
                                    </li>
                                  );
                                })
                              )}
                            </ul>
                            <div className="gov-form">
                              <input
                                className="gov-input"
                                list="tenant-users-list"
                                value={assignmentUserQuery}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setAssignmentUserQuery(value);
                                  const match = resolveUserFromQuery(value);
                                  setAssignmentUserId(match ? match.id : '');
                                }}
                                placeholder="Select user"
                              />
                              <select
                                className="gov-input"
                                value={assignmentRoleId}
                                onChange={(event) => setAssignmentRoleId(event.target.value)}
                              >
                                <option value="">Select role</option>
                                {roleOptionsList.map((role) => (
                                  <option key={role.value} value={role.value}>
                                    {role.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="connect-button"
                                onClick={() => proposeGovernanceAction('ASSIGN_ROLE', { userId: assignmentUserId, roleId: assignmentRoleId })}
                                disabled={!assignmentUserId || !assignmentRoleId}
                              >
                                Propose assign
                              </button>
                            </div>
                          </div>
                          <div className="panel-section">
                            <h4>Legacy On-Chain Approvers</h4>
                            <p className="panel-hint">Revocations require quorum and will revoke on-chain approver rights.</p>
                            <div className="gov-form">
                              <input
                                className="gov-input"
                                placeholder="Scan wallet address"
                                value={legacyScanWallet}
                                onChange={(event) => setLegacyScanWallet(event.target.value)}
                              />
                              <button
                                className="connect-button secondary"
                                onClick={scanLegacyAuthority}
                                disabled={!legacyScanWallet.trim()}
                              >
                                Scan wallet
                              </button>
                            </div>
                            <ul className="gov-list">
                              {legacyAuthorityAssignments.length === 0 ? (
                                <li>No legacy approvers recorded.</li>
                              ) : (
                                legacyAuthorityAssignments.map((assignment) => (
                                  <li key={assignment.id}>
                                    <span title={assignment.walletAddress || ''}>
                                      {(assignment.userEmail || assignment.userId || 'Wallet on record')} · {authorityLabelFor(assignment.authority)}
                                    </span>
                                    <button
                                      className="connect-button secondary small"
                                      onClick={() => proposeGovernanceAction('REVOKE_AUTHORITY', {
                                        userId: assignment.userId,
                                        walletAddress: assignment.walletAddress,
                                        authority: assignment.authority
                                      })}
                                      disabled={governanceSubmitting === 'REVOKE_AUTHORITY'}
                                    >
                                      Propose revoke
                                    </button>
                                  </li>
                                ))
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                      {governanceTab === 'policies' && (
                        <div className="governance-grid">
                          <div className="panel-section">
                            <h4>Approval Policies</h4>
                            <ul className="gov-list">
                              {governancePolicies.length === 0 ? (
                                <li>No approval policies configured.</li>
                              ) : (
                                governancePolicies.map((policy) => (
                                  <li key={policy.id}>
                                    <span title={policy.id}>
                                      {humanizePolicyId(policy.id, governancePolicies)} · {policy.status}
                                    </span>
                                  </li>
                                ))
                              )}
                            </ul>
                            <div className="panel-subsection">
                              <h5>Active policy bindings</h5>
                              <ul className="gov-list">
                                {governancePolicyBindings.length === 0 ? (
                                  <li>No active bindings.</li>
                                ) : (
                                  governancePolicyBindings.map((binding) => {
                                    const policy = governancePolicies.find((entry) => entry.id === binding.policyId);
                                    const policyLabel = policy?.name || humanizePolicyId(binding.policyId, governancePolicies);
                                    const bindingLabel = binding.provider
                                      ? `${binding.provider}:${binding.resourceType || 'resource'}:${binding.resourceId || '—'}`
                                      : binding.targetRef;
                                    return (
                                      <li key={binding.id}>
                                        <span title={binding.policyId}>
                                          {policyLabel} · {bindingLabel} · {binding.envScope}
                                        </span>
                                      </li>
                                    );
                                  })
                                )}
                              </ul>
                            </div>
                            <div className="gov-form stack">
                              <input
                                className="gov-input"
                                placeholder="Policy name"
                                value={policyName}
                                onChange={(event) => setPolicyName(event.target.value)}
                              />
                              <input
                                className="gov-input"
                                placeholder="Description (optional)"
                                value={policyDescription}
                                onChange={(event) => setPolicyDescription(event.target.value)}
                              />
                              <div className="gov-checkboxes">
                                {roleOptionsList.map((role) => (
                                  <label key={role.value} className="gov-checkbox" title={role.description || role.label}>
                                    <input
                                      type="checkbox"
                                      checked={policyRoleIds.includes(role.value)}
                                      onChange={(event) => {
                                        if (event.target.checked) {
                                          setPolicyRoleIds((prev) => [...prev, role.value]);
                                        } else {
                                          setPolicyRoleIds((prev) => prev.filter((id) => id !== role.value));
                                        }
                                      }}
                                    />
                                    <span>{role.label}</span>
                                  </label>
                                ))}
                              </div>
                              <select
                                className="gov-input"
                                value={policyScopeEnv}
                                onChange={(event) => setPolicyScopeEnv(event.target.value)}
                              >
                                <option value="ALL">Scope: All environments</option>
                                <option value="PROD">Scope: Production</option>
                                <option value="STAGING">Scope: Staging</option>
                              </select>
                              <button
                                className="connect-button"
                                onClick={() => proposeGovernanceAction('CREATE_POLICY', {
                                  name: policyName,
                                  description: policyDescription,
                                  requiredRoleIds: policyRoleIds,
                                  scopeEnv: policyScopeEnv
                                })}
                                disabled={!policyName.trim() || policyRoleIds.length === 0}
                              >
                                Propose policy
                              </button>
                            </div>
                            <div className="gov-form">
                              <select
                                className="gov-input"
                                value={policyDraftId}
                                onChange={(event) => setPolicyDraftId(event.target.value)}
                              >
                                <option value="">Select draft policy</option>
                                {governancePolicies.filter((policy) => policy.status === 'DRAFT').map((policy) => (
                                  <option key={policy.id} value={policy.id}>
                                    {policy.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="connect-button secondary"
                                onClick={() => proposeGovernanceAction('ACTIVATE_POLICY', { policyId: policyDraftId })}
                                disabled={!policyDraftId}
                              >
                                Propose activate
                              </button>
                            </div>
                            <div className="gov-form">
                              <select
                                className="gov-input"
                                value={policyActiveId}
                                onChange={(event) => setPolicyActiveId(event.target.value)}
                              >
                                <option value="">Select active policy</option>
                                {governancePolicies.filter((policy) => policy.status === 'ACTIVE').map((policy) => (
                                  <option key={policy.id} value={policy.id}>
                                    {policy.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="gov-input"
                                value={policyBindingMode}
                                onChange={(event) => setPolicyBindingMode(event.target.value)}
                              >
                                <option value="legacy">Binding: Legacy target_ref</option>
                                <option value="provider">Binding: Provider resource</option>
                              </select>
                              {policyBindingMode === 'provider' ? (
                                <>
                                  <div className="gov-form">
                                    <span className="panel-hint">Quick fill:</span>
                                    {policyBindingPresets.map((preset) => (
                                      <button
                                        key={preset.id}
                                        className="connect-button secondary small"
                                        type="button"
                                        onClick={() => {
                                          setPolicyBindingMode('provider');
                                          setPolicyBindingProvider(preset.provider);
                                          setPolicyBindingResourceType(preset.resourceType);
                                          if (preset.resourceId) {
                                            setPolicyBindingResourceId(preset.resourceId);
                                          }
                                        }}
                                      >
                                        {preset.label}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    className="gov-input"
                                    placeholder="Provider (e.g., gitlab)"
                                    value={policyBindingProvider}
                                    onChange={(event) => setPolicyBindingProvider(event.target.value)}
                                  />
                                  <p className="panel-hint">Use the integration provider id (e.g., gitlab or databridge).</p>
                                  <input
                                    className="gov-input"
                                    placeholder="Resource type (e.g., gitlab_project)"
                                    value={policyBindingResourceType}
                                    onChange={(event) => setPolicyBindingResourceType(event.target.value)}
                                  />
                                  <p className="panel-hint">Use a namespaced resource type (e.g., databridge_dataset).</p>
                                  <input
                                    className="gov-input"
                                    placeholder="Resource id (e.g., GitLab project id)"
                                    value={policyBindingResourceId}
                                    onChange={(event) => setPolicyBindingResourceId(event.target.value)}
                                  />
                                  <p className="panel-hint">Match the resource id used by the integration (e.g., customers_pii).</p>
                                  <textarea
                                    className="gov-input"
                                    placeholder="Scopes JSON (optional)"
                                    rows={3}
                                    value={policyBindingScopesText}
                                    onChange={(event) => setPolicyBindingScopesText(event.target.value)}
                                  />
                                </>
                              ) : (
                                <input
                                  className="gov-input"
                                  placeholder="Target reference (e.g., gitlab:group/acme)"
                                  value={policyTargetRef}
                                  onChange={(event) => setPolicyTargetRef(event.target.value)}
                                />
                              )}
                              <select
                                className="gov-input"
                                value={policyTargetEnv}
                                onChange={(event) => setPolicyTargetEnv(event.target.value)}
                              >
                                <option value="ALL">Environment: All</option>
                                <option value="PROD">Environment: Production</option>
                                <option value="STAGING">Environment: Staging</option>
                              </select>
                              <button
                                className="connect-button secondary"
                                onClick={() => {
                                  if (policyBindingMode === 'provider') {
                                    const scopes = parseJsonInput(policyBindingScopesText);
                                    if (policyBindingScopesText.trim() && !scopes) {
                                      showToast('Invalid scopes JSON.', 'error');
                                      return;
                                    }
                                    proposeGovernanceAction('BIND_POLICY', {
                                      policyId: policyActiveId,
                                      envScope: policyTargetEnv,
                                      provider: policyBindingProvider.trim(),
                                      resourceType: policyBindingResourceType.trim(),
                                      resourceId: policyBindingResourceId.trim(),
                                      scopes
                                    });
                                    return;
                                  }
                                  proposeGovernanceAction('BIND_POLICY', {
                                    policyId: policyActiveId,
                                    targetRef: policyTargetRef,
                                    envScope: policyTargetEnv
                                  });
                                }}
                                disabled={!policyActiveId || (policyBindingMode === 'provider'
                                  ? !policyBindingProvider.trim() || !policyBindingResourceType.trim() || !policyBindingResourceId.trim()
                                  : !policyTargetRef.trim())}
                              >
                                Propose bind
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {governanceTab === 'integrations' && (
                        <div className="governance-grid">
                          {!councilIsActive ? (
                            <div className="panel-section">
                              <h4>Integrations</h4>
                              <p className="panel-hint">Integrations are available once the council is ACTIVE.</p>
                            </div>
                          ) : (
                            <>
                              <div className="panel-section">
                                <div className="panel-title-row">
                                  <h4>GitLab Integration</h4>
                                  <div className="panel-actions">
                                    <span className={`status-badge ${gitlabStatusClass}`}>{gitlabStatusLabel}</span>
                                    <button
                                      type="button"
                                      className="panel-toggle"
                                      onClick={() => setShowGitlabIntegration((prev) => !prev)}
                                    >
                                      {showGitlabIntegration ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                </div>
                                {showGitlabIntegration ? (
                                  <>
                                    <ul className="gov-list">
                                      <li>Instance URL: {gitlabInstall?.externalOrgRef || '—'}</li>
                                      <li>Auth mode: {gitlabInstall?.authMode || 'oidc'}</li>
                                      <li>Audiences: {Array.isArray(gitlabInstall?.audiences) && gitlabInstall.audiences.length ? gitlabInstall.audiences.join(', ') : '—'}</li>
                                      <li>Issuers: {Array.isArray(gitlabInstall?.issuers) && gitlabInstall.issuers.length ? gitlabInstall.issuers.join(', ') : '—'}</li>
                                    </ul>
                                    <div className="gov-form stack">
                                      <input
                                        className="gov-input"
                                        placeholder="GitLab instance URL"
                                        value={integrationExternalOrgRef}
                                        onChange={(event) => setIntegrationExternalOrgRef(event.target.value)}
                                      />
                                      <select
                                        className="gov-input"
                                        value={integrationAuthMode}
                                        onChange={(event) => setIntegrationAuthMode(event.target.value)}
                                      >
                                        <option value="oidc">OIDC (GitLab JWT)</option>
                                      </select>
                                      <textarea
                                        className="gov-input"
                                        placeholder="Issuers (one per line)"
                                        rows={3}
                                        value={integrationIssuersText}
                                        onChange={(event) => setIntegrationIssuersText(event.target.value)}
                                      />
                                      <textarea
                                        className="gov-input"
                                        placeholder="Audiences (one per line)"
                                        rows={2}
                                        value={integrationAudiencesText}
                                        onChange={(event) => setIntegrationAudiencesText(event.target.value)}
                                      />
                                      <textarea
                                        className="gov-input"
                                        placeholder="Scopes JSON (optional)"
                                        rows={3}
                                        value={integrationScopesText}
                                        onChange={(event) => setIntegrationScopesText(event.target.value)}
                                      />
                                      <div className="gov-form">
                                        <button
                                          className="connect-button"
                                          onClick={() => {
                                            const scopes = parseJsonInput(integrationScopesText);
                                            if (integrationScopesText.trim() && !scopes) {
                                              showToast('Invalid scopes JSON.', 'error');
                                              return;
                                            }
                                            proposeGovernanceAction('CONFIGURE_INTEGRATION', {
                                              provider: 'gitlab',
                                              authMode: integrationAuthMode,
                                              externalOrgRef: integrationExternalOrgRef,
                                              issuers: parseListInput(integrationIssuersText),
                                              audiences: parseListInput(integrationAudiencesText),
                                              scopes,
                                              status: integrationStatus
                                            });
                                          }}
                                        >
                                          Propose register/update
                                        </button>
                                        <button
                                          className="connect-button secondary"
                                          onClick={() => proposeGovernanceAction('ENABLE_INTEGRATION', { provider: 'gitlab' })}
                                        >
                                          Propose enable
                                        </button>
                                        <button
                                          className="connect-button secondary"
                                          onClick={() => proposeGovernanceAction('DISABLE_INTEGRATION', { provider: 'gitlab' })}
                                        >
                                          Propose disable
                                        </button>
                                      </div>
                                    </div>
                                    <div className="panel-subsection">
                                      <div className="panel-title-row">
                                        <h5>Install snippet</h5>
                                        <button
                                          className="connect-button secondary"
                                          onClick={() => copyToClipboard(gitlabInstallSnippet)}
                                        >
                                          Copy snippet
                                        </button>
                                      </div>
                                      <p className="panel-hint">Use this snippet to create intents from GitLab. Approvals happen in the Ops Console.</p>
                                      <pre className="code-block">{gitlabInstallSnippet}</pre>
                                    </div>
                                    <div className="panel-subsection">
                                      <div className="panel-title-row">
                                        <h5>Execution trigger</h5>
                                        <span className="status-badge gray">Governed</span>
                                      </div>
                                      <p className="panel-hint">Required for automatic execution after quorum approval.</p>
                                      <div className="gov-form stack">
                                        <select
                                          className="gov-input"
                                          value={triggerScopeType}
                                          onChange={(event) => setTriggerScopeType(event.target.value)}
                                        >
                                          <option value="gitlab_project">Scope: GitLab project</option>
                                          <option value="gitlab_group">Scope: GitLab group</option>
                                        </select>
                                        <input
                                          className="gov-input"
                                          placeholder="Scope id (GitLab project or group numeric id)"
                                          value={triggerScopeId}
                                          onChange={(event) => setTriggerScopeId(event.target.value)}
                                        />
                                        <input
                                          className="gov-input"
                                          placeholder="Trigger token (api scope)"
                                          value={triggerToken}
                                          onChange={(event) => setTriggerToken(event.target.value)}
                                        />
                                        <input
                                          className="gov-input"
                                          placeholder="Pipeline ref (optional)"
                                          value={triggerRef}
                                          onChange={(event) => setTriggerRef(event.target.value)}
                                        />
                                        <textarea
                                          className="gov-input"
                                          placeholder="Variables JSON (optional)"
                                          rows={3}
                                          value={triggerVariablesText}
                                          onChange={(event) => setTriggerVariablesText(event.target.value)}
                                        />
                                        <div className="gov-form">
                                          <button
                                            className="connect-button"
                                            onClick={() => {
                                              const rawVariables = parseJsonInput(triggerVariablesText);
                                              const variables = normalizeTriggerVariables(rawVariables);
                                              if (triggerVariablesText.trim() && !variables) {
                                                showToast('Invalid variables JSON.', 'error');
                                                return;
                                              }
                                              proposeGovernanceAction('CONFIGURE_INTEGRATION_TRIGGER', {
                                                provider: 'gitlab',
                                                scopeType: triggerScopeType,
                                                scopeId: triggerScopeId.trim(),
                                                token: triggerToken.trim(),
                                                ref: triggerRef.trim() || null,
                                                variables: variables || null
                                              });
                                            }}
                                            disabled={!triggerScopeId.trim() || !triggerToken.trim()}
                                          >
                                            Propose configure trigger
                                          </button>
                                          <button
                                            className="connect-button secondary"
                                            onClick={() => proposeGovernanceAction('DISABLE_INTEGRATION_TRIGGER', {
                                              provider: 'gitlab',
                                              scopeType: triggerScopeType,
                                              scopeId: triggerScopeId.trim()
                                            })}
                                            disabled={!triggerScopeId.trim()}
                                          >
                                            Propose disable trigger
                                          </button>
                                        </div>
                                      </div>
                                      <ul className="gov-list">
                                        {integrationTriggers.length === 0 ? (
                                          <li>No triggers configured.</li>
                                        ) : (
                                          integrationTriggers.map((trigger) => (
                                            <li key={trigger.id}>
                                              <span>{trigger.scopeType}:{trigger.scopeId}</span>
                                              {' '}· {trigger.status || 'ACTIVE'}
                                              {trigger.ref ? ` · ref ${trigger.ref}` : ''}
                                            </li>
                                          ))
                                        )}
                                      </ul>
                                    </div>
                                  </>
                                ) : (
                                  <p className="panel-hint">Hidden to keep the Integrations view lightweight.</p>
                                )}
                              </div>
                              <div className="panel-section">
                                <div className="panel-title-row">
                                  <h4>Token Integrations</h4>
                                  <div className="panel-actions">
                                    <span className={`status-badge ${tokenStatusValue === 'ACTIVE' ? 'success' : tokenStatusValue === 'DISABLED' ? 'amber' : 'gray'}`}>
                                      {tokenStatusValue === 'ACTIVE' ? 'Enabled' : tokenStatusValue === 'DISABLED' ? 'Disabled' : 'Not configured'}
                                    </span>
                                    <button
                                      type="button"
                                      className="panel-toggle"
                                      onClick={() => setShowTokenIntegration((prev) => !prev)}
                                    >
                                      {showTokenIntegration ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                </div>
                                {showTokenIntegration ? (
                                  <>
                                    <div className="gov-form">
                                      {tokenInstalls.length === 0 ? (
                                        <p className="panel-hint">No token integrations configured yet.</p>
                                      ) : (
                                        tokenInstalls.map((install) => (
                                          <button
                                            key={install.id}
                                            className="connect-button secondary"
                                            type="button"
                                            onClick={() => setTokenProvider(install.provider)}
                                          >
                                            {install.provider} · {install.status || 'ACTIVE'}
                                          </button>
                                        ))
                                      )}
                                    </div>
                                    <div className="gov-form">
                                      <input
                                        className="gov-input"
                                        placeholder="Provider id (e.g., falcondive)"
                                        value={tokenProvider}
                                        onChange={(event) => setTokenProvider(event.target.value.trim())}
                                      />
                                      <button
                                        className="connect-button"
                                        onClick={() => proposeGovernanceAction('CONFIGURE_INTEGRATION', {
                                          provider: tokenProvider,
                                          authMode: 'token',
                                          status: 'ACTIVE'
                                        })}
                                        disabled={!tokenProvider}
                                      >
                                        Propose register/update
                                      </button>
                                      <button
                                        className="connect-button secondary"
                                        onClick={() => proposeGovernanceAction('ENABLE_INTEGRATION', { provider: tokenProvider })}
                                        disabled={!tokenProvider}
                                      >
                                        Propose enable
                                      </button>
                                      <button
                                        className="connect-button secondary"
                                        onClick={() => proposeGovernanceAction('DISABLE_INTEGRATION', { provider: tokenProvider })}
                                        disabled={!tokenProvider}
                                      >
                                        Propose disable
                                      </button>
                                    </div>
                                    <div className="gov-form stack">
                                      <input
                                        className="gov-input"
                                        placeholder="Token name (optional)"
                                        value={tokenName}
                                        onChange={(event) => setTokenName(event.target.value)}
                                      />
                                      <textarea
                                        className="gov-input"
                                        placeholder="Token scopes JSON (optional)"
                                        rows={3}
                                        value={tokenScopesText}
                                        onChange={(event) => setTokenScopesText(event.target.value)}
                                      />
                                      <input
                                        className="gov-input"
                                        placeholder="Expires at (ISO timestamp, optional)"
                                        value={tokenExpiryText}
                                        onChange={(event) => setTokenExpiryText(event.target.value)}
                                      />
                                      <button
                                        className="connect-button"
                                        onClick={() => {
                                          const scopes = parseJsonInput(tokenScopesText);
                                          if (tokenScopesText.trim() && !scopes) {
                                            showToast('Invalid token scopes JSON.', 'error');
                                            return;
                                          }
                                          proposeGovernanceAction('CREATE_INTEGRATION_TOKEN', {
                                            provider: tokenProvider,
                                            name: tokenName || null,
                                            scopes: scopes || null,
                                            expiresAt: tokenExpiryText || null
                                          });
                                        }}
                                        disabled={!tokenProvider}
                                      >
                                        Propose create token
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <p className="panel-hint">Hidden to keep the Integrations view lightweight.</p>
                                )}
                                {tokenCreatedActionId && tokenCreatedValue && (
                                  <div className="panel-hint">
                                    Token created. View it in action details.
                                    <button
                                      className="connect-button secondary"
                                      onClick={() => loadGovernanceActionDetails(tokenCreatedActionId)}
                                    >
                                      View action
                                    </button>
                                  </div>
                                )}
                                <ul className="gov-list">
                                  {integrationTokens.length === 0 ? (
                                    <li>No tokens for this provider.</li>
                                  ) : (
                                    integrationTokens.map((token) => (
                                      <li key={token.id}>
                                        <span title={token.id}>{token.name || token.id}</span>
                                        {' '}· Expires {token.expires_at || '—'}
                                        {' '}· Revoked {token.revoked_at ? 'Yes' : 'No'}
                                        <button
                                          className="connect-button secondary"
                                          onClick={() => proposeGovernanceAction('REVOKE_INTEGRATION_TOKEN', { provider: tokenProvider, tokenId: token.id })}
                                          disabled={!!token.revoked_at}
                                        >
                                          Propose revoke
                                        </button>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              </div>
                              <div className="panel-section">
                                <div className="panel-title-row">
                                  <h4>Token integration handoff</h4>
                                  <button
                                    className="connect-button secondary"
                                    onClick={() => copyToClipboard(tokenIntegrationHandoff)}
                                  >
                                    Copy guide
                                  </button>
                                </div>
                                <p className="panel-hint">Share this guide with any third-party integrating via token auth.</p>
                                <pre className="code-block">{tokenIntegrationHandoff}</pre>
                                {tokenProvider === 'databridge' && (
                                  <>
                                    <p className="panel-hint">DataBridge demo app (optional): uses config.json or URL params.</p>
                                    <div className="panel-actions">
                                      <button
                                        className="connect-button secondary"
                                        onClick={() => copyToClipboard(databridgeConfigJson)}
                                      >
                                        Copy config.json
                                      </button>
                                      <button
                                        className="connect-button secondary"
                                        onClick={() => downloadTextFile('config.json', databridgeConfigJson)}
                                      >
                                        Download config.json
                                      </button>
                                    </div>
                                    <pre className="code-block">{databridgeConfigJson}</pre>
                                  </>
                                )}
                              </div>
                              <div className="panel-section">
                                <div className="panel-title-row">
                                  <h4>Integration proposals</h4>
                                  <button
                                    type="button"
                                    className="panel-toggle"
                                    onClick={() => setShowIntegrationActions((prev) => !prev)}
                                  >
                                    {showIntegrationActions ? 'Hide' : 'Show'}
                                  </button>
                                </div>
                                {showIntegrationActions ? (
                                  renderWizardActions(governanceActionsAll.filter((action) => (
                                    ['CONFIGURE_INTEGRATION', 'CREATE_INTEGRATION_TOKEN', 'REVOKE_INTEGRATION_TOKEN', 'ENABLE_INTEGRATION', 'DISABLE_INTEGRATION', 'CONFIGURE_INTEGRATION_TRIGGER', 'DISABLE_INTEGRATION_TRIGGER']
                                      .includes(action.type)
                                  )))
                                ) : (
                                  <p className="panel-hint">Hidden to keep the Integrations view lightweight.</p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {governanceTab === 'activity' && (
                        <div className="governance-grid">
                          <div className="panel-section">
                            <h4>Governance Actions</h4>
                            {renderWizardActions(governanceActionsAll)}
                          </div>
                          <div className="panel-section">
                            <h4>Governance Audit</h4>
                            <p className="panel-hint">
                              {governanceAuditLogs.length} events captured.
                            </p>
                            <button
                              type="button"
                              className="connect-button secondary"
                              onClick={() => openDrawer('events')}
                              disabled={governanceDrawerEvents.length === 0}
                            >
                              View events
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </WorkSurface>
          <RightPanel>
            <div className="panel-section">
              <h3>Control Plane</h3>
              <p className="panel-muted">System truth and evidence services</p>
            </div>

            <div className="panel-section">
              <h4>Session</h4>
              {activeConsole === 'governance' ? (
                <>
                  <div className="panel-row prominent">
                    <span className="panel-label">
                      <span className="panel-icon">🛡️</span>
                      Can govern
                    </span>
                    <span className={`panel-value ${canGovern ? 'ok' : 'warn'}`}>
                      {canGovern ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="panel-row">
                    <span className="panel-label">Realtime</span>
                    <span className={`panel-value ${realtimeStatus === 'connected' ? 'ok' : 'warn'}`}>
                      {realtimeStatus === 'connected' ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                  <div className="panel-row">
                    <span className="panel-label">Can approve releases</span>
                    <span className={`panel-value ${canApproveReleases ? 'ok' : 'warn'}`}>
                      {canApproveReleases ? 'Yes' : 'No'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="panel-row prominent">
                  <span className="panel-label">
                    <span className="panel-icon">🛡️</span>
                    Can approve
                  </span>
                  <span className={`panel-value ${approveEnabled ? 'ok' : 'warn'}`}>
                    {approveEnabled ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>

            <div className="panel-section">
              <h4>Evidence Services</h4>
              <div className="status-row" title="API reachable">
                <span className="panel-label">API</span>
                <span className={`status-text ${statusTone(apiStatusLabel)}`}>{apiStatusLabel}</span>
              </div>
              <div className="status-row" title="Chain synced / RPC reachable">
                <span className="panel-label">Chain</span>
                <span className={`status-text ${statusTone(chainStatusLabel)}`}>{chainStatusLabel}</span>
              </div>
              <div className="status-row" title="IPFS gateway reachable">
                <span className="panel-label">IPFS</span>
                <span className={`status-text ${statusTone(ipfsStatusLabel)}`}>{ipfsStatusLabel}</span>
              </div>
            </div>

            <div className="panel-section">
              <h4>Evidence & Exports</h4>
              <a className="panel-button" href={apiUrl('/api/v1/intents?format=csv')} target="_blank" rel="noreferrer">
                Export Audit (CSV)
              </a>
              <button className="panel-button" type="button" disabled>
                Download Evidence Bundle
              </button>
              <p className="panel-muted">Evidence is anchored by chain tx + IPFS CIDs.</p>
            </div>

            <div className="panel-section">
              <h4>Quick Actions</h4>
              <button
                className="panel-button"
                type="button"
                disabled={awaitingIntents.length === 0}
                onClick={() => {
                  if (awaitingIntents[0]) {
                    openIntentModal(awaitingIntents[0]);
                  }
                }}
              >
                Open Latest Pending
              </button>
              <button
                className="panel-button"
                type="button"
                onClick={loadIntents}
              >
                Refresh Requests
              </button>
            </div>

            {debugAllowed && (
              <div className="panel-section">
                <div className="panel-row">
                  <h4>Debug Events</h4>
                  <button
                    type="button"
                    className="panel-toggle"
                    onClick={() => setShowControlDebug((prev) => !prev)}
                  >
                    {showControlDebug ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showControlDebug && (
                  <div className="panel-debug-list">
                    {debugEvents.length === 0 ? (
                      <div className="panel-muted">No debug events yet.</div>
                    ) : (
                      debugEvents.map((event, index) => (
                        <div key={`${event.time}-${index}`} className="panel-debug-item">
                          <span className="debug-time">{event.time}</span>
                          <span className="debug-message">{event.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </RightPanel>
        </div>
      </main>

      {approvalPrompt && (
        <div className="modal-overlay" onClick={() => setApprovalPrompt(null)}>
          <div className="modal-content approval-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve request</h3>
              <button className="modal-close" onClick={() => setApprovalPrompt(null)}>×</button>
            </div>
            <div className="modal-body">
              {(() => {
                const modalIntent = {
                  ...(releaseRecord?.intent || {}),
                  ...(approvalPrompt.intent || {})
                };
                const pipelineId = modalIntent.pipeline_id || modalIntent.pipelineId || releaseRecord?.pipeline_id || releaseRecord?.pipelineId;
                const environment = modalIntent.environment || releaseRecord?.environment || 'Deployment approval';
                const commitHash = modalIntent.commit || modalIntent.commitHash || modalIntent.commit_sha || releaseRecord?.commit;
                const policyId = modalIntent.policy_id || modalIntent.policyId || releaseRecord?.policy?.id || modalIntent.policy;
                const presentation = intentPresentation(modalIntent);
                const requiredRoles = requiredRolesForIntent({
                  ...modalIntent,
                  policy_id: policyId,
                  policy: releaseRecord?.policy || modalIntent.policy
                });
                const requiredCount = (modalIntent.required ?? modalIntent.approvals_required ?? requiredRoles.length) || 1;
                const approvalsCount = modalIntent.approvals ?? modalIntent.approvalsCount ?? 0;
                const approvalsDetail = Array.isArray(modalIntent.approvals_detail) ? modalIntent.approvals_detail : [];
                const policyLabel = releaseRecord?.policy?.display || humanizePolicyId(policyId, governancePolicies);
                const roleLabel = authorityTypes.length > 0
                  ? authorityTypes.map((role) => humanizeRoleId(role, governanceRoles)).join(', ')
                  : 'Not available';
                const pendingCount = Math.max(requiredCount - approvalsDetail.length, 0);

                const approvalRows = [
                  ...approvalsDetail.map((entry, idx) => ({
                    id: entry.id || entry.approver || idx,
                    name: resolveUserLabel({
                      userId: entry.userId || entry.user_id,
                      email: entry.email || entry.user_email,
                      walletAddress: entry.wallet_address || entry.wallet
                    }, tenantUsers),
                    status: 'Approved'
                  })),
                  ...Array.from({ length: pendingCount }).map((_, idx) => ({
                    id: `pending-${idx}`,
                    name: 'Pending approver',
                    status: 'Pending'
                  }))
                ];

                return (
                  <>
                    <InfoCard title="What you are approving">
                      <KeyValueList
                        items={[
                          { label: 'Target', value: environment },
                          presentation.isGitLab ? { label: 'Pipeline', value: pipelineId ? `#${pipelineId}` : '—' } : { label: 'Resource', value: presentation.targetLabel || '—' },
                          presentation.isGitLab
                            ? { label: 'Commit', value: commitHash ? 'Commit on record' : '—', title: commitHash || '' }
                            : { label: 'Action', value: presentation.actionLabel || '—' }
                        ]}
                      />
                    </InfoCard>

                    <InfoCard title="Why approval is required">
                      <KeyValueList
                        items={[
                          { label: 'Policy', value: policyLabel || 'Approval required' },
                          { label: 'Rule', value: describeApprovalRequirement(requiredRoles, governanceRoles) },
                          { label: 'Your role', value: roleLabel }
                        ]}
                      />
                    </InfoCard>

                    <InfoCard title="Approval progress">
                      <div className="approval-progress">
                        <span>{approvalsCount} / {requiredCount} approvals collected</span>
                        <StatusBadge
                          status={approvalsCount >= requiredCount ? 'Complete' : 'In progress'}
                          tone={approvalsCount >= requiredCount ? 'success' : 'default'}
                        />
                      </div>
                      <ul className="approval-approvers">
                        {approvalRows.map((entry) => (
                          <li key={entry.id} className="approval-approver">
                            <span>{entry.name}</span>
                            <StatusBadge
                              status={entry.status}
                              tone={entry.status === 'Approved' ? 'success' : 'muted'}
                            />
                          </li>
                        ))}
                      </ul>
                    </InfoCard>

                    <InfoCard title="What happens next">
                      <ul className="approval-next">
                        <li>Your approval is recorded securely for this request.</li>
                        <li>Other required approvers can sign.</li>
                        <li>When quorum is met, execution can proceed.</li>
                      </ul>
                    </InfoCard>

                    <button
                      type="button"
                      className="link-toggle"
                      onClick={() => {
                        setDrawerData({
                          summary: `${toTitleCase(presentation.actionLabel)} approval for request ${approvalPrompt.intentId || '—'} requires ${requiredCount} approval(s). ${approvalsCount} recorded so far.`,
                          audit: [
                            { id: 'policy', label: `policyId: ${policyId || '—'}` },
                            { id: 'roles', label: `roleIds: ${(requiredRoles || []).join(', ') || '—'}` },
                            { id: 'intent', label: `requestId: ${approvalPrompt.intentId || '—'}` },
                            { id: 'resource', label: `resource: ${presentation.targetLabel || '—'}` },
                            { id: 'action', label: `action: ${presentation.actionLabel || '—'}` }
                          ],
                          rawJson: {
                            approvalPrompt,
                            intent: modalIntent
                          },
                          copyItems: [
                            { label: 'Policy ID', value: policyId || '' },
                            { label: 'Role IDs', value: (requiredRoles || []).join(', ') },
                            { label: 'Request ID', value: approvalPrompt.intentId || '' },
                            { label: 'Resource', value: presentation.targetLabel || '' },
                            { label: 'Action', value: presentation.actionLabel || '' }
                          ]
                        });
                        openDrawer('audit');
                      }}
                    >
                      Show technical details
                    </button>
                  </>
                );
              })()}
              <div className="modal-footer">
                <button className="connect-button secondary" onClick={() => setApprovalPrompt(null)}>
                  Cancel
                </button>
                <button
                  className="connect-button"
                  onClick={confirmApproval}
                  disabled={approvingIntentId === approvalPrompt.intentId}
                >
                  {approvingIntentId === approvalPrompt.intentId ? (
                    <>
                      <span className="spinner" aria-hidden="true" />
                      Securing approval record…
                    </>
                  ) : (
                    'Approve & Sign'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {governanceDetail && (
        <div className="modal-overlay" onClick={closeGovernanceDetail}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Governance Action Details</h3>
              <button className="modal-close" onClick={closeGovernanceDetail}>×</button>
            </div>
            <div className="modal-body">
              {governanceDetailLoading ? (
                <p>Loading details…</p>
              ) : (
                <>
                  <p><strong>Action:</strong> {humanizeGovernanceActionType(governanceDetail.action?.type)}</p>
                  <p><strong>State:</strong> {governanceDetail.action?.state === 'APPLIED'
                    ? 'Applied'
                    : governanceDetail.action?.state === 'SIGNED'
                      ? 'Signed'
                      : governanceDetail.action?.state === 'PROPOSED'
                        ? 'Proposed'
                        : (governanceDetail.action?.state || '—')}
                  </p>
                  <p>
                    <strong>Payload hash:</strong>{' '}
                    <span title={governanceDetail.action?.payloadHash || ''}>
                      {governanceDetail.action?.payloadHash ? 'Recorded' : '—'}
                    </span>
                  </p>
                  <p><strong>Applied at:</strong> {governanceDetail.action?.appliedAt || '—'}</p>
                  <p>
                    <strong>Tx hash:</strong>{' '}
                    <span title={governanceDetail.action?.onChainTxHash || ''}>
                      {governanceDetail.action?.onChainTxHash ? 'Recorded' : '—'}
                    </span>
                  </p>
                  {governanceActionSecrets[governanceDetail.action?.id]?.token && (
                    <div className="panel-section">
                      <h4>Integration Token (one-time)</h4>
                      <p className="panel-hint">Copy this token now. It will not be shown again once you close this modal.</p>
                      <div className="gov-form">
                        <input
                          className="gov-input"
                          readOnly
                          value={governanceActionSecrets[governanceDetail.action?.id]?.token}
                        />
                        <button
                          className="connect-button"
                          onClick={() => copyToClipboard(governanceActionSecrets[governanceDetail.action?.id]?.token)}
                        >
                          Copy token
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="panel-section">
                    <h4>Parameters</h4>
                    <pre className="code-block">{JSON.stringify(governanceDetail.payload?.params || {}, null, 2)}</pre>
                  </div>
                  <div className="panel-section">
                    <h4>Signatures</h4>
                    <ul className="gov-list">
                      {(governanceDetail.signatures || []).map((sig) => (
                        <li key={`${sig.userId}-${sig.walletAddress}`}>
                          <span title={sig.walletAddress || ''}>
                            {sig.email || sig.userId || 'Council member'} · Wallet on record
                          </span>{' '}
                          · {new Date(sig.signedAt).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="panel-section">
                    <h4>Audit timeline</h4>
                    <ul className="gov-list">
                      {(governanceDetail.audit || []).map((log, idx) => (
                        <li key={`${log.actionId}-${idx}`}>
                          {log.eventType} · {new Date(log.createdAt).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeIntent && (
        <div className="drawer-overlay" onClick={closeIntentModal}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{useCustomIntegrationModal ? 'Request Details' : 'Deployment Request Details'}</h3>
              <div className="modal-actions">
                {modalApproval && modalApproval.overallStatus === 'PENDING_APPROVAL' && (
                  modalApproval.eligible ? (
                    modalApproval.approvedByUser ? (
                      <span className="status-badge amber">Approved by you</span>
                    ) : (
                      <button
                        className="approve-button"
                        onClick={() => startApproval(activeIntent.id)}
                        disabled={approvingIntentId === activeIntent.id}
                      >
                        {approvingIntentId === activeIntent.id ? 'Approving...' : 'Approve with Wallet'}
                      </button>
                    )
                  ) : (
                    <span className="status-badge gray">Approval required</span>
                  )
                )}
                {modalApproval && modalApproval.overallStatus !== 'PENDING_APPROVAL' && (
                  <StatusPill overallStatus={modalApproval.overallStatus} />
                )}
              </div>
              <button className="modal-close" onClick={closeIntentModal}>×</button>
            </div>
            <div className="drawer-body">
              {modalApproval && (
                <div className="modal-approval-progress">
                  {modalApproval.approvalsCount} / {modalApproval.requiredCount} approvals collected
                </div>
              )}
              {releaseRecord ? (
                useCustomIntegrationModal ? (
                  <CustomIntegrationModal
                    release={releaseRecord}
                    policies={governancePolicies}
                  />
                ) : (
                  <ReleaseDetails
                    release={releaseRecord}
                    view={releaseView}
                    onViewChange={setReleaseView}
                    roles={governanceRoles}
                    policies={governancePolicies}
                  />
                )
              ) : (
                <p className="muted">Loading release record…</p>
              )}
            </div>
          </div>
        </div>
      )}
        <BottomDrawer />
      </div>
  );
}

export default App;
